import { Component, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { HttpClient } from '@angular/common/http';
import { EMPTY, forkJoin, Observable, of, OperatorFunction, Subject, Subscription } from 'rxjs';
import { catchError, concatMap, debounceTime, distinctUntilChanged, filter, repeatWhen, shareReplay, switchMap, tap } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';

import { RegisterOfLegalPersonsModalComponent } from '@components/register-of-legal-persons-modal/register-of-legal-persons-modal.component';
import { WarningMessageboxComponent } from '@components/_base/_messageboxes/warning-messagebox/warning-messagebox.component';
import { YesNoMessageboxComponent } from '@components/_base/_messageboxes/yes-no-messagebox/yes-no-messagebox.component';
import { BaseParentComponent } from '@components/_base/base-parent/base-parent.component';

import { TechnicalInspectionService } from '@app/technical-inspections/_services/technical-inspection.service';
import { TechnicalPointService } from '@app/tech-points/_services/technical-point.service';
import { CustomerCardService } from '@app/card-config/_services/customer-card.service';
import { AuthenticationService } from '@app/login/_services/authentication.service';
import { PermissionsService } from '@app/login/_services/permissions.service';
import { ContractService } from '@app/contracts/_services/contract.service';
import { NomenclatureService } from '@services/nomenclature.service';
import { ActiveDiscountCampaignDTO, DiscountDTO, PromotionalCampaign } from '@services/promotional-campaign.service';

import { UtilValidators } from '@app/_validators/util.validators';

import { config } from '@env/config';
import { modalMinWidth } from '@env/config';

import { PaymentServiceData, createPaymentServiceData } from '@app/technical-inspections/_models/payment-service-data.model';
import { TechnicalInspectionItem } from '@app/technical-inspections/_models/technical-inspection-item.model';
import { IcashCatalogItem } from '@app/sales/_models/icash-catalog-item.model';
import { SaleService } from '@app/sales/_services/sale.service';
import { GroupedTechnicalPointWorkingHour } from '@app/tech-points/_models/grouped-technical-point-working-hours.model';
import { CustomerSubscriptionService } from '@app/customers/_models/customer-subscription-service.model';
import { TechnicalInspection } from '@app/technical-inspections/_models/technical-inspection.model';
import { CustomerSubscription } from '@app/customers/_models/customer-subscription.model';
import { TechPointService } from '@app/tech-points/_models/tech-point-service.model';
import { ConsiceSubjectVersion } from '@models/consice-subject-version.model';
import { CustomerCards } from '@app/card-config/_models/customer-cards.model';
import { TechInspectionTime } from '@models/tech-inspection-time.model';
import { CustomerDiscount } from '@models/customer-discount.model';
import { OrderElement } from '@models/order-element.model';
import { SubjectVersion } from '@models/subject-version.model';
import { PriceAttribute } from '@models/price-attribute.model';
import { Line } from '@app/tech-points/_models/line.model';
import { RejectReason } from '@models/reject-reason.model';
import { RvsCategory } from '@models/rvs-category.model';
import { LoyalCombo } from '@models/loyal-combo.model';
import { StatusCode } from '@app/_enums/status-code';
import { Status } from '@models/status.model';

import { prepareDateForDb, prepareDateForForm } from '@app/_utils/date-util';
import { UIEventCustom } from '@app/_utils/ui-event-util';
import { displayError } from '@app/_utils/error-util';
import { PromotionService } from '@app/promotions/_services/promotion.service';
import { PromotionService as PromotionDiscountService, CustomerPromocodeMobileDTO, PaymentTypeDTO } from '@app/_services/promotion.service';
import { PromotionCodeService } from '@app/technical-inspections-cc/_models/promotion-code-service.model';
import { VoucherCodeDTO } from '@app/card-config/_models/voucher-code-dto.model';
import { VoucherService } from '@app/vouchers/_services/voucher.service';

/**
 * One goods line on the inspection's bill.
 *
 * Kept separate from the services, which are priced by the technical point's own price function
 * while these come from the iCash catalogue - the two never meet until the order is built.
 */
interface ItemLine {
  saleItemId: number;
  name: string;
  quantity: number;
  /** Gross unit price, as iCash gave it. */
  unitPrice: number;
  /** Discount for the whole line, in currency, as typed by the operator. */
  discount: number;
  /**
   * What is actually in the discount box, kept apart from {@link discount} so that a half-typed
   * figure survives. Binding the box straight to the number would rewrite "1." to "1" under the
   * cursor the moment the dot was pressed, and an empty box would read as a literal zero.
   */
  discountInput: string;
  inventoryId?: string;
  barcode?: string;
  measureUnitName?: string;
}

@Component({
  selector: 'app-add-edit-technical-inspection',
  templateUrl: './add-edit-technical-inspection.component.html',
  styleUrls: ['./add-edit-technical-inspection.component.css']
})
export class AddEditTechnicalInspectionComponent extends BaseParentComponent implements OnInit, OnDestroy {
  // Units
  technicalInspection:                TechnicalInspection;
  annualService:                      TechPointService | null;
  defRvsCategory:                     RvsCategory | null;
  customerSubscription:               CustomerSubscription | null;
  groupedTechPointWorkingHours:       GroupedTechnicalPointWorkingHour[];
  paymentServiceConvertedData:        Array<PaymentServiceData> = [];
  basicServicesForFirstAvailableSlot: TechPointService[];
  paymentServiceTableData:            Array<TechPointService> = [];
  technicalInspectionTimes:           TechInspectionTime[] | null;
  futureTechInspections:              TechnicalInspection[];
  additionalServices:                 TechPointService[];
  customerDiscounts:                  CustomerDiscount[];
  initialCustomerDiscounts:           CustomerDiscount[];
  promoCodeDiscounts:                 CustomerDiscount[] = [];
  activeVoucherCodes:                 VoucherCodeDTO[] = [];
  annualServices:                     TechPointService[];
  basicServices:                      TechPointService[];
  basicServicesCopy:                  TechPointService[];
  rejectReasons:                      RejectReason[];
  rvsCategories:                      RvsCategory[];
  technicalPointLines:                Line[];
  techInspStatuses:                   Status[];
  customerCard:                       CustomerCards | null;
  priceAttribute:                     PriceAttribute;
  currentTechnicalInspectionTimes:    TechInspectionTime[];
  assignedLines:                      {id: number, number: number}[] = [];
  originalServicePrices:              Map<number, number> = new Map();
  activeDiscountCampaigns:            ActiveDiscountCampaignDTO[] = [];

  // Constants
  readonly RESP_OK                          = 'OK';
  readonly RESP_CURRENT_SLOT_NOT_AVAILABLE  = 'CURRENT_SLOT_NOT_AVAILABLE';
  readonly RESP_HAS_NOT_AVAILABLE_SLOT      = 'HAS_NOT_AVAILABLE_SLOT';
  readonly BASIC_SERVICE_NAME               = 'ГОДИШЕН ТЕХНИЧЕСКИ ПРЕГЛЕД';
  readonly INNITIAL_CHECK_GAS_METAN_SERVICE = 'ПЪРВОНАЧАЛНА ПРОВЕРКА НА ГАЗОВА/МЕТАНОВА УРЕДБА';
  readonly ECO_GROUP_DETERMINATION_SERVICE  = "ОПРЕДЕЛЯНЕ НА ЕКО ГРУПА";
  /** Service names that can receive customer discount (dropdown) or loyalty card when selected as basic service */
  get mainServiceNamesForDiscount(): string[] {
    return [this.BASIC_SERVICE_NAME, this.INNITIAL_CHECK_GAS_METAN_SERVICE, this.ECO_GROUP_DETERMINATION_SERVICE];
  }
  getSelectedBasicServiceName(): string | undefined {
    return this.mainTechInspForm.get('basicTechPointService')?.value?.serviceName;
  }
  readonly BASIC_SERVICE_TYPE               = 'Основна услуга';
  readonly CARD_PAYMENT_TYPE                = 'С карта';
  readonly PAYMENT_TYPE_IN_TECH_POINT       = 'На каса в пункт';
  readonly PAYMENT_TYPE_BANK                = 'По банка';
  readonly PAYMENT_TYPE_DEFER               = 'Отложено плащане';
  readonly DISCOUNT_PERCENTAGE              = 'P';
  readonly DISCOUNT_COVERED_BY_PROMO_CAMPAIGN_ERROR_CODE = 'DAP-431';
  readonly RVS_CATEGORY_CODE                = 'M1';
  readonly CODE_RESERVED                    = 'RESERVED';
  readonly CODE_AWAITING_INSPECTION         = 'AWAITING_INSPECTION';
  readonly CODE_FINISHED                    = 'FINISHED';
  readonly CODE_TERMINATED                  = 'TERMINATED';
  readonly CODE_ANNULLED                    = 'ANNULLED';
  readonly CODE_LATED                       = 'LATED';
  readonly CODE_STARTED                     = 'STARTED';
  readonly CODE_PAID                        = 'PAID';
  readonly CODE_ACTIVE                      = 'ACTIVE';
  readonly CODE_AWAITING_PAYMENT            = 'AWAITING_PAYMENT'
  readonly DISCOUNT_VALUE                   = 'V';
  readonly INCOMING_WAY_TP                  = 'erp_tp';
  readonly INVALID_LOYAL_CARD               = "INVALID_LOYAL_CARD";
  readonly DISCOUNT_TYPE_TECHNICAL_POINT    = "tp";
  readonly FINISHED_RES                     = "FINISHED";
  readonly SITE_INCOMING_WAY                = "autotest_site";
  /** Pre-filled contact email for new inspections; replaced when an existing inspection is loaded from the API. */
  readonly defaultTechnicalInspectionEmail  = 'nomail@nomail';

  // Booleans
  isNotFromWhilli                   = true;
  loading                           = false;
  showAllFields                     = false;
  showLateButton                    = false;
  showStartButton                   = false;
  submittedMainForm                 = false;
  isStartedTechInsp                 = false;
  hasValidSubscription              = false;
  isValidMainFormRegNum             = false;
  isValidSlotFormRegNum             = false;
  submittedAdditionalForm           = false;
  hideSaveAndSubjectButtons         = false;
  //isValidMainFormRegCertNum       = false;
  submittedAvailableSlotForm        = false;
  isVisiblePaymentServicesTable     = false;
  issueInvoice                      = false;
  disableTechPointServices:         boolean | null;
  disableMainTechPointServices:     boolean | null;
  disableAnnualServiceAndDiscount:  boolean | null;
  isDeferPayment                    = false;
  isBankPayment                     = false;
  hasPromocode                      = false;
  allowedPaymentTypeDescriptions: string[] | null = null;
  viewInvoice                       = false;
  disableSaveButton                 = false;
  isChangedRvsCategory              = false;
  canEdit                           = true;
  arePastLoaded                     = false;
  isPastClicked                     = false;
  isCredit                          = false;
  isDebit                           = false;
  viewCreditAndDebit                = false;
  showCreditDebitButton             = false;
  loadingQuickReservation           = false;
  isInvalidRegNumberForForeigner    = false;
  isInvalidRegNumberForDiplomatic   = false;
  isInvalidRegNumberForElectric     = false;
  mainFormValidation                = false;
  slotFormValidation                = false;
  isHaveSubject                     = false;
  agreeWithNotification!:     boolean | null;
  isReservationPaid                 = false;
  orderPriceMismatch                = false;
  orderPaid                         = false;
  isSubjectFromPromocode            = false;
  displayExceedCardMessage          = false;
  displayCardDoesNotExistMessage    = false;
  displayDiscountAlreadyExistsMessage = false;
  displayNoPermissionForMainServicePromoMessage = false;
  displayServiceNotSelectedForDiscountMessage = false;
  displayDiscountCoveredByPromoCampaignMessage = false;
  exceedCardMessageTimeout:        any = null;
  cardDoesNotExistMessageTimeout:   any = null;
  discountAlreadyExistsMessageTimeout: any = null;
  noPermissionForMainServicePromoMessageTimeout: any = null;
  serviceNotSelectedForDiscountMessageTimeout: any = null;
  discountCoveredByPromoCampaignMessageTimeout: any = null;
  wantMarketingNotification         = true;
  reservationPaidAlready            = false;
  showPromoCodeSuccessMessage       = false;
  showCardSuccessMessage            = false;
  showMobileCardSuccessMessage      = false;
  promoCodeSuccessMessageTimeout:   any = null;
  cardSuccessMessageTimeout:        any = null;
  mobileCardSuccessMessageTimeout: any = null;
  isValidatingCardOrPromoCode       = false;
  promoCodeInputValue               = '';
  promotionCodeService:             PromotionCodeService | null = null;

  // Payloads
  userTechnicalPointMobileIds:        number[];
  userTechnicalPointId:               number;
  techPointWorkingHourIds:            number[];
  currentSelectedServiceMobileAppIds: Array<number> = [];
  servicesMobileAppIdsForCard:        number[];
  technicalInspectionId:              number;
  techInspMobileId:                   number | null;
  customerSubscriptionName:           String | null;
  techInspStatusName:                 string | null;
  refreshIntervalId:                  any;
  subjectMobileAppId:                 number;
  selectedAdditionalServices:         Array<any> = [];
  selectedTechPointServices:          Array<any> = [];
  /**
   * How many of each additional service, keyed by technical point service id.
   *
   * Only the additional services can be sold more than once - a vehicle gets one ГТП, and the
   * basic service is what the slot duration is booked from - so nothing else appears here.
   */
  additionalServiceQuantities:        { [techPointServiceId: number]: number } = {};
  minDate:                            { year: number, month: number, day: number };
  lastSelectedDiscountByCard          = '';
  orderElementsByPromotionCode:       any[] = [];
  get allowedCustomerCardDiscountServices(): string[] {
    return this.mainServiceNamesForDiscount;
  }
  paidOrderCode                         = 'PAID';
  paymentTypeCardName                   = 'С карта';
  orderAmountPaid                       = 0;
  currentAmountServices                 = 0;
  futureTechInspId                      = 0;
  lastTechInspId                        = 0;
  techInspModalPopUpCounter             = 0;
  promocodeAvailableServices: string[]  = [];
  promocodeAvailableCategories: string[]= [];
  appliedDiscounts: Array<{type: 'card' | 'promocode' | 'mobile-card' | 'customer-discount', name: string, discount: CustomerDiscount | null, promoCodeService?: PromotionCodeService | null, cardNumber?: string, isVoucher?: boolean, paymentTypes?: PaymentTypeDTO[], isMainService?: boolean}> = [];

  // Goods sold alongside the inspection
  /** The goods on the bill. A plain array, like the services - this screen uses no FormArray. */
  itemLines:                            ItemLine[] = [];
  /** Bound with ngModel rather than a control: it is a search box, not part of the payload. */
  itemSearchTerm                        = '';
  catalogResults:                       IcashCatalogItem[] = [];
  itemQuantities:                       { [saleItemId: number]: number } = {};
  catalogLoading                        = false;
  checkingStockForSaleItemId:           number | null = null;
  itemMessages:                         { [saleItemId: number]: string } = {};
  private itemMessageTimeouts:          { [saleItemId: number]: any } = {};
  private itemSearchSubject             = new Subject<string>();
  private readonly CATALOG_PAGE_SIZE    = 10;
  private readonly SEARCH_DEBOUNCE_MS   = 400;
  private readonly ITEM_MESSAGE_TIMEOUT_MS = 5000;
  /** The service type a good is reported under, matching n_service_types id 10. */
  private readonly GOODS_SERVICE_TYPE   = 'Стока';

  // Form
  availableSlotForm = this.formBuilder.group({
    forDate:                      [''],
    line:                         [''],
    regNumber:                    ['', [Validators.required, Validators.maxLength(16)]],
    phoneNumber:                  ['', [Validators.required, Validators.minLength(9), Validators.maxLength(16), UtilValidators.validatePhoneNumber()]],
    rvsCategory:                  ['', [Validators.required]],
    isEmployee:                   [false],
    basicTechPointService:        [''],
    groupedTechPointWorkingHours: [''],
  });

  mainTechInspForm = this.formBuilder.group({
    id:                     [''],
    regNumber:              ['', [Validators.required, Validators.maxLength(16)]],
    registrationCertNumber: ['', [Validators.maxLength(10)]],
    rvsCategory:            ['', [Validators.required]],
    basicTechPointService:  ['', [Validators.required]],
    inspectionDate:         [''],
    versionData:            [''],
    status:                 [''],
    lineNumber:             [''],
    customerRvsId:          [''],
    technicalPointId:       [''],
    incomingWay:            [''],
    fromErp:                [''],
  });

  additionalTechInspForm = this.formBuilder.group({
    phoneNumber:             ['',   [Validators.required, Validators.minLength(9), Validators.maxLength(16), UtilValidators.validatePhoneNumber()]],
    email:                   ['',   [Validators.required, Validators.email, Validators.maxLength(60)]],
    name:                    ['',   [Validators.required, Validators.maxLength(60)]],
    customerCard:            [null],     
    customerDiscount:        [''],
    voucherCode:             [null],
    promotionId:             [null],     
    isAgreeWithNotification: [true, [Validators.required]],
    isEmployee:              [false, [Validators.required]],
    rejectReason:            [null],
    createdOrder:            [''],
    issueInvoice:            [false],
    contractRvsId:           [null],
    subjectId:               [null],
    subjectIdentNum:         [null],
    subjectManager:          [null],
    subjectInvoiceDdsReg:    [null],         
    subjectName:             [null],
    subjectCity:             [null],
    subjectAddress:          [null],
    subjectVersionData:      [null],
    cardOrPromoCode:         [''],
    mobileCard:              [null],
    // Optional, but exactly the seven digits printed on the receipt when it is filled in. The
    // pattern only bites once something is typed, so leaving it empty still saves.
    fiscalNumber:            [null, [Validators.pattern(/^[0-9]{7}$/)]]
  });
  
  // Observables
  private subscriptions = new Subscription();
  
  loadTechnicalInspection$ = this.route.queryParams.pipe(
    tap((params) => this.technicalInspectionId = params['id']),
    concatMap(() => {
      return this.techPointService.getTechPointIdByTechPointMobileAppId(this.userTechnicalPointMobileIds[0]);
    }),
    tap(techPointId => {
      this.userTechnicalPointId = techPointId;
    }),
    concatMap(() => {
      return forkJoin([
        this.techInspService.findTechnicalInspectionById(this.technicalInspectionId),
        this.techInspService.getTechInspectionNomenclatures(StatusCode.TechnicalInspection, this.userTechnicalPointId, this.userTechnicalPointMobileIds[0])
      ]).pipe(
        tap(([techInspection, [futureTechInspections, lines, rvsCategories, rejectReasons, customerDiscounts, techInspStatuses]]) => {
          this.mainTechInspForm.get('technicalPointId')?.patchValue(this.userTechnicalPointId);
          this.currentTechnicalInspectionTimes = techInspection?.techInspectionTimes!;
          
          //this check remove of falidation not work when button "button.fill" from fast rezervation list
          if (techInspection?.incomingWay?.includes('whilli')) {
            this.isNotFromWhilli = false;
            this.addRemoveValidatorsWhilli();
          } else {
            this.isNotFromWhilli = true;
            this.addRemoveValidatorsWhilli();
          }
          
          this.mainTechInspForm.patchValue(techInspection);
          this.additionalTechInspForm.patchValue(techInspection);
          this.technicalInspection = techInspection;

          // The goods carry their own snapshot, so they are restored straight away - unlike the
          // services, which have to wait for the category's service lists to arrive.
          this.restoreItemLines(techInspection);

          const resolvedEmail = techInspection?.email ?? this.defaultTechnicalInspectionEmail;
          this.additionalTechInspForm.get('email')?.setValue(resolvedEmail);

          // If backend populated mobileCard, add it to appliedDiscounts so it shows as already applied
          const mobileCardFromBe = this.additionalTechInspForm.get('mobileCard')?.value;
          if (mobileCardFromBe) {
            const mobileCardNumber = typeof mobileCardFromBe === 'string' ? mobileCardFromBe.trim() : String(mobileCardFromBe).trim();
            if (mobileCardNumber) {
              const exists = this.appliedDiscounts.some(ad => ad.type === 'mobile-card' && ad.cardNumber === mobileCardNumber);
              if (!exists) {
                this.appliedDiscounts.push({
                  type: 'mobile-card' as const,
                  name: `Мобилна карта - ${mobileCardNumber}`,
                  discount: null,
                  cardNumber: mobileCardNumber
                });
              }
            }
          }
          if (this.technicalInspection && this.technicalInspection.techInspectionOrders) {
            this.technicalInspection.techInspectionOrders?.forEach(o => {
              console.log([o])
              this.reservationPaidAlready = o.order?.status?.code === this.paidOrderCode ? true : false;

              if (o.order?.status?.code === this.paidOrderCode && o.order.paymentType.description === this.paymentTypeCardName) {
                this.isReservationPaid = true;
                this.orderAmountPaid = 0;
                const firstVersionData = this.findLastFirstVersion(techInspection.versionData);
                this.techInspService.getPaidAmountFromFirstTechnicalInspection(firstVersionData.id).subscribe(res => {
                  this.orderAmountPaid = res;
                });
              } else {
                this.isReservationPaid = false;
              }
            });
          }

          const filteredFutureTechInspections = this.filterFutureTechInspectionsByEmployeeLines(futureTechInspections);
          this.futureTechInspections = filteredFutureTechInspections;
          this.futureTechInspections.forEach(techInsp => {
            techInsp.inspectionDate = this.prepareDateForUi(techInsp.inspectionDate);
          });

          this.showHideLateAndStartButton(this.futureTechInspections);
          this.technicalPointLines = lines;
          this.rvsCategories = rvsCategories;
          this.rejectReasons = rejectReasons;
          this.customerDiscounts = this.filterDiscountsByType(customerDiscounts);
          this.initialCustomerDiscounts = this.customerDiscounts;
          this.techInspStatuses = techInspStatuses;

          this.wantMarketingNotification = this.technicalInspection?.wantsMarketingMessages ?? true;

          if (this.technicalInspection?.techInspectionOrders![0]?.order) {
            if (this.technicalInspection?.techInspectionOrders![0]?.order?.status?.code == this.CODE_PAID) {
              this.orderPaid = true;
            }
          }

          if (techInspection && techInspection.customerCard) {
            this.customerCard = techInspection.customerCard;
            if (this.customerCard.programMobileId && this.customerCard.programMobileId.subjectVersion) {
              this.setSubjectDataWithoutContract(this.customerCard.programMobileId.subjectVersion, true);
            }
            if (this.customerCard.cardNumber) {
              let isHaveTechInspectionOrders = false;
              this.additionalTechInspForm.get('customerCard')?.setValue(this.customerCard.cardNumber);
              
              if (techInspection.techInspectionOrders && techInspection.techInspectionOrders.length !== 0) {
                if (techInspection.techInspectionOrders[0].order) {
                  isHaveTechInspectionOrders = true;
                  if (techInspection.techInspectionOrders[0].order.customerDiscount) {
                    this.additionalTechInspForm.get('customerDiscount')?.setValue(techInspection.techInspectionOrders[0].order.customerDiscount);
                    this.customerDiscounts.push(techInspection.techInspectionOrders[0].order.customerDiscount);
                    customerDiscounts = Array.from(new Set(customerDiscounts));
                  }
                }
              }

              if (!isHaveTechInspectionOrders) {
                this.cardService.getLoyalComboByCard(this.customerCard.cardNumber).subscribe(data => {
                  const loyalCombo = data as LoyalCombo;
                  this.customerCard = loyalCombo.customerCard;
                  if (loyalCombo.customerDiscount) {
                    this.lastSelectedDiscountByCard = loyalCombo.customerDiscount.discountName;
                    this.additionalTechInspForm.get('customerDiscount')?.setValue(loyalCombo.customerDiscount);
                    this.customerDiscounts.push(loyalCombo.customerDiscount);
                    customerDiscounts = Array.from(new Set(customerDiscounts));
                  }
                });
              }
            }
          }

          this.loadDefaultAvailableSlotFilters();

          if (techInspection == null) {
            this.showAllFields = false;
            this.mainTechInspForm.get('inspectionDate')?.patchValue(null);
            this.mainTechInspForm.get('rvsCategory')?.patchValue(this.defRvsCategory);
            this.rvsAdditionalSubject.next(this.defRvsCategory);
            this.additionalTechInspForm.get('isAgreeWithNotification')?.patchValue(true);
          } else {
            if (this.technicalInspection.status!.code !== this.CODE_RESERVED
                && this.technicalInspection.status!.code !== this.CODE_AWAITING_INSPECTION) {
              this.findAlreadyStarted();
              this.showAllFields = true;
            }
            if (this.technicalInspection.status!.code === this.CODE_PAID) {
              this.disableTechPointServices = true;
            }
            if (this.technicalInspection.status!.code === this.CODE_FINISHED || this.technicalInspection.status!.code === this.CODE_ANNULLED || this.technicalInspection.status!.code === this.CODE_LATED) {
              this.hideSaveAndSubjectButtons = true;
            }

            this.techPointWorkingHourIds = this.technicalInspection.techPointWorkingHourIds!;
            this.techInspStatusName = this.technicalInspection.status!.name;
            this.techInspMobileId = this.technicalInspection.mobileId!;
            this.rvsAdditionalSubject.next(techInspection.rvsCategory);

            const res = this.technicalInspection.techInspectionOrders?.[0]?.order?.invoiceSubjectVersion;
            // A goods element carries no service at all, so the optional chaining is load-bearing
            // here rather than defensive: without it this throws on any inspection sold a good.
            const rawMainServiceDiscountId = this.technicalInspection.techInspectionOrders?.[0]?.order?.orderElements.find(oe => oe.service?.serviceType?.description === this.BASIC_SERVICE_TYPE)?.discountId;
            // Backend uses 0 (and undefined) to mean "no discount"; normalize to null so the != null / truthiness checks below don't treat it as an applied promo.
            const orderElementMainServicePromocodeId = rawMainServiceDiscountId ? rawMainServiceDiscountId : null;
            if (this.technicalInspection.techInspectionOrders?.[0]?.order?.promotion?.id != null || orderElementMainServicePromocodeId != null) {
                const promotionId = this.technicalInspection.techInspectionOrders?.[0]?.order?.promotion?.id || orderElementMainServicePromocodeId!;
                this.promotionService.findSubjectByPromotionId(promotionId).subscribe(sub => {
                    if (sub && this.technicalInspection.status?.code !== this.CODE_FINISHED && !res) {
                        this.setSubjectData(sub, false, false);
                        this.isSubjectFromPromocode = true;
                    }
                });
                this.fillPromoRvsAndServices(promotionId);
                this.hasPromocode = true;

                this.orderElementsByPromotionCode = this.technicalInspection.techInspectionOrders![0]?.order?.orderElements || [];
            }
          }
        }),
        catchError(err => {
          displayError(err);
          this.errorMessageSubject.next(this.translateService.instant('messages.errorLoadingData'));
          return EMPTY;
        }),
        repeatWhen(() => this.reload$)
      );
    })
  );

  private rvsAdditionalSubject = new Subject<RvsCategory | null>();
  rvsAdditional$ = this.rvsAdditionalSubject.asObservable().pipe(
    concatMap(rvsCategory => {
      return this.techInspService.getRvsCategoryServicesByType(rvsCategory, this.userTechnicalPointId).pipe(
        tap(([basicServices, additionalServices, annualServices]) => {
          this.basicServices = basicServices;
          this.basicServicesCopy = JSON.parse(JSON.stringify(basicServices));
          this.additionalServices = additionalServices;
          this.annualServices = annualServices;
          this.customerSubscriptionName = null;
          this.techInspStatusName = null;
          this.techInspMobileId = null;

          if (this.technicalInspectionId == null) {
            this.mainTechInspForm.get('basicTechPointService')?.patchValue(this.basicServices.find(service => service.serviceName === this.BASIC_SERVICE_NAME) || null);
            //TODO: Return when whilli is in use - automatically select the annual service when we start the technical inspection.
            // this.annualService = annualServices[0];
            // this.getServicesMobileAppIdsForCard(this.annualService!.serviceId!);
          } else {
            if (this.technicalInspection?.incomingWay === this.SITE_INCOMING_WAY) {
              if (this.technicalInspection.status?.code === this.FINISHED_RES) {
                this.agreeWithNotification = this.technicalInspection.isAgreeWithNotification || true;
              } else {
                this.agreeWithNotification = true;
              }
              this.additionalTechInspForm.get('isAgreeWithNotification')?.patchValue(this.agreeWithNotification);
            } else {
              if (this.technicalInspection.isAgreeWithNotification === true || this.technicalInspection.isAgreeWithNotification === false) {
                this.agreeWithNotification = this.technicalInspection.isAgreeWithNotification;
              } else {
                this.agreeWithNotification = true;
              }
              this.additionalTechInspForm.get('isAgreeWithNotification')?.patchValue(this.agreeWithNotification);
            }

            this.additionalTechInspForm.get('isEmployee')?.patchValue(this.technicalInspection.isEmployee == null ? false : this.technicalInspection.isEmployee);
            this.mainTechInspForm.get('lineNumber')?.patchValue(this.technicalInspection.techInspectionTimes![0].technicalPointWorkingHours!.line.number);
            this.mainTechInspForm.get('technicalPointId')?.patchValue(this.technicalInspection.technicalPoint?.id);
            this.additionalTechInspForm.get('issueInvoice')?.patchValue(this.technicalInspection.issueInvoice);
            this.additionalTechInspForm.get('isEmployee')?.patchValue(this.technicalInspection.isEmployee);
           
            if (this.technicalInspection.status!.code === this.CODE_RESERVED
                || this.technicalInspection.status!.code === this.CODE_AWAITING_INSPECTION) {
              // this.additionalTechInspForm.get('isAgreeWithNotification')?.patchValue(true);
              this.additionalTechInspForm.get('isEmployee')?.patchValue(false);
              if (!this.technicalInspection?.incomingWay?.includes('whilli')) {
                if (this.annualServices.length > 0) {
                  this.annualServices.forEach(element => {
                    this.technicalInspection.techInspectionServices?.forEach(service => {
                      if (element.id === service.technicalPointService?.id) {
                        this.annualService = element;
                      }
                    });
                  });
                } else {
                  this.annualService = annualServices[0];
                }
                if (this.annualService) {
                  this.getServicesMobileAppIdsForCard(this.annualService!.serviceId!);
                }
                //for auto select cart check here if this.annualService is null and set  annualServices[0];
              }
            } 
            //TODO: Return when whilli is in use - automatically select the annual service when tech.insp. status is started

            // else if(this.technicalInspection.status!.code === this.CODE_STARTED) { // if technical point resume in status started to fix problem 
            //   if (!this.technicalInspection?.incomingWay?.includes('whilli')) {
            //     // this.annualService = annualServices[0];
            //     // this.getServicesMobileAppIdsForCard(this.annualService!.serviceId!);
            //   }
            // }

            // Basic 
            this.basicServices.forEach(basicTechPointService => {
              this.technicalInspection.techInspectionServices?.forEach(techInspService => {
                if (basicTechPointService.serviceMobileAppId === techInspService.technicalPointService!.serviceMobileAppId) {
                  this.mainTechInspForm.get('basicTechPointService')?.patchValue(basicTechPointService);
                }
              });
            });

            if (this.technicalInspection.status!.code !== this.CODE_RESERVED
                && this.technicalInspection.status!.code !== this.CODE_AWAITING_INSPECTION) {
                this.isVisiblePaymentServicesTable = true;

                let invoiceSubjVer: ConsiceSubjectVersion | undefined;
                if (this.technicalInspection.techInspectionOrders?.length != 0) {
                  if (this.technicalInspection.techInspectionOrders?.length != 1) {
                    invoiceSubjVer = this.technicalInspection.techInspectionOrders![this.technicalInspection.techInspectionOrders!.length - 1].order?.invoiceSubjectVersion;
                  } else {
                    invoiceSubjVer = this.technicalInspection.techInspectionOrders![0].order?.invoiceSubjectVersion;
                  }
                }
                this.setSubjectData(invoiceSubjVer, false, true);
                // Additional
                // Pre-select from the inspection's tech-insp services AND from the order elements. A started
                // version may not carry the additional services in techInspectionServices, but the order
                // created at reservation time still holds them, so we also match against orderElements to
                // keep the checkboxes and the payment table consistent (and to carry the services forward
                // on the next save).
                this.additionalServices.forEach(additionalTechPointService => {
                  const storedService = this.technicalInspection.techInspectionServices?.find(
                    techInspService => additionalTechPointService.id === techInspService.technicalPointService!.id
                  );
                  const inTechInspServices = storedService != null;
                  const inOrderElements = this.isServiceInOrderElements(additionalTechPointService);
                  const alreadySelected = this.selectedAdditionalServices.some(s => s.id === additionalTechPointService.id);
                  if ((inTechInspServices || inOrderElements) && !alreadySelected) {
                    this.selectedAdditionalServices.push(additionalTechPointService);
                    // Rows written before the quantity column existed carry null, which has
                    // always meant one of each.
                    this.additionalServiceQuantities[additionalTechPointService.id!] =
                        storedService?.quantity ?? 1;
                  }
                });

                // Annual
                this.annualServices.forEach(annualTechPointService => {
                  this.technicalInspection.techInspectionServices?.forEach(techInspService => {
                      if (annualTechPointService.id === techInspService.technicalPointService!.id) {
                        this.annualService = annualTechPointService;
                        this.customerSubscriptionName = annualTechPointService.serviceName!;
                      }
                  });
                });

                if (this.techInspStatusName == null) {
                  if (this.technicalInspection.status?.name) {
                    this.techInspStatusName = this.technicalInspection.status?.name;
                  }
                }
                if (this.techInspMobileId == null) {
                  if (this.technicalInspection.mobileId) {
                    this.techInspMobileId = this.technicalInspection.mobileId;
                  }
                }
                if (this.customerSubscriptionName == null) {
                  this.getCustomerSubscriptionByRegNumber(this.mainTechInspForm.get('regNumber')?.value);
                }

                let custDisc: CustomerDiscount | undefined;
                if (this.technicalInspection.techInspectionOrders?.length != 0) {
                  if (this.technicalInspection.techInspectionOrders?.length != 1) {
                    custDisc = this.technicalInspection.techInspectionOrders![this.technicalInspection.techInspectionOrders!.length - 1].order?.customerDiscount;
                  } else {
                    custDisc = this.technicalInspection.techInspectionOrders![0].order?.customerDiscount;
                  }

                  if (this.technicalInspection.techInspectionOrders![0]?.order?.accountingDocument?.invoice != null) {
                    this.showCreditDebitButton = true;
                  }

                  if (this.technicalInspection) {
                    if (!this.technicalInspection.customerCard) {
                      this.additionalTechInspForm.get('customerDiscount')?.patchValue(custDisc);
                    }
                  }
                } 
            }

            if (this.technicalInspection.techInspectionOrders?.length == 0) {
              this.isVisiblePaymentServicesTable = false;
            } else {
              this.technicalInspection.techInspectionOrders?.forEach(techInspOrder => {
                techInspOrder.order!.orderElements.forEach(orderElement => {
                  // A goods line has no service, so reading its name here would throw. The goods
                  // are added to the table from the bill instead, by addItemsToPaymentServiceTable
                  // - taking them from here as well would list and count each one twice.
                  if (orderElement.service == null) { return; }
                  // The stored element already holds the money for the whole line, so its
                  // quantity is shown rather than used to scale anything. Absent on an element
                  // written before the column existed, which means one.
                  this.paymentServiceConvertedData.push(createPaymentServiceData(orderElement.service.name!, orderElement.service.serviceType!.description, orderElement.totalAmount, orderElement.discountAmount == null ? 0 : orderElement.discountAmount, orderElement.quantity ?? 1));
                });
              });
              
              // Reconstruct appliedDiscounts from order elements
              this.reconstructAppliedDiscountsFromOrderElements();
            } 

            // Price the table for every inspection that has started, not only a finished one.
            // Reopening a started inspection used to leave it empty until something was clicked,
            // because nothing had priced it: a started inspection has no order yet, so the loop
            // above over the order elements contributes nothing and the figures have to be
            // computed from the services just restored.
            //
            // A refresh, not showPaymentServicesTable: this runs while the inspection is being
            // loaded, and that one would mark the form submitted - painting the validation
            // errors red before the operator has touched anything - and could open the
            // subscription dialog on arrival.
            if (!this.isChangedRvsCategory) {
              this.refreshPaymentServicesTable();
            }
          }
        }),
      );
    })
  );

  customerDiscountSearch: OperatorFunction<string, CustomerDiscount[]> = (input$: Observable<string>) =>
    input$.pipe(
      debounceTime(300),
      filter((input) => input.length > 3),  
      switchMap(input => 
        this.nomenclatureService.filterCustomerDiscounts(input).pipe(
          catchError(() => of([])),
          shareReplay()
      )
    )
  );

  // Children
  @ViewChild('confirmTermination') confirmTerminationDialog: TemplateRef<any>;
  @ViewChild('confirmAnnul') confirmAnnul: TemplateRef<any>;
  @ViewChild('dataChange') dataChangeDialog: TemplateRef<any>;
  dialogRef: any

  constructor(
    protected router:             Router,
    public perms:                 PermissionsService,
    private dialog:               MatDialog,
    private route:                ActivatedRoute,
    private formBuilder:          FormBuilder,
    private uiEvent:              UIEventCustom,
    private translateService:     TranslateService,
    private techPointService:     TechnicalPointService,
    private techInspService:      TechnicalInspectionService,
    private nomenclatureService:  NomenclatureService,
    private contractService:      ContractService,
    private cardService:          CustomerCardService,
    private promotionService:     PromotionService,
    private voucherService:       VoucherService,
    private promotionalCampaign:  PromotionalCampaign,
    private http:                  HttpClient,
    private authService:          AuthenticationService,
    /** The goods picker reuses the sales screen's catalogue endpoints as they are. */
    private saleService:          SaleService
  ) {
    super(router); 
    this.technicalInspection = new TechnicalInspection;
    this.minDate = prepareDateForForm(new Date());

    this.refreshFutureTechnicalInspections();
    this.userTechnicalPointMobileIds = AuthenticationService.getEmployeeTechPointsIds();
  }

  ngOnInit() {
    this.rvsAdditional$.subscribe();
    super.ngOnInit();
    this.promotionalCampaign.getActiveDiscountCampaigns().subscribe({
      next: res => this.activeDiscountCampaigns = res.activeDiscountCampaigns ?? [],
      error: err => displayError(err)
    });
    localStorage.setItem('techInspModalPopUpCounter', '0');
    this.subscriptions.add(this.additionalTechInspForm.get("isAgreeWithNotification")?.valueChanges.subscribe(value => {
      this.addRemovePhoneValidator(value);
    }));
    this.loadAssignedLines();
    this.initItemSearch();
  }

  /**
   * Prefers the backend's own message for a known error code, falling back to the generic one.
   * instant() echoes the key back when there is no translation for it.
   */
  private resolveItemErrorMessage(err: any): string {
    const apiCode = err?.error?.code;
    const i18nKey = apiCode ? `errorCodes.${apiCode}` : null;
    const translated = i18nKey ? this.translateService.instant(i18nKey) as string : null;
    if (translated && translated !== i18nKey) { return translated; }
    return this.translateService.instant('messages.errorLoadingData') as string;
  }

  /**
   * The goods search: debounced, and cancelling whatever is in flight when the operator types on.
   *
   * catchError sits inside the switchMap on purpose. Outside it, the first failed search would
   * complete the pipeline and the box would go dead for the rest of the session.
   */
  private initItemSearch() {
    this.subscriptions.add(
      this.itemSearchSubject.asObservable().pipe(
        debounceTime(this.SEARCH_DEBOUNCE_MS),
        distinctUntilChanged(),
        switchMap(term => {
          const techPointMobileAppId = this.userTechnicalPointMobileIds?.[0];
          if (!term || techPointMobileAppId == null) {
            this.catalogResults = [];
            this.catalogLoading = false;
            return EMPTY;
          }
          this.catalogLoading = true;
          return this.saleService.findCatalogItems(techPointMobileAppId, term, 1, this.CATALOG_PAGE_SIZE).pipe(
            catchError(err => {
              displayError(err);
              this.catalogLoading = false;
              this.uiEvent.displayUIError(this.resolveItemErrorMessage(err));
              return EMPTY;
            })
          );
        })
      ).subscribe(page => {
        this.catalogResults = page?.content ?? [];
        this.catalogLoading = false;
        this.catalogResults.forEach(item => {
          if (this.itemQuantities[item.saleItemId] == null) {
            this.itemQuantities[item.saleItemId] = 1;
          }
        });
      })
    );
  }

  ngOnDestroy() {
    super.ngOnDestroy();
    clearInterval(this.refreshIntervalId);
    if (this.promoCodeSuccessMessageTimeout) {
      clearTimeout(this.promoCodeSuccessMessageTimeout);
    }
    if (this.cardSuccessMessageTimeout) {
      clearTimeout(this.cardSuccessMessageTimeout);
    }
    this.rvsAdditionalSubject.unsubscribe();
    this.subscriptions.unsubscribe();
    // Pending picker-row timers would otherwise fire against a destroyed component.
    Object.values(this.itemMessageTimeouts).forEach(timeout => clearTimeout(timeout));
    localStorage.removeItem('techInspModalPopUpCounter');
  }

  private hideSuccessMessageAfterAnimation(messageType: 'promoCode' | 'card' | 'mobile-card', delayMs: number): void {
    // Clear any existing timeout for this message type
    if (messageType === 'promoCode' && this.promoCodeSuccessMessageTimeout) {
      clearTimeout(this.promoCodeSuccessMessageTimeout);
    } else if (messageType === 'card' && this.cardSuccessMessageTimeout) {
      clearTimeout(this.cardSuccessMessageTimeout);
    } else if (messageType === 'mobile-card' && this.mobileCardSuccessMessageTimeout) {
      clearTimeout(this.mobileCardSuccessMessageTimeout);
    }

    // Set timeout to hide the message after animation completes
    const timeout = setTimeout(() => {
      if (messageType === 'promoCode') {
        this.showPromoCodeSuccessMessage = false;
        this.promoCodeSuccessMessageTimeout = null;
      } else if (messageType === 'card') {
        this.showCardSuccessMessage = false;
        this.cardSuccessMessageTimeout = null;
      } else if (messageType === 'mobile-card') {
        this.showMobileCardSuccessMessage = false;
        this.mobileCardSuccessMessageTimeout = null;
      }
    }, delayMs);

    // Store timeout reference
    if (messageType === 'promoCode') {
      this.promoCodeSuccessMessageTimeout = timeout;
    } else if (messageType === 'card') {
      this.cardSuccessMessageTimeout = timeout;
    } else if (messageType === 'mobile-card') {
      this.mobileCardSuccessMessageTimeout = timeout;
    }
  }

  private hideErrorMessageAfterDelay(errorType: 'exceedCard' | 'cardDoesNotExist' | 'discountAlreadyExists' | 'noPermissionForMainServicePromo' | 'serviceNotSelectedForDiscount' | 'discountCoveredByPromoCampaign', delayMs: number = 7000): void {
    // Clear any existing timeout for this error type
    if (errorType === 'exceedCard' && this.exceedCardMessageTimeout) {
      clearTimeout(this.exceedCardMessageTimeout);
    } else if (errorType === 'cardDoesNotExist' && this.cardDoesNotExistMessageTimeout) {
      clearTimeout(this.cardDoesNotExistMessageTimeout);
    } else if (errorType === 'discountAlreadyExists' && this.discountAlreadyExistsMessageTimeout) {
      clearTimeout(this.discountAlreadyExistsMessageTimeout);
    } else if (errorType === 'noPermissionForMainServicePromo' && this.noPermissionForMainServicePromoMessageTimeout) {
      clearTimeout(this.noPermissionForMainServicePromoMessageTimeout);
    } else if (errorType === 'serviceNotSelectedForDiscount' && this.serviceNotSelectedForDiscountMessageTimeout) {
      clearTimeout(this.serviceNotSelectedForDiscountMessageTimeout);
    } else if (errorType === 'discountCoveredByPromoCampaign' && this.discountCoveredByPromoCampaignMessageTimeout) {
      clearTimeout(this.discountCoveredByPromoCampaignMessageTimeout);
    }

    // Set timeout to hide the error message after delay
    const timeout = setTimeout(() => {
      if (errorType === 'exceedCard') {
        this.displayExceedCardMessage = false;
        this.exceedCardMessageTimeout = null;
      } else if (errorType === 'cardDoesNotExist') {
        this.displayCardDoesNotExistMessage = false;
        this.cardDoesNotExistMessageTimeout = null;
      } else if (errorType === 'discountAlreadyExists') {
        this.displayDiscountAlreadyExistsMessage = false;
        this.discountAlreadyExistsMessageTimeout = null;
      } else if (errorType === 'noPermissionForMainServicePromo') {
        this.displayNoPermissionForMainServicePromoMessage = false;
        this.noPermissionForMainServicePromoMessageTimeout = null;
      } else if (errorType === 'serviceNotSelectedForDiscount') {
        this.displayServiceNotSelectedForDiscountMessage = false;
        this.serviceNotSelectedForDiscountMessageTimeout = null;
      } else if (errorType === 'discountCoveredByPromoCampaign') {
        this.displayDiscountCoveredByPromoCampaignMessage = false;
        this.discountCoveredByPromoCampaignMessageTimeout = null;
      }
    }, delayMs);

    // Store timeout reference
    if (errorType === 'exceedCard') {
      this.exceedCardMessageTimeout = timeout;
    } else if (errorType === 'cardDoesNotExist') {
      this.cardDoesNotExistMessageTimeout = timeout;
    } else if (errorType === 'discountAlreadyExists') {
      this.discountAlreadyExistsMessageTimeout = timeout;
    } else if (errorType === 'noPermissionForMainServicePromo') {
      this.noPermissionForMainServicePromoMessageTimeout = timeout;
    } else if (errorType === 'serviceNotSelectedForDiscount') {
      this.serviceNotSelectedForDiscountMessageTimeout = timeout;
    } else if (errorType === 'discountCoveredByPromoCampaign') {
      this.discountCoveredByPromoCampaignMessageTimeout = timeout;
    }
  }

  private findLastFirstVersion(versionData: any): any {
    while (versionData.firstVersion !== null) {
      versionData = versionData.firstVersion;
    }
    return versionData;
  }

  private fillPromoRvsAndServices(promotionId: number) {
    this.promotionService.findServicesAndRvsByPromotionId(promotionId).subscribe(res => {
      if (res) {
        const rvsCodes = res.rvsCodes;
        const services = res.services;
        if (rvsCodes && Array.isArray(rvsCodes)) {
          this.promocodeAvailableCategories.push(...rvsCodes);
        }
        if (services && Array.isArray(services)) {
          this.promocodeAvailableServices.push(...services);
        }
      }
    });
  }

  addRemovePhoneValidator(isAgrNotif: boolean) {
    let phoneNumberControl = this.additionalTechInspForm.get('phoneNumber');
    if (this.annualService != null) {
      phoneNumberControl?.clearValidators();
      phoneNumberControl?.setValidators([Validators.required, Validators.maxLength(16), UtilValidators.validatePhoneNumber()]);
    } else {
      if (isAgrNotif) {
        phoneNumberControl?.clearValidators();
        phoneNumberControl?.setValidators([Validators.required, Validators.maxLength(16), UtilValidators.validatePhoneNumber()]);
      } else {
        phoneNumberControl?.clearValidators();
        phoneNumberControl?.setValidators([Validators.maxLength(16), UtilValidators.validatePhoneNumber()]);
      }
    }
    phoneNumberControl?.updateValueAndValidity();
  }

  addRemoveValidatorsWhilli() {
    let nameControl = this.additionalTechInspForm.get('name');
    if (this.isNotFromWhilli) {
      nameControl?.clearValidators();
      nameControl?.setValidators([Validators.required, Validators.maxLength(60)]);
    } else {
      nameControl?.clearValidators();
      nameControl?.setValidators([Validators.maxLength(60)]);
    }
    nameControl?.updateValueAndValidity();
  }

  openModal() {
    if (this.dialog.openDialogs.length == 0) {
      let ref = this.dialog.open(RegisterOfLegalPersonsModalComponent, modalMinWidth);
      this.subscriptions.add(ref.componentInstance.submitSubject.subscribe(result => {
        this.subjectMobileAppId = result?.mobileAppId;
        this.setSubjectData(result, true, true);
      }));

      let subjectId = this.additionalTechInspForm.get('subjectId')?.value;
      let subjectVersionData = this.additionalTechInspForm.get('subjectVersionData')?.value;
      if (subjectId != null) {
        ref.componentInstance.selectSubject(subjectId, subjectVersionData.id);
      } 
    }
  }

  clearSubject() {
    this.isHaveSubject = false;
    this.isSubjectFromPromocode = false;
    this.cleanPaymentServiceTable();
    this.additionalTechInspForm.get('subjectId')?.reset();
    this.additionalTechInspForm.get('subjectIdentNum')?.reset();
    this.additionalTechInspForm.get('subjectManager')?.reset();
    this.additionalTechInspForm.get('subjectInvoiceDdsReg')?.reset();
    this.additionalTechInspForm.get('subjectName')?.reset();
    this.additionalTechInspForm.get('subjectCity')?.reset();
    this.additionalTechInspForm.get('subjectAddress')?.reset();
    this.additionalTechInspForm.get('subjectVersionData')?.reset();
    this.additionalTechInspForm.get('customerDiscount')?.enable();
    this.additionalTechInspForm.get('customerCard')?.enable();

    this.nomenclatureService.getCustomerDiscountsBySubjectType(1).subscribe(customerDiscounts => {
      this.customerDiscounts = this.filterDiscountsByType(customerDiscounts);
      const canApply = this.applyLoyaltyCardDiscountForAllowedServices();
      if (!canApply) {
        return;
      }
      this.applyCustomerCardIfHad();
    });
  }

  async onSubmitMainForm() {
    this.findAlreadyStarted();
    this.submittedMainForm = true;
    if (this.submittedAdditionalForm) {
      if (this.mainTechAndAdditionalFormsValidation()) {
        if (this.isNotFromWhilli) {
          this.notifyRequiredFieldsMissing();
          return;
        }
      }
    } else {
      if (this.mainTechFormValidation()) {
        this.notifyRequiredFieldsMissing();
        return;
      }
      this.disableMainTechPointServices = true;
      this.mainTechInspForm.get('status')?.setValue(this.techInspStatuses.find(status => status.code === this.CODE_STARTED) || null);
      this.showAllFields = !this.showAllFields;
      
      if (this.mainTechInspForm.get('versionData')?.value === null || this.mainTechInspForm.get('versionData')?.value.id === undefined) {
        const assignedLineIds = this.assignedLines.length > 0 ? this.assignedLines.map(l => l.id) : undefined;
        let resp = await this.techInspService.getTechnicalPointWorkingHourResponse(this.mainTechInspForm.get('basicTechPointService')?.value.duration, this.userTechnicalPointId, assignedLineIds).toPromise();
        let respStatus = resp.status;

        if (respStatus === this.RESP_OK) {
          this.techPointWorkingHourIds = resp.techPointWorkingHourIds;
        } else if (respStatus === this.RESP_CURRENT_SLOT_NOT_AVAILABLE) {
          this.showAllFields = false;
          this.showSlotWarningModal("messagebox.currentSlotNotAvailable");
          return;
        } else if (respStatus === this.RESP_HAS_NOT_AVAILABLE_SLOT) {
          this.showAllFields = false;
          this.showSlotWarningModal("messagebox.hasNotAvailableSlot");
          return;
        }
      }

      // The table is on screen from here on, so it is priced straight away rather than waiting
      // for the first change. Only once the slot checks above have passed - they can still turn
      // showAllFields back off and return.
      this.refreshPaymentServicesTable();
    }

    this.loading = true;
    this.disableSaveButton = true;
    this.additionalTechInspForm.get('customerDiscount')?.enable();

    this.techInspService.saveTechnicalInspection(this.constructTechInspection(this.isDeferPayment), this.arePastLoaded).subscribe(versionData => {
      this.disableSaveButton = false;
  
      if (this.technicalInspection == null) {
        this.techInspService.findTechnicalInspectionById(versionData?.objectId).subscribe(ti =>this.currentTechnicalInspectionTimes = ti.techInspectionTimes!);
      }

      if (versionData) {
        this.mainTechInspForm.get('versionData')?.setValue(versionData);
        this.selectedTechPointServices = [];

        this.additionalServices.forEach(additionalTechPointService => {
          this.technicalInspection?.techInspectionServices?.forEach(techInspService => {
            if (additionalTechPointService.id === techInspService.technicalPointService!.id) {
              const index = this.selectedAdditionalServices.findIndex(object => object.id === additionalTechPointService.id);
              if (index === -1) {
                this.selectedAdditionalServices.push(additionalTechPointService);
              }
            }
          });
        });

        if (!this.submittedAdditionalForm) {
          this.getLineNumberByVersionId(versionData.id!);
          this.getCustomerSubscriptionByRegNumber(this.mainTechInspForm.get('regNumber')?.value);

          // Starting a reservation creates a NEW technical inspection row (new primary-key id) with status
          // STARTED and invalidates the previous RESERVED version. findTechnicalInspectionById looks up by
          // that primary-key id, so the URL must point at the new version — otherwise a page refresh reloads
          // the stale RESERVED version, shows the Start button again and lets the user create a duplicate
          // STARTED inspection on the same order. versionData.objectId is the backend-assigned id of the
          // newly created inspection.
          if (versionData.objectId != null) {
            this.router.navigate(['/add-edit-technical-inspection'], { queryParams: { id: versionData.objectId } });
          }
        }

        if (this.issueInvoice) {
          this.router.navigate(['/add-edit-accounting-document'], { relativeTo: this.route, queryParams: { techInspectionId: versionData.objectId } });
        }

        if (this.submittedAdditionalForm) {
          this.uiEvent.displayUISuccess();

          if (this.viewInvoice) {
            if (this.technicalInspection.techInspectionOrders![0]?.order?.accountingDocument?.invoice != null) {
              this.router.navigate(['/add-edit-accounting-document'], {queryParams: {id: this.technicalInspection.techInspectionOrders![0]?.order?.accountingDocument?.invoice?.id}});
            } else {
              this.router.navigate(['/add-edit-accounting-document'], {queryParams: {id: this.technicalInspection.techInspectionOrders![0]?.order?.accountingDocument?.id}});
            }
          } else if (!this.issueInvoice) {
            this.router.navigate(['/add-edit-technical-inspection'], { relativeTo: this.route });
          }

          if (this.viewCreditAndDebit) {
            if (this.technicalInspection.techInspectionOrders![0]?.order?.accountingDocument?.invoice != null) { 
              this.router.navigate(['/add-edit-accounting-document'], {queryParams: {id: this.technicalInspection.techInspectionOrders![0]?.order?.accountingDocument?.id}});
            }
          }

          if (this.isBankPayment) {
            this.router.navigate(['/add-edit-accounting-document'], {queryParams: {techInspectionId: versionData.objectId}});
          }

          if (this.isDebit || this.isCredit) {
            this.router.navigate(['/add-edit-accounting-document'], 
                                {relativeTo: this.route, 
                                  queryParams: {techInspectionId: versionData.objectId, 
                                                attachedInvoiceId: this.technicalInspection.techInspectionOrders![0]?.order?.accountingDocument?.id,
                                                documentType: this.isDebit ? 'D' : 'C'}});
          }
          
          this.prepareNewTechnicialInpection();
          this.loadFutureTechInspections();
          this.loadDefaultAvailableSlotFilters();
        }
      } else {
        this.showNoAvailableSlotsModal();
      }
    }, error => {
        this.loading = false;
        displayError(error);
    });

    this.isDeferPayment = false;
  }

  async showSlotWarningModal(warnMsg: String) { 
    let dialogRef = this.dialog.open(WarningMessageboxComponent, {
      ...modalMinWidth,
      data: {
        message: warnMsg
      }
    });

    await dialogRef.afterClosed().toPromise().then((isConfirm) => {
      if (isConfirm) {
        // Do nothing
      }
    });
  }

  getLineNumberByVersionId(versionDataId: number) {
    this.techInspService.getLineNumberByVersionId(versionDataId).subscribe(lineNumber => {
      this.mainTechInspForm.get('lineNumber')?.setValue(lineNumber);
    });
  }

  getCustomerSubscriptionByRegNumber(regNumber: string) {
    this.techInspService.getCustomerSubscriptionByRegNumber(regNumber).subscribe(customerSubscription => {
      if (customerSubscription != null || customerSubscription != undefined) {
        if (customerSubscription.status.code == this.CODE_ACTIVE || customerSubscription.status.code == this.CODE_AWAITING_PAYMENT) {
          this.disableAnnualServiceAndDiscount = true;
          this.annualService = null;
        }
        this.customerSubscriptionName = customerSubscription.name;
        this.customerSubscription = customerSubscription;
        
        this.techInspService.getCustomerPhoneByCustomerRvs(customerSubscription.customerRvs.id).subscribe(resp => {
          this.additionalTechInspForm.get('phoneNumber')?.patchValue(resp.phoneNumber);
        });
      }
    });
  }

  onSubmitAdditionalFormDeferPayment() {
    this.isDeferPayment = true;
    this.onSubmitAdditionalForm();
  }

  onSubmitAdditionalFormBankPayment() {
    this.isBankPayment = true;
    this.onSubmitAdditionalForm();
  }

  onSubmitAdditionalForm() {
    this.submittedAdditionalForm = true;
    this.mainTechInspForm.get('status')?.setValue(this.techInspStatuses.find(status => status.code === this.CODE_FINISHED) || null);
    this.additionalTechInspForm.get('createdOrder')?.setValue(true);
    this.onSubmitMainForm();
  }

  /**
   * Saves a finished inspection without changing how it was paid for.
   *
   * The payment type is not stored on the inspection - the backend picks it from the
   * isDeferPayment / isBankPayment flags on every save, and both are false on a freshly loaded
   * screen. Saving through the ordinary handler therefore re-stamped an order that had been
   * deferred, or paid by bank, as "на каса в пункт".
   *
   * The two flags arrive derived from the order that already exists, so putting them back before
   * saving is what keeps the payment type as it was. This is the same restore that
   * onSubmitViewInvoice does, for the same reason.
   */
  onSubmitAdditionalFormKeepingPaymentType() {
    this.isDeferPayment = this.technicalInspection?.isDeferPayment === true;
    this.isBankPayment = this.technicalInspection?.isBankPayment === true;
    this.onSubmitAdditionalForm();
  }

  onSubmitAdditionalFormWithoutPayment() {
    this.submittedAdditionalForm = true;
    this.mainTechInspForm.get('status')?.setValue(this.techInspStatuses.find(status => status.code === this.CODE_FINISHED) || null);
    this.onSubmitMainForm();
  }

  onSubmitAdditionalFormAnnulModalFutureReservation(id: number) {
    this.onSubmitAdditionalFormAnnulModal();
    this.futureTechInspId = id; 
  }

  onSubmitAdditionalFormTerminatedModal() {
    this.dialogRef  = this.dialog.open(this.confirmTerminationDialog, {
      width: '800px',
      height: '200px'
    });
  }

  onSubmitAdditionalFormAnnulModal() {
    this.dialogRef  = this.dialog.open(this.confirmAnnul, {
      width: '800px',
      height: '200px'
    });
  }

  onSubmitAdditionalFormTerminated() {
    this.dialogRef.afterClosed().subscribe((result: any) => {
      if (result) {
        if (this.futureTechInspId) {
          this.changeTechInspStatus(this.futureTechInspId, this.CODE_ANNULLED);
          this.futureTechInspId = 0;
          return;
        } 
        this.submittedAdditionalForm = true;
        this.mainTechInspForm.get('status')?.setValue(this.techInspStatuses.find(status => status.code === this.CODE_TERMINATED) || null);
        this.onSubmitMainForm();
      } else {
        this.additionalTechInspForm.get('rejectReason')?.setValue(null);
      }
    });
  }

  async onSubmitViewInvoice() {
    if (this.technicalInspection?.incomingWay?.includes('whilli')) {
      this.router.navigate(['/add-edit-accounting-document'], { queryParams: { id: this.technicalInspection.techInspectionOrders![0]?.order?.accountingDocument?.id } });
    } else {
      this.technicalInspection?.isDeferPayment ? this.isDeferPayment = true : this.isDeferPayment = false;
      this.technicalInspection?.isBankPayment ? this.isBankPayment = true : this.isBankPayment = false;
      this.submittedAdditionalForm = true;
      this.additionalTechInspForm.get('createdOrder')?.setValue(true);
      this.viewInvoice = true;
      // this.onSubmitMainForm();
      if (this.technicalInspection) {
        if (this.technicalInspection.id) {
          this.router.navigate(['/add-edit-accounting-document'], { queryParams: { id: this.technicalInspection.techInspectionOrders![0]?.order?.accountingDocument?.id } });
        }
      }
    }
  }

  onSubmitViewCreditOrDebit() {
    this.submittedAdditionalForm = true;
    this.additionalTechInspForm.get('createdOrder')?.setValue(true);
    this.viewCreditAndDebit = true;
    this.onSubmitMainForm();
  }

  onSubmitAdditionalFormAndIssueInvoice() {
    if (this.technicalInspection?.incomingWay?.includes('whilli') && this.technicalInspection.status?.code == this.CODE_FINISHED) {
      this.router.navigate(['/add-edit-accounting-document'], { relativeTo: this.route, queryParams: { techInspectionId: this.mainTechInspForm.get('id')?.value } });
    } else {
      this.technicalInspection?.isDeferPayment ? this.isDeferPayment = true : this.isDeferPayment = false;
      this.technicalInspection?.isBankPayment ? this.isBankPayment = true : this.isBankPayment = false;
      this.submittedAdditionalForm = true;
      this.mainTechInspForm.get('status')?.setValue(this.techInspStatuses.find(status => status.code === this.CODE_FINISHED) || null);
      this.additionalTechInspForm.get('createdOrder')?.setValue(true);
      this.issueInvoice = true;
      this.onSubmitMainForm();
    }
  }

  generateCreditDocument() {
    this.submittedAdditionalForm = true;
    this.mainTechInspForm.get('status')?.setValue(this.techInspStatuses.find(status => status.code === this.CODE_FINISHED) || null);
    this.additionalTechInspForm.get('createdOrder')?.setValue(true);
  
    this.onSubmitMainForm();
  }

  generateDebitDocument() {
    this.submittedAdditionalForm = true;
    this.mainTechInspForm.get('status')?.setValue(this.techInspStatuses.find(status => status.code === this.CODE_FINISHED) || null);
    this.additionalTechInspForm.get('createdOrder')?.setValue(true);
  
    this.onSubmitMainForm();
  }

  private async loadAssignedLines() {
    const employeeId = AuthenticationService.getEmployeeId();
    if (employeeId == null) return;

    try {
      this.assignedLines = await this.authService.getAssignedLines(employeeId).toPromise() as {id: number, number: number}[];
    } catch (err) {
      console.error('Failed to load assigned lines', err);
    }
  }

  private filterFutureTechInspectionsByEmployeeLines(futureTechInspections: TechnicalInspection[]): TechnicalInspection[] {
    if (this.assignedLines.length === 0) {
      return futureTechInspections;
    }

    const assignedLineIds = this.assignedLines.map(line => line.id);
    return futureTechInspections.filter(techInsp => {
      const lineId = techInsp.techInspectionTimes?.[0]?.technicalPointWorkingHours?.line?.id;
      return lineId && assignedLineIds.includes(lineId);
    });
  }

  loadFutureTechInspections() {
    this.techInspService.getFutureTechnicalInspections(this.userTechnicalPointId).subscribe(futureTechInspections => {
      futureTechInspections.forEach(futureTechInsp => {
        futureTechInsp.inspectionDate = this.prepareDateForUi(futureTechInsp.inspectionDate);
      });
      // Filter by assigned lines
      const filteredTechInspections = this.filterFutureTechInspectionsByEmployeeLines(futureTechInspections);
      this.futureTechInspections = filteredTechInspections;
      this.showHideLateAndStartButton(this.futureTechInspections);
    });
  }

  changeTechInspStatus(id: number, statusCode: string): void {
    this.techInspService.changeTechInspStatus(id, statusCode).subscribe({
      next: (data) => {
        this.uiEvent.displayUISuccessChange();
        this.loadFutureTechInspections();
        this.loadDefaultAvailableSlotFilters();
      }, 
    });
  }

  checkForContract(regNumber: string) {
    if (regNumber !== '') {
      this.techInspService.checkForExistingContractByRegNumber(regNumber).subscribe(contractRvsList => {
        if (contractRvsList.length > 1) {
          this.showMultipleContractWarningModal();
        } else if (contractRvsList.length != 0) {
          const contractRvs = contractRvsList[0];
          this.mainTechInspForm.get('registrationCertNumber')?.setValue(contractRvs.registrationCertNum);
          this.additionalTechInspForm.get('contractRvsId')?.setValue(contractRvs.id);
          this.additionalTechInspForm.get('phoneNumber')?.setValue(contractRvs.phoneNumber);
          this.additionalTechInspForm.get('customerDiscount')?.setValue(contractRvs.customerDiscount);
        }
      }, error => {
        displayError(error);
        this.loading = false;  
      });
    }
  }

  async showMultipleContractWarningModal() { 
    let dialogRef = this.dialog.open(WarningMessageboxComponent, {
      ...modalMinWidth,
      data: {
        message: "messagebox.multipleCustomerDiscounts"
      }
    });

    await dialogRef.afterClosed().toPromise().then((isConfirm) => {
      if (isConfirm) {
        // Do nothing
      }
    });
  }

  onRvsCategorySelect(rvsCategory: any) {        
    this.isChangedRvsCategory = true;
    
    this.selectedAdditionalServices = [];
    this.rvsAdditionalSubject.next(rvsCategory);

    if (this.subjectMobileAppId != null) {
      this.subjectHasContract(this.subjectMobileAppId, true);
    }
    this.hasPromocode = this.promocodeAvailableCategories.includes(rvsCategory.code);
    this.cleanPaymentServiceTable();
  }

  onBasicTechPointServiceSelect() {
    const value = this.mainTechInspForm.get('basicTechPointService')?.value;
    if (value) {
      this.hasPromocode = this.promocodeAvailableServices.includes(value.serviceName);
    }

    if (this.subjectMobileAppId != null) {
      this.subjectHasContract(this.subjectMobileAppId, true);
      const canApply = this.applyLoyaltyCardDiscountForAllowedServices();
      if (!canApply) {
        this.resetCustomerCardAndDiscount();
        return;
      }
    }
   
    this.cleanPaymentServiceTable();
    const canApply = this.applyLoyaltyCardDiscountForAllowedServices();
    if (!canApply) {
      this.resetCustomerCardAndDiscount();
      return;
    }
  }

  private resetCustomerCardAndDiscount() {
    this.additionalTechInspForm.get('customerCard')?.setValue('');
    this.additionalTechInspForm.get('customerDiscount')?.setValue('');
  }

  onCustomerDiscountSelect() {
    this.cleanPaymentServiceTable();

    if (this.lastSelectedDiscountByCard) {
      const discount = this.additionalTechInspForm.get('customerDiscount')?.value as CustomerDiscount;
      if (discount?.discountName !== this.lastSelectedDiscountByCard) {
        this.customerCard = null;
        this.additionalTechInspForm.get('customerCard')?.setValue('');
      }
    }

    const selectedDiscount = this.additionalTechInspForm.get('customerDiscount')?.value as CustomerDiscount | null;
    if (!selectedDiscount) {
      // User cleared the dropdown: remove any customer-discount from appliedDiscounts
      const idx = this.appliedDiscounts.findIndex(ad => ad.type === 'customer-discount');
      if (idx !== -1) {
        this.appliedDiscounts.splice(idx, 1);
      }
      return;
    }

    // Only allow customer discount when selected basic service is one of the main services that support it
    const selectedBasicName = this.getSelectedBasicServiceName();
    if (!selectedBasicName) {
      return;
    }
    const selectedNameTrimmed = selectedBasicName.trim();
    const isAllowedMainService = this.mainServiceNamesForDiscount.some(
      name => name.trim() === selectedNameTrimmed
    );
    if (!isAllowedMainService) {
      return;
    }

    // Remove any existing customer-discount so we can replace with the newly selected one
    const existingIdx = this.appliedDiscounts.findIndex(ad => ad.type === 'customer-discount');
    if (existingIdx !== -1) {
      this.appliedDiscounts.splice(existingIdx, 1);
    }

    // Check if main service already has another applied discount (card or promocode for main service)
    const mainServiceHasOtherDiscount = this.appliedDiscounts.some(ad => {
      if (ad.type === 'promocode' && ad.promoCodeService?.promotionServiceName) {
        return selectedBasicName ? ad.promoCodeService.promotionServiceName === selectedBasicName : this.mainServiceNamesForDiscount.includes(ad.promoCodeService.promotionServiceName);
      }
      if (ad.type === 'card') {
        return true;
      }
      return false;
    });

    if (mainServiceHasOtherDiscount) {
      return;
    }

    this.appliedDiscounts.push({
      type: 'customer-discount',
      name: selectedDiscount.discountName,
      discount: selectedDiscount
    });

    // Set discount information on the corresponding service in paymentServiceTableData (selected basic service)
    if (this.paymentServiceTableData) {
      const targetService = this.paymentServiceTableData.find(service => service.serviceName === selectedBasicName);
      if (targetService) {
        targetService.discountId = selectedDiscount.id;
        targetService.discountTypeName = 'LOYAL_CARD_PLASTIC';
        // targetService.discountKindName = discountKind as 'V' | 'P';
        // targetService.discountValue = discountValue;
      }
    }

    // Also set on services in basicServices, additionalServices, and annualServices if they match
    const allServices = [
      ...(this.basicServices || []),
      ...(this.additionalServices || []),
      ...(this.annualServices || [])
    ];
    const matchingService = allServices.find(service => service.serviceName === selectedBasicName);
    if (matchingService) {
      matchingService.discountId = selectedDiscount.id;
      matchingService.discountTypeName = 'LOYAL_CARD_PLASTIC';
      // matchingService.discountKindName = discountKind as 'V' | 'P';
      // matchingService.discountValue = discountValue;
    }
  }

  onRejectReasonSelect(rejectReason: any) {
    if (rejectReason == '') {
      this.additionalTechInspForm.get('rejectReason')?.patchValue(null);
    }
  }

  onChangeAdditionalService(event: any, service: TechPointService) {
    if (event.target.checked) {
      // Default to one on the way in, so the box beside the checkbox is never blank.
      if (this.additionalServiceQuantities[service.id!] == null) {
        this.additionalServiceQuantities[service.id!] = 1;
      }
      this.selectedAdditionalServices.push(service);
    } else {
      let index = this.selectedAdditionalServices.indexOf(service);
      this.selectedAdditionalServices.splice(index,1);
      // Forget the quantity with the service: re-checking it later starts at one again rather
      // than silently restoring a figure the operator can no longer see.
      delete this.additionalServiceQuantities[service.id!];
    }

    this.cleanPaymentServiceTable();
  }

  /**
   * The quantity box beside a checked additional service.
   *
   * Unchecking is how a service is removed, so the box floors at one - a zero would otherwise
   * put a line on the bill that costs nothing.
   */
  onChangeAdditionalServiceQuantity(service: TechPointService) {
    const quantity = Number(this.additionalServiceQuantities[service.id!]);
    this.additionalServiceQuantities[service.id!] =
        !quantity || quantity < 1 ? 1 : Math.floor(quantity);

    this.cleanPaymentServiceTable();
  }

  // ****************************************************
  // ********* Goods sold alongside the inspection *******
  // ****************************************************

  onItemSearchChange(term: string) {
    this.itemSearchSubject.next((term ?? '').trim());
  }

  /**
   * Adds an article to the bill, but only once iCash confirms the stock is there.
   *
   * The quantity shown in the search list comes from iCash's last sync and can be out of date, so
   * the stock is read live here - at the one moment it matters - rather than trusted from the
   * list. The check is against the running total for the article, not just the amount being
   * added, so repeated small additions cannot walk past the stock a unit at a time.
   */
  addCatalogItem(item: IcashCatalogItem) {
    const techPointMobileAppId = this.userTechnicalPointMobileIds?.[0];
    if (techPointMobileAppId == null) { return; }

    const quantity = this.normalizeItemQuantity(this.itemQuantities[item.saleItemId]);
    const existing = this.itemLines.find(line => line.saleItemId === item.saleItemId);
    const requested = (existing?.quantity ?? 0) + quantity;

    this.clearItemMessage(item.saleItemId);
    this.checkingStockForSaleItemId = item.saleItemId;

    this.subscriptions.add(
      this.saleService.findLiveCatalogItem(techPointMobileAppId, item.saleItemId).subscribe({
        next: live => {
          this.checkingStockForSaleItemId = null;

          // iCash sends qty null when it cannot determine the stock, which is not the same as
          // zero - treat it as unknown and let the add through rather than blocking on a figure
          // that was never reported.
          const available = live?.qty;
          if (available != null && available < requested) {
            this.showItemMessage(item.saleItemId,
                this.translateService.instant('sales.goods.notEnoughQuantity', { available: available }) as string);
            return;
          }

          this.commitCatalogItem(item, live, quantity, existing);
        },
        error: err => {
          displayError(err);
          this.checkingStockForSaleItemId = null;
          // No confirmation of stock means no add: the whole point of the check is to keep the
          // counter from selling what is not there.
          this.showItemMessage(item.saleItemId, this.resolveItemErrorMessage(err));
        }
      })
    );
  }

  /** Puts the article on the bill once its stock has been confirmed. */
  private commitCatalogItem(item: IcashCatalogItem, live: IcashCatalogItem | null,
                            quantity: number, existing: ItemLine | undefined) {
    // The same article added twice is one line with a larger quantity, not two lines - that is
    // how it is rung up and how the chip above the bill reads.
    if (existing) {
      existing.quantity += quantity;
    } else {
      this.itemLines.push({
        saleItemId: item.saleItemId,
        name: item.name,
        quantity: quantity,
        // The live reading is the fresher price, so it wins over the one the search returned.
        unitPrice: live?.sellPrice ?? item.sellPrice ?? 0,
        discount: 0,
        // Empty rather than "0", so the operator types into a blank box instead of having to
        // clear a zero first. An empty box counts as no discount.
        discountInput: '',
        inventoryId: item.inventoryId,
        barcode: item.barcode,
        measureUnitName: item.measureUnitName
      });
    }

    this.itemSearchTerm = '';
    this.catalogResults = [];
    this.itemQuantities[item.saleItemId] = 1;
    // The price table is rebuilt from the bill, so it has to be invalidated the way adding a
    // service invalidates it.
    this.cleanPaymentServiceTable();
  }

  removeItemLine(line: ItemLine) {
    this.itemLines = this.itemLines.filter(l => l !== line);
    this.cleanPaymentServiceTable();
  }

  /**
   * Reads the discount box while it is being typed in.
   *
   * The figure is taken as far as it parses and the price table is repriced from it, so the
   * totals follow each keystroke. Nothing is corrected here - a half-typed "1." or a figure
   * that is momentarily too large is left exactly as typed and only settled on blur, because
   * rewriting the box under the cursor is what makes these fields unusable.
   */
  onItemLineDiscountInput(line: ItemLine) {
    const parsed = this.parseItemLineDiscount(line.discountInput);
    // An unparseable or out-of-range entry bills the line at full price until it becomes valid,
    // rather than silently keeping the last good figure, which would show a discount the box
    // does not.
    line.discount = this.isItemLineDiscountInvalid(line) || parsed == null ? 0 : parsed;
    this.cleanPaymentServiceTable();
  }

  /**
   * Settles the discount box once the operator leaves it.
   *
   * Only tidying happens here - a valid figure is written back at two decimals and anything
   * invalid is cleared to no discount. The value is never clamped to the line's total: silently
   * turning 100 into 19.99 reads as if the entry were accepted.
   */
  capItemLineDiscount(line: ItemLine) {
    const parsed = this.parseItemLineDiscount(line.discountInput);

    if (parsed == null || parsed <= 0 || parsed >= this.itemLineGross(line)) {
      line.discount = 0;
      line.discountInput = '';
    } else {
      line.discount = this.roundMoney(parsed);
      line.discountInput = line.discount.toFixed(2);
    }

    this.cleanPaymentServiceTable();
  }

  /**
   * The discount box as a number, or null when it does not read as one.
   *
   * A comma is accepted as the decimal separator: the operators type on a Bulgarian layout and
   * the numeric keypad there gives a comma.
   */
  private parseItemLineDiscount(raw: string | null | undefined): number | null {
    const text = (raw ?? '').trim().replace(',', '.');
    if (text === '') { return null; }
    // Number('') is 0 and Number('1.2.3') is NaN, so the pattern is what rejects a stray second
    // dot or a letter before the conversion ever happens.
    if (!/^\d*\.?\d*$/.test(text)) { return null; }

    const value = Number(text);
    return isNaN(value) ? null : value;
  }

  /**
   * Whether what is in the discount box cannot be used. An empty box is not invalid - it is how
   * a line at full price is written.
   */
  isItemLineDiscountInvalid(line: ItemLine): boolean {
    const text = (line.discountInput ?? '').trim();
    if (text === '') { return false; }

    const parsed = this.parseItemLineDiscount(text);
    if (parsed == null) { return true; }

    // A trailing dot is mid-typing, not a mistake, so it is left alone while the box has focus.
    if (text.endsWith('.') || text.endsWith(',')) { return false; }

    return parsed <= 0 || parsed >= this.itemLineGross(line);
  }

  /** What is wrong with the discount box, for the message under it. */
  itemLineDiscountError(line: ItemLine): string {
    const parsed = this.parseItemLineDiscount(line.discountInput);
    if (parsed == null) {
      return this.translateService.instant('errors.onlyDigits') as string;
    }
    return this.translateService.instant('sales.goods.discountOutOfRange',
                                         { max: this.itemLineGross(line).toFixed(2) }) as string;
  }

  itemLineGross(line: ItemLine): number {
    return this.roundMoney(line.unitPrice * line.quantity);
  }

  itemLineTotal(line: ItemLine): number {
    return this.roundMoney(this.itemLineGross(line) - (line.discount ?? 0));
  }

  /** What the goods add to the amount due. */
  get itemsTotal(): number {
    return this.roundMoney(this.itemLines.reduce((sum, line) => sum + this.itemLineTotal(line), 0));
  }

  /**
   * The amount due, summed from the rows of the price table as they are displayed.
   *
   * Taken from paymentServiceConvertedData rather than from currentAmountServices, which looks
   * like it would do: that one is summed from paymentServiceTableData, a different list that
   * holds technical point services only, so on a loaded inspection it can disagree with the rows
   * on screen. A figure printed under a column has to be that column's own total.
   *
   * amountDds is already net of any discount at every site that fills this list - a promotion
   * writes fullPrice minus the discount, a stored element contributes its totalAmount, and a good
   * contributes itemLineTotal - so calculatedDiscount is shown beside it for the operator's
   * benefit and must not be subtracted again here.
   */
  get paymentTableTotal(): number {
    return this.roundMoney(
        this.paymentServiceConvertedData.reduce((sum, row) => sum + (row.amountDds || 0), 0));
  }

  /**
   * Multiplies the price table rows of any additional service chosen more than once.
   *
   * The rows are matched by service name, which is how this table identifies a row throughout -
   * there is one row per service however many were sold, and the name gains a "2 x " prefix so
   * the operator can see why the figure is what it is.
   *
   * Both the amount and the discount are scaled: the discount applies to the whole line.
   */
  private applyAdditionalServiceQuantities() {
    this.selectedAdditionalServices.forEach(service => {
      const quantity = this.additionalServiceQuantities[service.id];
      if (quantity == null || quantity <= 1) { return; }

      const row = this.paymentServiceConvertedData.find(r => r.serviceName === service.serviceName);
      if (row == null) { return; }

      row.amountDds = this.roundMoney((row.amountDds ?? 0) * quantity);
      if (row.calculatedDiscount != null) {
        row.calculatedDiscount = this.roundMoney(row.calculatedDiscount * quantity);
      }
      // The quantity goes in its own column rather than into the name. Leaving the name alone
      // also keeps the match above working: it finds the row by service name, which a "2 x "
      // prefix would have broken had this ever run twice over the same table.
      row.quantity = quantity;
    });
  }

  /**
   * Puts the goods into the Стъпка 4 price table, as rows of type "Стока".
   *
   * Follows the convention the services use in this table: amountDds is what is actually charged,
   * already net of the discount, and calculatedDiscount sits beside it to show what came off.
   * The total below sums amountDds alone, so anything else would double-count the reduction.
   *
   * The discount is 0 rather than null when there is none - the column renders through the number
   * pipe, and null prints as an empty cell.
   */
  private addItemsToPaymentServiceTable() {
    this.itemLines.forEach(line => {
      // The quantity has a column of its own now, so the name is just the name - it used to be
      // prefixed with "2 x " because this table had nowhere else to show it.
      this.paymentServiceConvertedData.push(
          createPaymentServiceData(line.name, this.GOODS_SERVICE_TYPE,
                                   this.itemLineTotal(line), line.discount ?? 0, line.quantity));
    });
  }

  /** A rejection shown on one picker row, cleared on a timer. */
  private showItemMessage(saleItemId: number, message: string) {
    this.clearItemMessage(saleItemId);
    this.itemMessages[saleItemId] = message;

    this.itemMessageTimeouts[saleItemId] = setTimeout(() => {
      delete this.itemMessages[saleItemId];
      delete this.itemMessageTimeouts[saleItemId];
    }, this.ITEM_MESSAGE_TIMEOUT_MS);
  }

  private clearItemMessage(saleItemId: number) {
    if (this.itemMessageTimeouts[saleItemId]) {
      clearTimeout(this.itemMessageTimeouts[saleItemId]);
      delete this.itemMessageTimeouts[saleItemId];
    }
    delete this.itemMessages[saleItemId];
  }

  private normalizeItemQuantity(quantity: number | null | undefined): number {
    const parsed = Number(quantity);
    return !parsed || parsed < 1 ? 1 : Math.floor(parsed);
  }

  private roundMoney(value: number): number {
    return Math.round((value + Number.EPSILON) * 100) / 100;
  }

  /**
   * The goods as the backend reads them: the article, how many, and the discount the operator
   * typed. Prices are deliberately not sent - they are re-read from iCash while saving.
   */
  private constructItems(): TechnicalInspectionItem[] {
    return this.itemLines.map(line => ({
      saleItemId: line.saleItemId,
      quantity: line.quantity,
      discountAmount: line.discount
    }));
  }

  /**
   * Rebuilds the goods lines from a stored inspection.
   *
   * The price and the discount come from the order element that billed each item rather than from
   * the item row, because that is where what was actually charged is recorded.
   */
  private restoreItemLines(techInspection: TechnicalInspection) {
    this.itemLines = [];

    const items = techInspection?.techInspectionItems ?? [];
    if (items.length === 0) { return; }

    const orderElements = techInspection?.techInspectionOrders?.[0]?.order?.orderElements ?? [];

    items.forEach(item => {
      const element = orderElements.find(oe => oe.technicalInspectionItem?.id === item.id);
      const discount = Number(element?.discountAmount ?? 0);

      this.itemLines.push({
        saleItemId: item.saleItemId,
        name: item.name ?? '',
        quantity: item.quantity ?? 1,
        unitPrice: Number(item.price ?? 0),
        discount: discount,
        // A line sold at full price shows an empty box rather than "0", matching a freshly
        // added one.
        discountInput: discount > 0 ? discount.toFixed(2) : '',
        inventoryId: item.inventoryId,
        barcode: item.barcode,
        measureUnitName: item.measureUnitName
      });
    });
  }

  async onChangeAnnualService(event: any, service: TechPointService) {
    this.cleanPaymentServiceTable();
    if (this.annualService != null && this.annualService.id === service.id) {
      this.annualService = null;
      return;
    }

    this.annualService = service;
    this.getServicesMobileAppIdsForCard(this.annualService.serviceId!);
    this.additionalTechInspForm.get('customerDiscount')?.patchValue('');
    this.addRemovePhoneValidator(this.additionalTechInspForm.get("isAgreeWithNotification")?.value);

    // The subscription check used to run when the operator pressed "покажи данни за плащане".
    // With the table always on screen there is no such press, so it happens here instead - at
    // the moment a subscription is actually chosen, which is what it was always asking about.
    // It can clear annualService, so the table is priced again afterwards.
    let additionalData = this.additionalTechInspForm.value;
    if (additionalData.phoneNumber != null && additionalData.phoneNumber != ''
          && this.technicalInspection?.status?.code !== this.CODE_FINISHED) {
      await this.confirmAnnualSubscription(additionalData);
    }

    this.refreshPaymentServicesTable();
  }

  /**
   * Clears the price table and prices it again from the current state.
   *
   * Called from everything that changes what is owed - a service checked or unchecked, a promo
   * code applied or removed, a card, a good added or its discount typed. The table used to be
   * hidden here and brought back by the button; now there is no button, so the clear is followed
   * by a rebuild and the figures on screen are never stale.
   */
  cleanPaymentServiceTable() {
    this.submittedAdditionalForm = false;
    this.paymentServiceTableData = [];

    if (this.additionalTechInspForm.get('customerDiscount')?.value || this.technicalInspection?.techInspectionOrders == undefined 
        || this.technicalInspection?.techInspectionOrders![0]?.order?.promotion.id == null) {
      this.paymentServiceConvertedData = [];
    } else if (this.technicalInspection?.techInspectionOrders != undefined && this.technicalInspection?.techInspectionOrders![0]?.order?.promotion.id != null) {    
      // When we have a promotion we do not clear this service.
      let servicesWithPromotions: PaymentServiceData[] = [];
      this.paymentServiceConvertedData.forEach(service => {
        if (this.showAllFields == true) {
          if (service.calculatedDiscount != 0) {
            if (this.selectedAdditionalServices.length > 0) {
              this.selectedAdditionalServices.forEach(addService => {
                if (addService.serviceName == service.serviceName) {
                  servicesWithPromotions.push(service);
                }
              })
            }
            if (service.serviceType == this.BASIC_SERVICE_TYPE) {
              servicesWithPromotions.push(service);
            }
          } 
        } else {
          if (service.calculatedDiscount && service?.calculatedDiscount != 0) {
            servicesWithPromotions.push(service);
          } else {
            servicesWithPromotions = [];
          }
        }
      })
      
      this.paymentServiceConvertedData = [];
  
      servicesWithPromotions?.forEach(promotionService => {
        if (promotionService.calculatedDiscount != 0) {
          let basicService = this.mainTechInspForm.get('basicTechPointService')?.value;
          if(basicService?.serviceName != promotionService?.serviceName && promotionService?.serviceType == this.BASIC_SERVICE_TYPE) {
            this.paymentServiceConvertedData.push(createPaymentServiceData(basicService.serviceName, basicService.serviceType,
                                                                           basicService.amountDds, basicService.calculatedDiscount));
          } else {
            this.paymentServiceConvertedData.push(promotionService);
          }
        }

      });
      servicesWithPromotions = [];
    }

    // Price it again straight away. Deliberately not awaited: every caller of this is a UI
    // handler that has no use for the result, and awaiting would make each of them async.
    this.refreshPaymentServicesTable();
  }

  /**
   * Recomputes the price table in the background, after something that changes what is owed.
   *
   * The table is always on screen, so this runs on every such change rather than on a button.
   * That rules out the two things showPaymentServicesTable does for the operator: it does not
   * turn on the form's validation errors, and it does not open the subscription dialogs. Both
   * belong to an explicit action, and firing them while someone is still filling the form in
   * would paint the screen red and interrupt with a modal on every click.
   */
  async refreshPaymentServicesTable() {
    // Nothing to price until the review has started and a main service is chosen.
    if (!this.showAllFields || this.mainForm.basicTechPointService.value == null) {
      return;
    }

    this.calculatePaidAmountServices();
    // if annualService != null to get corect services for this card
    if (this.annualService != null) {
      this.getServicesMobileAppIdsForCard(this.annualService!.serviceId!);
    }

    await this.buildPaymentServicesTable();
  }


  /**
   * Warns when the customer already has a valid subscription, or asks whether to sell one.
   *
   * Split out of showPaymentServicesTable so the table can be rebuilt without it: these dialogs
   * are a question put to the operator, not part of pricing.
   */
  private async confirmAnnualSubscription(additionalData: any) {
    let mainData = this.mainTechInspForm.value;
    {
      let hasValidSubscription = await this.techInspService.checkForValidSubscription(additionalData.phoneNumber, mainData.regNumber, mainData.registrationCertNumber, this.annualService!.serviceId!).toPromise();

      if (hasValidSubscription) {
        let dialogRefWarn = this.dialog.open(WarningMessageboxComponent, {
          ...modalMinWidth,
          data: {
            message: "messagebox.validSubscription"
          }
        });
        await dialogRefWarn.afterClosed().toPromise();
        this.annualService = null;
      } else {
        let dialogRefWarn = this.dialog.open(YesNoMessageboxComponent, {
          ...modalMinWidth,
          data: {
            isWarning: false,
            message: "messagebox.cardPurchase"
          }
        });
        let isConfirmed = await dialogRefWarn.afterClosed().toPromise();
        if (!isConfirmed) {
          this.annualService = null;
        }
      }
    }
  }

  /**
   * Prices the chosen services and goods and fills the table with the result.
   *
   * Everything here is recomputed from scratch, so it is safe to call repeatedly - which is what
   * keeps the table honest now that it is always on screen. The working arrays are cleared first
   * for that reason: they are appended to below, and without the reset a second call would list
   * every service twice and double the total.
   */
  private async buildPaymentServicesTable() {
    let mainData = this.mainTechInspForm.value;

    this.isVisiblePaymentServicesTable = true;

    this.paymentServiceTableData = [];
    this.paymentServiceConvertedData = [];
    this.currentSelectedServiceMobileAppIds = [];

    if (this.selectedAdditionalServices.length > 0) {
      this.selectedAdditionalServices.forEach(service => this.paymentServiceTableData.push(service));
    }

    if (this.annualService != null) {
      this.paymentServiceTableData.push(this.annualService);
    }

    let mainService = this.mainForm.basicTechPointService.value;
    if (mainService && typeof mainService.amountDds === 'number') {
        mainService.amountDds = Number.parseFloat(mainService.amountDds.toFixed(2));
    }
    this.paymentServiceTableData.push(mainService);

    let orderElIds: number[] = [];

    this.paymentServiceTableData.forEach(service => {
      if (!this.additionalTechInspForm.get('customerDiscount')?.value && this.technicalInspection?.techInspectionOrders != undefined 
         && this.technicalInspection?.techInspectionOrders![0]?.order?.promotion?.id != null) {

        this.technicalInspection?.techInspectionOrders![0]?.order?.orderElements?.forEach(orderEl => {
          // Goods lines carry no service and can never match one, so they are skipped.
          if (orderEl.service == null) { return; }
          orderElIds.push(orderEl.service.mobileAppId);
          if (service.serviceMobileAppId == orderEl.service.mobileAppId){
            if (orderEl.discountAmount == 0){
              this.currentSelectedServiceMobileAppIds.push(service.serviceMobileAppId!);
            } 
          }})

          if (!orderElIds.includes(service.serviceMobileAppId!)) {
            this.currentSelectedServiceMobileAppIds.push(service.serviceMobileAppId!);
          }
      } else {
        this.currentSelectedServiceMobileAppIds.push(service.serviceMobileAppId!);
      }     
    });

    let result: any[] = []; 

    if (this.technicalInspection != null && this.technicalInspection.inspectionDate && this.currentSelectedServiceMobileAppIds.length != 0) {
      result = await this.techInspService.getTechPointServicePricesFromFunction(this.techPointWorkingHourIds[0], this.userTechnicalPointId, mainData.rvsCategory.code, this.currentSelectedServiceMobileAppIds, this.technicalInspection.inspectionDate!).toPromise();
      this.currentSelectedServiceMobileAppIds = [];
    } else {
      if (this.currentSelectedServiceMobileAppIds.length != 0) {
        result = await this.techInspService.getTechPointServicePricesFromFunction(this.techPointWorkingHourIds[0], this.userTechnicalPointId, mainData.rvsCategory.code, this.currentSelectedServiceMobileAppIds, new Date().toISOString().split('T')[0].split("-").reverse().join("-")).toPromise();
        this.currentSelectedServiceMobileAppIds = [];
      }
    }


    this.originalServicePrices.clear();
    this.paymentServiceTableData.forEach(service1 => {
      result.forEach(service2 => {
        if (service1.serviceMobileAppId === service2.serviceMobileAppId) {
          if (service1.serviceMobileAppId) {
            this.originalServicePrices.set(service1.serviceMobileAppId, service2.discountedPrice);
          }
          service1.amountDds = service2.discountedPrice;
          service1.calculatedDiscount = service2.discountedValue;
          service1.isFreeCampaign = false;

          //APPLY 100% DISCOUNT TO ADDITIONAL SERVICE IF ELIGIBLE FOR PROMOTIONAL CAMPAIGN
          // if the requirements are met and the concrete service is eligible for 100% discount we need to apply it here
          if (this.promotionalCampaign.isEligibleForFreeCampaign(service1, this.paymentServiceTableData, this.activeDiscountCampaigns)) {
            service1.calculatedDiscount = service2.discountedPrice;
            service1.amountDds = 0;
            service1.isFreeCampaign = true;
          } else {
            const triggerDiscount = this.getCampaignTriggerDiscount(service1);
            if (triggerDiscount) {
              if (triggerDiscount.discountKind === 'P') {
                const discountAmount = Math.round(service2.discountedPrice * (triggerDiscount.discountValue / 100) * 100) / 100;
                service1.calculatedDiscount = discountAmount;
                service1.amountDds = Math.round((service2.discountedPrice - discountAmount) * 100) / 100;
              } else {
                service1.calculatedDiscount = triggerDiscount.discountValue;
                service1.amountDds = Math.round((service2.discountedPrice - triggerDiscount.discountValue) * 100) / 100;
              }
              service1.discountKindName = triggerDiscount.discountKind as 'P' | 'V';
              service1.discountValue = triggerDiscount.discountValue;
            }
          }
        }
      });
    });

    if (this.annualService != null) {
      this.checkForServicesInCard();
    } else if(this.customerSubscription != null && this.customerSubscription != undefined && this.technicalInspection?.status?.code !== this.CODE_FINISHED) {
      this.checkForServicesInExistingCard();
    } else {
      this.paymentServiceTableData.forEach(service => {
        const index = this.paymentServiceConvertedData.findIndex(object => object.serviceName === service.serviceName);
       
        if (index === -1) {
          this.paymentServiceConvertedData.push(createPaymentServiceData(service.serviceName!, service.serviceType!, service.amountDds!, service.calculatedDiscount!));
        }
      });
    }

    if (this.additionalForm.customerDiscount.value !== '' || this.promoCodeDiscounts.length > 0) {
      this.calculateServicesDiscount();
    }

    // Scale the additional services that were chosen more than once. Done here, after every
    // discount has been worked out, because the discount applies to the whole line: three of a
    // discounted service costs three discounted prices, not two full ones and a discounted one.
    this.applyAdditionalServiceQuantities();

    // Goods are added after the services and after calculateServicesDiscount, because the
    // inspection's promo codes and loyalty discounts apply to services only - a good is sold at
    // its iCash price, less whatever the operator typed on the line.
    this.addItemsToPaymentServiceTable();

    this.isChangedRvsCategory = false;
    
    if ((this.customerSubscriptionName != null || this.customerSubscriptionName != undefined) && this.technicalInspection.status?.code == this.CODE_FINISHED) {
      this.canEdit = false;
    } else {
      this.canEdit = true;
    }

    
    if (!this.technicalInspection || !this.technicalInspection.techInspectionOrders?.length) return;
    
    if (this.technicalInspection.techInspectionOrders![0].order?.status.code === this.CODE_PAID) {
      let serviceTableTotalAmount = 0;
      this.paymentServiceConvertedData.forEach(service => {
        serviceTableTotalAmount += service?.amountDds;
      });

      if (this.technicalInspection?.techInspectionOrders![0]?.order?.promotion?.id) {
        this.currentAmountServices = serviceTableTotalAmount || 0;
        this.calculatePaidAmountServicesFromTable();
      }

      if (Number(serviceTableTotalAmount.toFixed(2)) < this.technicalInspection.techInspectionOrders![0].order?.totalAmount 
          && this.technicalInspection?.status?.code != this.CODE_FINISHED) {
        this.isCredit = true;
        this.isDebit = false;
      } else if (Number(serviceTableTotalAmount.toFixed(2)) > this.technicalInspection.techInspectionOrders![0].order?.totalAmount 
        && this.technicalInspection?.status?.code != this.CODE_FINISHED) {
        this.isDebit = true;
        this.isCredit = false;
      } else {
        this.isDebit = false;
        this.isCredit = false;
      }
    }
  }

  private checkForServicesInExistingCard() {
    this.paymentServiceConvertedData = [];
    this.paymentServiceTableData.forEach(service => {
      if (this.checkIfServiceIsInTheExistingCard(service.serviceMobileAppId!)) {
        this.paymentServiceConvertedData.push(createPaymentServiceData(service.serviceName!, service.serviceType!, 0, null));
      } else {
        this.paymentServiceConvertedData.push(createPaymentServiceData(service.serviceName!, service.serviceType!, Number.parseFloat(service.amountDds!.toFixed(2)), service.calculatedDiscount!));
      }
    });
  }

  checkIfServiceIsInTheExistingCard(mobileAppId:number) {
    const subscrServices: CustomerSubscriptionService[] = this.customerSubscription!.customerSubscriptionServices;
    for (let index = 0; index < subscrServices.length; index++) {
      const subscrServ = subscrServices[index];

      if (subscrServ.serviceAddInfoItem.childService!.mobileAppId === mobileAppId && subscrServ.isAvailable) {
        return true;
      }
    }
    return false;
  }

  private checkForServicesInCard() {
    this.paymentServiceConvertedData = [];
    this.paymentServiceTableData.forEach(service => {
      if (this.checkIfServiceIsInTheCard(service.serviceMobileAppId!)) {
        this.paymentServiceConvertedData.push(createPaymentServiceData(service.serviceName!, service.serviceType!, 0, null));
      } else {
        this.paymentServiceConvertedData.push(createPaymentServiceData(service.serviceName!, service.serviceType!, Number.parseFloat(service.amountDds!.toFixed(2)), service.calculatedDiscount!));
      }
    });
  }

  checkIfServiceIsInTheCard(mobileAppId:number) {
    return this.servicesMobileAppIdsForCard!.some(mobAppId => mobAppId === mobileAppId);
  }

  private calculateServicesDiscount() {
    // Clear stale flags from price fetch / prior passes so eligibility is not blocked incorrectly
    this.paymentServiceTableData.forEach(s => {
      s.isFreeCampaign = false;
    });
    // First, reset all discount calculations to prevent double application
    // Restore original amounts by adding back calculated discounts
    this.paymentServiceTableData.forEach(service => {
      if (service.calculatedDiscount && service.calculatedDiscount > 0) {
        if (service.amountDds !== undefined) {
          service.amountDds = (service.amountDds || 0) + (service.calculatedDiscount || 0);
          service.amountDds = Number.parseFloat(service.amountDds.toFixed(2));
        }
        service.calculatedDiscount = 0;
      }
    });

    // Apply discounts from appliedDiscounts to corresponding services
    if (this.appliedDiscounts && this.appliedDiscounts.length > 0) {
      this.appliedDiscounts.forEach(appliedDiscount => {
        // Skip mobile-card discounts as they don't have discount values
        if (appliedDiscount.type === 'mobile-card' || !appliedDiscount.discount) {
          return;
        }

        const discount = appliedDiscount.discount;
        let targetServiceName: string | null = null;

        // Determine which service this discount applies to
        if (appliedDiscount.type === 'promocode' && appliedDiscount.promoCodeService?.promotionServiceName) {
          targetServiceName = appliedDiscount.promoCodeService.promotionServiceName;
        } else if (appliedDiscount.type === 'card' || appliedDiscount.type === 'customer-discount') {
          // Loyalty cards and dropdown customer discount apply to the selected basic service
          targetServiceName = this.getSelectedBasicServiceName() ?? this.BASIC_SERVICE_NAME;
        }

        if (!targetServiceName || !discount.priceAttribute) {
          return;
        }

        // Find the target service in paymentServiceTableData
        const targetService = this.paymentServiceTableData.find(service => service.serviceName === targetServiceName);
        if (!targetService) {
          return;
        }

        // Apply discount based on type
        if (discount.priceAttribute.kind === this.DISCOUNT_PERCENTAGE) {
          const currentCalculateDiscount = targetService.calculatedDiscount || 0;
          const discountAmount = Math.round(((targetService.amountDds! * discount.priceAttribute.amount! / 100)) * 100) / 100;
          targetService.calculatedDiscount = (currentCalculateDiscount || 0) + discountAmount;
          targetService.amountDds! -= discountAmount;
          if (targetService.amountDds) {
            targetService.amountDds = Number.parseFloat(targetService.amountDds.toFixed(2));
          }
        } else if (discount.priceAttribute.kind === this.DISCOUNT_VALUE) {
          // For value discounts on cards, apply the full discount to the basic service
          if (appliedDiscount.type === 'card') {
            const currentCalculateDiscount = targetService.calculatedDiscount || 0;
            const discountValue = discount.priceAttributeAmountDds || discount.priceAttribute.amountDds || 0;
            targetService.calculatedDiscount = (currentCalculateDiscount || 0) + discountValue;
            if (targetService.amountDds != undefined) {
              targetService.amountDds -= discountValue;
              targetService.amountDds = Number.parseFloat(targetService.amountDds.toFixed(2));
            }
          } else {
            // For promocode value discounts, calculate proportionally if needed
            // For now, apply to the specific service
            const currentCalculateDiscount = targetService.calculatedDiscount || 0;
            const discountValue = discount.priceAttributeAmountDds || discount.priceAttribute.amountDds || 0;
            targetService.calculatedDiscount = (currentCalculateDiscount || 0) + discountValue;
            if (targetService.amountDds != undefined) {
              targetService.amountDds -= discountValue;
              targetService.amountDds = Number.parseFloat(targetService.amountDds.toFixed(2));
            }
          }
        }
      });

      // Handle value discount redistribution for cards (move discounts from additional services to basic service)
      const cardDiscount = this.appliedDiscounts.find(ad => ad.type === 'card' && ad.discount?.priceAttribute?.kind === this.DISCOUNT_VALUE);
      const basicServiceNameForRedist = this.getSelectedBasicServiceName() ?? this.BASIC_SERVICE_NAME;
      if (cardDiscount && cardDiscount.discount) {
        this.paymentServiceTableData.forEach(s => {
          if (s.serviceName !== basicServiceNameForRedist && s.calculatedDiscount && s.calculatedDiscount > 0) {
            const basicServiceIndex = this.paymentServiceTableData.findIndex(entity => entity.serviceName === basicServiceNameForRedist);
            
            if (basicServiceIndex !== -1 && this.paymentServiceTableData[basicServiceIndex]) {
              const basicServiceEntity = this.paymentServiceTableData[basicServiceIndex];
              basicServiceEntity.calculatedDiscount = (basicServiceEntity.calculatedDiscount || 0) + (s.calculatedDiscount || 0);
              if (basicServiceEntity.amountDds != undefined) {
                basicServiceEntity.amountDds -= (s.calculatedDiscount || 0);
              }
              if (s.amountDds != undefined) {
                s.amountDds += (s.calculatedDiscount || 0);
              }
              s.calculatedDiscount = 0;
            }
          }
        });

        // Adjust basic service discount to match the exact discount value
        const basicServiceIndex = this.paymentServiceTableData.findIndex(entity => entity.serviceName === basicServiceNameForRedist);
        if (basicServiceIndex !== -1) {
          const basicServiceEntity = this.paymentServiceTableData[basicServiceIndex];
          const expectedDiscount = cardDiscount.discount.priceAttributeAmountDds || cardDiscount.discount.priceAttribute?.amountDds || 0;
          const discountDiff = expectedDiscount - (basicServiceEntity.calculatedDiscount || 0);
          
          if (discountDiff !== 0) {
            basicServiceEntity.amountDds = (basicServiceEntity.amountDds || 0) + discountDiff;
            basicServiceEntity.calculatedDiscount = expectedDiscount;
          }

          // Restore original amount if needed
          const originalBasicIndex = this.basicServicesCopy.findIndex(entity => entity.serviceName === basicServiceEntity.serviceName);
          if (originalBasicIndex !== -1) {
            const originalBasicEntity = this.basicServicesCopy[originalBasicIndex];
            
            if (originalBasicEntity.amountDds !== basicServiceEntity.amountDds) {
              basicServiceEntity.amountDds = (originalBasicEntity.amountDds || 0) - (basicServiceEntity.calculatedDiscount || 0);
            }
          }
        }
      }
    }

    // Build paymentServiceConvertedData after applying all discounts
    if (this.appliedDiscounts && this.appliedDiscounts.length > 0) {
      this.paymentServiceConvertedData = [];
      this.paymentServiceTableData.forEach(service => {
        const index = this.paymentServiceConvertedData.findIndex(object => object.serviceName === service.serviceName);
        if (index === -1) {
          service.isFreeCampaign = false;

          //APPLY 100% DISCOUNT TO ADDITIONAL SERVICE IF ELIGIBLE FOR PROMOTIONAL CAMPAIGN
          // if the requirements are met and the concrete service is eligible for 100% discount we need to apply it here
          const appliedVoucher = this.getAppliedVoucherForService(service.serviceName);
          if (this.promotionalCampaign.isEligibleForFreeCampaign(service, this.paymentServiceTableData, this.activeDiscountCampaigns)) {
            service.calculatedDiscount = service.amountDds;
            service.amountDds = 0;
            service.isFreeCampaign = true;
            // Free beats a voucher. Its id must not go to the backend alongside the free amounts.
            if (appliedVoucher) {
              this.clearVoucherFields(service);
            }
          } else if (appliedVoucher) {
            // A voucher on this service replaces its campaign discount. The amounts are already the
            // voucher's from the loop above; the price fetch may have left the campaign's kind and
            // value on the line, so the voucher's are put back.
            service.discountId = appliedVoucher.promoCodeService?.id;
            service.discountTypeName = 'VOUCHER_CODE';
            service.discountKindName = appliedVoucher.discount?.priceAttribute?.kind as 'P' | 'V';
            service.discountValue = appliedVoucher.promoCodeService?.totalDiscountForServices;
          } else {
            const triggerDiscount = this.getCampaignTriggerDiscount(service);
            if (triggerDiscount && service.serviceMobileAppId != null) {
              const fullPrice = this.originalServicePrices.get(service.serviceMobileAppId);
              if (fullPrice !== undefined) {
                if (triggerDiscount.discountKind === 'P') {
                  const discountAmount = Math.round(fullPrice * (triggerDiscount.discountValue / 100) * 100) / 100;
                  service.calculatedDiscount = discountAmount;
                  service.amountDds = Math.round((fullPrice - discountAmount) * 100) / 100;
                } else {
                  service.calculatedDiscount = triggerDiscount.discountValue;
                  service.amountDds = Math.round((fullPrice - triggerDiscount.discountValue) * 100) / 100;
                }
                service.discountKindName = triggerDiscount.discountKind as 'P' | 'V';
                service.discountValue = triggerDiscount.discountValue;
              }
            }
          }
          this.paymentServiceConvertedData.push(createPaymentServiceData(service.serviceName!, service.serviceType!, Number.parseFloat(service.amountDds!.toFixed(2)), service.calculatedDiscount!));
        }
      });
      this.calculatePaidAmountServices();
      return;
    }

    if (this.technicalInspection && this.technicalInspection.promotionId) {
      this.paymentServiceConvertedData = [];
      this.orderElementsByPromotionCode.forEach(ol => {
        // Goods lines carry no service; they are added to the table from the bill instead.
        if (ol.service == null) { return; }
        // As above: a stored element's money is already for the whole line, so its quantity is
        // only displayed.
        this.paymentServiceConvertedData.push(createPaymentServiceData(ol.service.name!, ol.service.serviceType!.description, ol.totalAmount!, ol.discountAmount!, ol.quantity ?? 1));
      });
      this.addItemsToPaymentServiceTable();
    }
    this.calculatePaidAmountServices();
  }

  private constructTechInspection(isDeferPayment?: boolean) {
    this.mainTechInspForm.get('incomingWay')?.setValue(this.INCOMING_WAY_TP);
    this.mainTechInspForm.get('inspectionDate')?.patchValue(this.prepareDateForDb(this.mainTechInspForm.get('inspectionDate')?.value));
    this.mainTechInspForm.get('fromErp')?.patchValue(true);

    let mainData = this.mainTechInspForm.value;
    // Use getRawValue() so disabled controls (e.g. customerDiscount when hasPromocode) are included
    let additionalData = this.additionalTechInspForm.getRawValue?.() ?? this.additionalTechInspForm.value;

    if (this.technicalInspection !== null) {
      this.technicalInspectionTimes
    }

    if (this.selectedAdditionalServices.length > 0) {
      this.selectedAdditionalServices?.forEach(service => {
        if (!this.additionalTechInspForm.get('customerDiscount')?.value && this.technicalInspection?.techInspectionOrders != undefined && this.technicalInspection?.techInspectionOrders![0]?.order?.promotion?.id != null) { 
          if (this.constructTechPointServiceFromOrderElement(service) != null) {
            this.selectedTechPointServices.push(this.constructTechPointServiceFromOrderElement(service));
          } else {
            this.selectedTechPointServices.push(service);
          }
        } else {
          this.selectedTechPointServices.push(service);
        }
      });
    }

    if (this.annualService != null && this.submittedAdditionalForm) {
      this.selectedTechPointServices.push(this.annualService);
    }

    if (this.technicalInspection?.techInspectionOrders != undefined && !this.additionalTechInspForm.get('customerDiscount')?.value) {
      if (this.technicalInspection?.techInspectionOrders![0]?.order?.promotion?.id != null) {
        if (this.constructTechPointServiceFromOrderElement(mainData.basicTechPointService) != null) {
          this.selectedTechPointServices.push(this.constructTechPointServiceFromOrderElement(mainData.basicTechPointService));
        } else {
          this.selectedTechPointServices.push(mainData.basicTechPointService);
        }
      } else {
        this.selectedTechPointServices.push(mainData.basicTechPointService);
      }
    } else {
      this.selectedTechPointServices.push(mainData.basicTechPointService);
    }
    
    let isAgree = additionalData.isAgreeWithNotification;
    if (isAgree === null || isAgree === undefined) {
      isAgree = true;
    }

    this.syncTechPointServicesDiscountsFromPaymentTable();
    this.applyPromoCampaignFieldsToSelectedServices();

    const services = this.constructServices(this.selectedTechPointServices);

    // Resolve customerDiscount: form control value (includes when control is disabled) or applied customer-discount
    const customerDiscountFromControl = this.additionalTechInspForm.get('customerDiscount')?.value;
    const customerDiscountFromApplied = this.appliedDiscounts.find(ad => ad.type === 'customer-discount')?.discount ?? null;
    const resolvedCustomerDiscount = customerDiscountFromControl ?? customerDiscountFromApplied ?? additionalData.customerDiscount ?? null;

    if (!resolvedCustomerDiscount) {
      this.customerCard = null;
    }

    // Check if voucher or promo code is applied
    const isAppliedVoucherOrPromocode = this.appliedDiscounts.some(ad => ad.type === 'promocode');

    const obj = {
      id:                      mainData.id                            || null,
      status:                  mainData.status                        || null,
      regNumber:               mainData.regNumber                     || null,
      customerRvsId:           mainData.customerRvsId                 || null,
      registrationCertNumber:  mainData.registrationCertNumber        || null,
      rvsCategory:             mainData.rvsCategory                   || null,
      versionData:             mainData.versionData                   || null,
      inspectionDate:          mainData.inspectionDate                || null,
      technicalPointId:        this.userTechnicalPointId              || null,
      incomingWay:             mainData.incomingWay                   || null,
      fromErp:                 mainData.fromErp                       || null,
      email:                   additionalData.email                   || null,
      name:                    additionalData.name                    || null,
      contractRvsId:           additionalData.contractRvsId           || null,
      phoneNumber:             this.formatPhoneNumber(additionalData.phoneNumber),
      rejectReason:            additionalData.rejectReason            || null,
      customerDiscount:        resolvedCustomerDiscount               || null,
      createdOrder:            additionalData.createdOrder            || null,
      issueInvoice:            additionalData.issueInvoice            || null,
      isAgreeWithNotification: isAgree,
      isEmployee:              additionalData.isEmployee              || false,
      customerCard:            this.customerCard                      || null,
      promotionId:             additionalData.promotionId             || null,
      techPointServices:       services,
      // Always sent, even when empty: the backend reads an absent list as "this caller does not
      // manage goods" and carries the stored ones forward, so omitting it would make removing
      // the last good impossible.
      techInspectionItems:     this.constructItems(),
      techInspectionOrders:    this.constructTechInspOrders(this.technicalInspection!),
      techInspectionTimes:     this.constructTimes(this.technicalInspection!),
      invoiceSubjectVersion:   this.constructSubject(additionalData),
      techPointWorkingHourIds: this.constructTechPointWorkingHours(),
      isDeferPayment:          isDeferPayment,
      isBankPayment:           this.isBankPayment,
      skipSubscribtion:        false,
      wantsMarketingMessages:  this.wantMarketingNotification,
      isAppliedVoucherOrPromocode: isAppliedVoucherOrPromocode,
      mobileCard:              additionalData.mobileCard,
      // ?? rather than the || used above: || would turn a legitimate "0000000" into null.
      fiscalNumber:            additionalData.fiscalNumber ?? null
    }

    return obj;
  }

  private formatPhoneNumber(value: string): string { 
    if (!value) return value;
  
    // Remove spaces and unwanted characters (keep digits and +)
    let cleaned = value.replace(/[^0-9+]/g, '');
  
    // If already starts with +359 → do nothing
    if (cleaned.startsWith('+359')) {
      return cleaned;
    }
  
    // If starts with 08 or 09 → replace leading 0 with +359
    if (/^0[89]/.test(cleaned)) {
      return '+359' + cleaned.substring(1);
    }
  
    return cleaned;
  }

  private constructTechPointServiceFromOrderElement(serviceData: any) {
    let isConstructed: boolean = false;
    this.technicalInspection?.techInspectionOrders![0]?.order?.orderElements?.forEach(element => {
      // Goods lines carry no service and can never match one, so they are skipped.
      if(element.service == null) { return; }
      if(element.service.mobileAppId == serviceData.serviceMobileAppId) {
        if(element.discountAmount != 0) {
          this.priceAttribute = {
            id: undefined, 
            amount: element.amount,
            amountDds: element.totalAmount,
            ddsValue: 20
          }
          isConstructed = true;
        }
      } 
    })

    if(isConstructed) {
      return {
        id:                 serviceData.id                 || null,
        status:             serviceData.status             || null,
        isValid:            serviceData.isValid            || null,
        rvsService:         serviceData.rvsService         || null,
        duration:           serviceData.duration           || null,
        validFrom:          serviceData.validFrom          || null,
        validTo:            serviceData.validTo            || null,
        serviceType:        serviceData.serviceType        || null,
        serviceId:          serviceData.serviceId          || null,
        serviceMobileAppId: serviceData.serviceMobileAppId || null,
        priceAttribute:     this.priceAttribute            || null,
        calculatedDiscount: serviceData.calculatedDiscount || null,
        discountId:         serviceData.discountId         || null,
        discountTypeName:   serviceData.discountTypeName   || null,
        discountKindName:   serviceData.discountKindName   || null,
        discountValue:      serviceData.discountValue      || null
      }
    } else {
      return null;    
    }
  }

  private constructTimes(techInspection: TechnicalInspection) {
    if (techInspection === null || techInspection.techInspectionTimes?.length === 0) {
      return [];
    }
    return techInspection.techInspectionTimes;
  }

  private constructTechInspOrders(techInspection: TechnicalInspection) {
    if (techInspection === null || techInspection.techInspectionOrders?.length === 0) {
      return [];
    }
    return techInspection.techInspectionOrders;
  }

  private constructTechPointWorkingHours() {
    if (this.techPointWorkingHourIds === null || this.techPointWorkingHourIds?.length === 0) {
      return [];
    }

    return this.techPointWorkingHourIds;
  }

  /**
   * Copy discount fields from the payment table (source of truth after calculateServicesDiscount / free campaign)
   * onto the same services used for the save payload (form value can diverge from table row references).
   */
  private syncTechPointServicesDiscountsFromPaymentTable(): void {
    if (!this.paymentServiceTableData?.length) {
      return;
    }
    const sync = (svc: any) => {
      if (svc?.serviceMobileAppId == null) {
        return;
      }
      const row = this.paymentServiceTableData.find(t => t.serviceMobileAppId === svc.serviceMobileAppId);
      if (!row) {
        return;
      }
      svc.calculatedDiscount = row.calculatedDiscount;
      svc.amountDds = row.amountDds;
      svc.isFreeCampaign = row.isFreeCampaign;
      if (row.discountId != null) {
        svc.discountId = row.discountId;
      }
      if (row.discountTypeName != null) {
        svc.discountTypeName = row.discountTypeName;
      }
      if (row.discountKindName != null) {
        svc.discountKindName = row.discountKindName;
      }
      if (row.discountValue != null) {
        svc.discountValue = row.discountValue;
      }
    };
    this.selectedTechPointServices.forEach(sync);
    const basic = this.mainTechInspForm.get('basicTechPointService')?.value;
    if (basic) {
      sync(basic);
    }
  }

  private applyPromoCampaignFieldsToSelectedServices(): void {
    if (!this.activeDiscountCampaigns?.length) {
      return;
    }
    const apply = (svc: any) => {
      if (svc?.serviceMobileAppId == null || !svc?.serviceName) {
        return;
      }
      const serviceName = svc.serviceName!.valueOf() as string;
      const fullPrice = this.originalServicePrices.get(svc.serviceMobileAppId);
      if (this.promotionalCampaign.isEligibleForFreeCampaign(svc, this.paymentServiceTableData, this.activeDiscountCampaigns)) {
        svc.isFreeCampaign = true;
        if (fullPrice !== undefined) {
          svc.calculatedDiscount = fullPrice;
        }
        if (this.getAppliedVoucherForService(serviceName)) {
          this.clearVoucherFields(svc);
        }
        return;
      }
      const triggerDiscount = this.getCampaignTriggerDiscount(svc);
      if (triggerDiscount && fullPrice !== undefined) {
        if (triggerDiscount.discountKind === 'P') {
          svc.calculatedDiscount = Math.round(fullPrice * (triggerDiscount.discountValue / 100) * 100) / 100;
        } else {
          svc.calculatedDiscount = triggerDiscount.discountValue;
        }
        svc.discountKindName = triggerDiscount.discountKind as 'P' | 'V';
        svc.discountValue = triggerDiscount.discountValue;
      }
    };
    this.selectedTechPointServices.forEach(apply);
    const basic = this.mainTechInspForm.get('basicTechPointService')?.value;
    if (basic) {
      apply(basic);
    }
  }

  /** The voucher applied to the given service, if any. Promo codes are not included. */
  private getAppliedVoucherForService(serviceName: string | String | null | undefined) {
    if (!serviceName) {
      return undefined;
    }
    const name = serviceName.valueOf();
    return this.appliedDiscounts.find(ad => ad.type === 'promocode' && ad.isVoucher && ad.promoCodeService?.promotionServiceName === name);
  }

  /**
   * The promo-campaign trigger discount for a service, unless a voucher is applied to it: a voucher
   * replaces the campaign discount on its service (weaker vouchers are already refused by the backend).
   */
  private getCampaignTriggerDiscount(service: any): DiscountDTO | null {
    if (!service?.serviceName || this.getAppliedVoucherForService(service.serviceName)) {
      return null;
    }
    return this.promotionalCampaign.getTriggerServiceDiscount(service.serviceName.valueOf() as string, this.activeDiscountCampaigns);
  }

  private clearVoucherFields(service: any): void {
    if (service?.discountTypeName !== 'VOUCHER_CODE') {
      return;
    }
    service.discountId = undefined;
    service.discountTypeName = undefined;
    service.discountKindName = undefined;
    service.discountValue = undefined;
  }

  private constructServices(data: any[]) {
    return data.filter(serviceData => serviceData.id !== null || serviceData.isValid).map(serviceData => {
      return {
        id:                 serviceData.id                 || null,
        status:             serviceData.status             || null,
        isValid:            serviceData.isValid            || null,
        rvsService:         serviceData.rvsService         || null,
        duration:           serviceData.duration           || null,
        validFrom:          serviceData.validFrom          || null,
        validTo:            serviceData.validTo            || null,
        serviceType:        serviceData.serviceType        || null,
        serviceId:          serviceData.serviceId          || null,
        serviceMobileAppId: serviceData.serviceMobileAppId || null,
        priceAttribute:     serviceData.priceAttribute     || null,
        calculatedDiscount: serviceData.calculatedDiscount || null,
        discountId:         serviceData.discountId         || null,
        discountTypeName:   serviceData.discountTypeName   || null,
        discountKindName:   serviceData.discountKindName   || null,
        discountValue:      serviceData.discountValue      || null,
        isFreeCampaign:     serviceData.isFreeCampaign     || false,
        // Only the additional services can be more than one; everything else is sold singly,
        // so anything without a recorded quantity goes as 1 rather than null.
        quantity:           this.resolveServiceQuantity(serviceData)
      }
    });
  }

  /**
   * How many of a service to send. The map is keyed by technical point service id and only the
   * additional services are ever in it, so the basic service and the annual subscription always
   * resolve to one.
   */
  private resolveServiceQuantity(serviceData: any): number {
    const quantity = this.additionalServiceQuantities[serviceData?.id];
    return quantity == null || quantity < 1 ? 1 : Math.floor(quantity);
  }

  private constructSubject(data: any) {
    if (data.subjectId == null) return undefined;

    return {
      id:          data.subjectId,
      fullName:    data.subjectName,
      identNum:    data.subjectIdentNum,
      versionData: data.subjectVersionData
    }
  }

  private setSubjectData(subject: SubjectVersion | ConsiceSubjectVersion | undefined, isFromModal: boolean, checkForContract: boolean) {
    if (subject == null) return;
    this.isHaveSubject = true;

    // this.additionalTechInspForm.get('customerDiscount')?.patchValue(null);

    this.additionalTechInspForm.get('subjectId')?.patchValue(subject?.id);
    this.additionalTechInspForm.get('subjectIdentNum')?.patchValue(subject?.identNum);
    this.additionalTechInspForm.get('subjectManager')?.patchValue(subject?.manager);
    this.additionalTechInspForm.get('subjectInvoiceDdsReg')?.patchValue(subject?.invoiceDdsRegistration);
    this.additionalTechInspForm.get('subjectName')?.patchValue(subject?.fullName);
    this.additionalTechInspForm.get('subjectCity')?.patchValue(subject?.city);
    this.additionalTechInspForm.get('subjectAddress')?.patchValue(subject?.address);
    this.additionalTechInspForm.get('subjectVersionData')?.patchValue(subject?.versionData);
    
    if (checkForContract) {
      this.subjectHasContract(subject?.mobileAppId, isFromModal);
    }
  }

  private setSubjectDataWithoutContract(subject: SubjectVersion | ConsiceSubjectVersion | undefined, isFromModal: boolean) {
    if (subject == null) return;

    this.isHaveSubject = true;
    this.additionalTechInspForm.get('subjectId')?.patchValue(subject?.id);
    this.additionalTechInspForm.get('subjectIdentNum')?.patchValue(subject?.identNum);
    this.additionalTechInspForm.get('subjectManager')?.patchValue(subject?.manager);
    this.additionalTechInspForm.get('subjectInvoiceDdsReg')?.patchValue(subject?.invoiceDdsRegistration);
    this.additionalTechInspForm.get('subjectName')?.patchValue(subject?.fullName);
    this.additionalTechInspForm.get('subjectCity')?.patchValue(subject?.city);
    this.additionalTechInspForm.get('subjectAddress')?.patchValue(subject?.address);
    this.additionalTechInspForm.get('subjectVersionData')?.patchValue(subject?.versionData);
  }

  subjectHasContract(mobileAppId: number | undefined, isFromModal: boolean) {
    this.cleanPaymentServiceTable();

    this.contractService.findContractDiscountBySubjectId(mobileAppId, this.userTechnicalPointMobileIds[0], 
                                                         this.mainTechInspForm.get('basicTechPointService')?.value.serviceMobileAppId,
                                                         this.mainTechInspForm.get('rvsCategory')?.value.id).subscribe(customerDiscounts => {
      this.additionalTechInspForm.get('customerCard')?.enable();
      if (customerDiscounts?.length == 0 && isFromModal) {
        this.additionalTechInspForm.get('customerDiscount')?.patchValue(null);
        this.nomenclatureService.getCustomerDiscountsBySubjectType(1).subscribe(customerDiscounts => {
          this.customerDiscounts = this.filterDiscountsByType(customerDiscounts);
          const canApply = this.applyLoyaltyCardDiscountForAllowedServices();
          if (!canApply) {
            return;
          }
          this.applyCustomerCardIfHad();
        });
        this.additionalTechInspForm.get('customerDiscount')?.enable();
      } else if (customerDiscounts?.length == 1) {
        this.nomenclatureService.getCustomerDiscountsBySubjectType(1).subscribe(customerDiscounts => {
          this.customerDiscounts = this.filterDiscountsByType(customerDiscounts);
          const canApply = this.applyLoyaltyCardDiscountForAllowedServices();
          if (!canApply) {
            return;
          }
          this.applyCustomerCardIfHad();
        });
        if (customerDiscounts.length > 0) {
          this.additionalTechInspForm.get('customerDiscount')?.patchValue(customerDiscounts[0]);
          this.additionalTechInspForm.get('customerDiscount')?.disable();
          customerDiscounts.splice(1);
        }
        const canApply = this.applyLoyaltyCardDiscountForAllowedServices();
        if (!canApply) {
          return;
        }
        this.applyCustomerCardIfHad();
      } else if (customerDiscounts?.length > 1) {
        this.customerDiscounts = this.filterDiscountsByType(customerDiscounts);
        this.additionalTechInspForm.get('customerDiscount')?.enable();
        const canApply = this.applyLoyaltyCardDiscountForAllowedServices();
        if (!canApply) {
          return;
        }
        this.applyCustomerCardIfHad();
      }
    });
  }

  private applyCustomerCardIfHad() {
    const customerCard = this.additionalTechInspForm.get('customerCard')?.value;
    if (customerCard) {
      this.cardService.getLoyalComboByCard(customerCard).subscribe(data => {
        if (!data) return;
        const loyalCombo = data as LoyalCombo;
        this.customerCard = loyalCombo.customerCard;
        if (loyalCombo.customerDiscount) {
          this.lastSelectedDiscountByCard = loyalCombo.customerDiscount.discountName;
          this.additionalTechInspForm.get('customerDiscount')?.setValue(loyalCombo.customerDiscount);
          this.customerDiscounts.push(loyalCombo.customerDiscount);
          this.customerDiscounts = Array.from(new Set(this.customerDiscounts));
        }
      });
    }
  }

  private availableSlotFormValidation() {
    return this.availableSlotForm.invalid || this.isValidSlotFormRegNum || this.isInvalidRegNumberForForeigner 
      || this.isInvalidRegNumberForDiplomatic || this.isInvalidRegNumberForElectric;
  }

  private mainTechFormValidation() {
    return this.mainTechInspForm.invalid || this.isValidMainFormRegNum || this.isInvalidRegNumberForForeigner
      || this.isInvalidRegNumberForDiplomatic || this.isInvalidRegNumberForElectric;
  }

  private mainTechAndAdditionalFormsValidation() {
    return this.mainTechInspForm.invalid || this.additionalTechInspForm.invalid || this.isValidMainFormRegNum;
  }

  /**
   * The toast shown when a save is refused for a field that is not filled in.
   *
   * Raised where the save actually turns back, not from the validation methods themselves: a
   * Whilli inspection is deliberately let through an invalid form, so a toast tied to the
   * validation result would announce a refusal that never happened.
   *
   * The red marks under the fields appear at the same moment, but the form is long enough that
   * the offending field is often scrolled out of view, which is why pressing the button looked
   * like it did nothing at all.
   */
  private notifyRequiredFieldsMissing() {
    this.uiEvent.displayUIError('toast.requiredFieldsMissing');
  }

  /**
   * Whether an additional service is currently chosen.
   *
   * Read from selectedAdditionalServices alone, which is the live state: the inspection is
   * loaded by pushing into it every service found on the stored tech-insp services OR on the
   * order (a started version may carry them only on the order created at reservation time), and
   * every tick and untick since then has gone through it as well.
   *
   * Consulting the stored lists here as well would get both directions wrong - a service ticked
   * a moment ago is in neither of them and would read back as unchecked, and one just unticked
   * is still in both and would stay checked.
   */
  checkId(id:number) {
    return this.selectedAdditionalServices.some(s => s.id === id);
  }

  /**
   * True when the given tech-point service appears among the loaded inspection's order elements.
   * Matches by service mobile-app id (stable across versions), falling back to service name.
   */
  private isServiceInOrderElements(service: TechPointService): boolean {
    const orderElements: OrderElement[] = [];
    this.technicalInspection?.techInspectionOrders?.forEach(o => {
      o.order?.orderElements?.forEach(oe => orderElements.push(oe));
    });
    if (orderElements.length === 0) {
      return false;
    }
    return orderElements.some(oe =>
      (service.serviceMobileAppId != null && oe.service?.mobileAppId === service.serviceMobileAppId) ||
      (service.serviceName != null && oe.service?.name === service.serviceName)
    );
  }

  compareById(optionOne: any, optionTwo: any) {
    return optionOne && optionTwo && optionOne.id === optionTwo.id;
  }

  compareVoucherCodeById(optionOne: VoucherCodeDTO | null, optionTwo: VoucherCodeDTO | null): boolean {
    return !!(optionOne && optionTwo && optionOne.id === optionTwo.id);
  }

  getServicesMobileAppIdsForCard(serviceId: number) {
    if (serviceId != null) {
      this.techInspService.getServicesMobileAppIdsForCard(serviceId).subscribe(servicesMobileAppIdsForCard => {
        this.servicesMobileAppIdsForCard = servicesMobileAppIdsForCard;
        this.checkForServicesInCard();
      });
    }
  }

  getGroupedTechnicalPointWorkingHours(availableSlotData: any, arePastLoaded: boolean) {
    const dateStr = prepareDateForDb(availableSlotData.forDate);

    this.loadingQuickReservation = true;
    this.techInspService.getGroupedTechnicalPointWorkingHours(dateStr, availableSlotData.line.id, availableSlotData.rvsCategory.id, 
        availableSlotData.basicTechPointService.duration, this.userTechnicalPointId, arePastLoaded).subscribe(groupedTechPointWorkingHours => {
      this.groupedTechPointWorkingHours = groupedTechPointWorkingHours;
      this.availableSlotForm.get('groupedTechPointWorkingHours')?.patchValue(this.groupedTechPointWorkingHours[0]);
      this.loadingQuickReservation = false;
    });
  }

  loadOldReservations(arePastLoaded: boolean, isPastClicked: boolean) {
    this.arePastLoaded = !arePastLoaded;
    this.isPastClicked = !isPastClicked;
    this.getGroupedTechnicalPointWorkingHours(this.availableSlotForm.value, this.arePastLoaded);
  }

  onAvailableSlotDataChange() {
    this.getGroupedTechnicalPointWorkingHours(this.availableSlotForm.value, this.arePastLoaded);
  }

  onAvailableSlotRvsCategoryChange() {
    let data = this.availableSlotForm.value;
    this.techInspService.getBasicRvsCategoryServices(data.rvsCategory, this.userTechnicalPointId).subscribe(basicServices => {
      this.basicServicesForFirstAvailableSlot = basicServices;
      let fetchedBasicTechPointService = this.basicServicesForFirstAvailableSlot.find(service => service.serviceName === this.BASIC_SERVICE_NAME) || this.basicServicesForFirstAvailableSlot[0];
      this.availableSlotForm.get('forDate')?.patchValue(data.forDate);
      this.availableSlotForm.get('line')?.patchValue(data.line);
      this.availableSlotForm.get('rvsCategory')?.patchValue(data.rvsCategory);
      this.availableSlotForm.get('basicTechPointService')?.patchValue(fetchedBasicTechPointService);
      this.getGroupedTechnicalPointWorkingHours(this.availableSlotForm.value, this.arePastLoaded);
    });
  }

  onSubmitAvailableSlotForm() {
    this.submittedAvailableSlotForm = true;
    if (this.availableSlotFormValidation()) {
      return;
    }

    this.loading = true;
    let availableSlotData = this.availableSlotForm.value;
    
    this.techInspService.saveAvalableSlot(this.constructAvailableSlotData(availableSlotData), this.arePastLoaded).subscribe({
      next: (result) => {
        this.submittedAvailableSlotForm = false;
        if (result === 0) {
           this.showBusySlotWarningModal(availableSlotData);
        } else {
          this.uiEvent.displayUISuccess();

          this.loadFutureTechInspections();
          this.loadDefaultAvailableSlotFilters();
        }
      }  
    });
  }

  async showBusySlotWarningModal(availableSlotData: any) { 
    let dialogRef = this.dialog.open(WarningMessageboxComponent, {
      ...modalMinWidth,
      data: {
        message: "messagebox.busySlot"
      }
    });

    await dialogRef.afterClosed().toPromise().then((isConfirm) => {
      if (isConfirm) {
        this.loadFutureTechInspections();
        this.loadAvailableSlotWithCurrentFilters(availableSlotData);
      }
    });
  }

  async showNoAvailableSlotsModal() { 
    let dialogRef = this.dialog.open(WarningMessageboxComponent, {
      ...modalMinWidth,
      data: {
        message: "messagebox.noAvailableSlots"
      }
    });

    await dialogRef.afterClosed().toPromise().then((isConfirm) => {
      if (isConfirm) {
        this.prepareNewTechnicialInpection();
      }
    });
  }

  private constructAvailableSlotData(availableSlotData: any) {
    const dateStr = prepareDateForDb(availableSlotData.forDate);

    return {
      forDate:                      dateStr,
      line:                         availableSlotData.line                         || null,
      regNumber:                    availableSlotData.regNumber                    || null,
      phoneNumber:                  availableSlotData.phoneNumber                  || null,
      rvsCategory:                  availableSlotData.rvsCategory                  || null,
      techPointService:             availableSlotData.basicTechPointService        || null,
      groupedTechPointWorkingHours: availableSlotData.groupedTechPointWorkingHours || null
    }
  }

  private loadDefaultAvailableSlotFilters() {
    this.defRvsCategory = this.rvsCategories.find(category => category.code === this.RVS_CATEGORY_CODE) || null;
    this.availableSlotForm.get('forDate')?.patchValue(prepareDateForForm(new Date()));
    this.availableSlotForm.get('regNumber')?.patchValue("");
    this.availableSlotForm.get('rvsCategory')?.patchValue(this.defRvsCategory);
    this.availableSlotForm.get('line')?.patchValue(this.technicalPointLines[0]);
    
    this.techInspService.getBasicRvsCategoryServices(this.defRvsCategory, this.userTechnicalPointId).subscribe(basicServices => {
      this.basicServicesForFirstAvailableSlot = basicServices;
      let fetchedBasicTechPointService = this.basicServicesForFirstAvailableSlot.find(service => service.serviceName === this.BASIC_SERVICE_NAME) || null;
      this.availableSlotForm.get('basicTechPointService')?.patchValue(fetchedBasicTechPointService);
      this.getGroupedTechnicalPointWorkingHours(this.availableSlotForm.value, this.arePastLoaded);
    });
  }

  private loadAvailableSlotWithCurrentFilters(availableSlotData: any) {
    this.availableSlotForm.get('forDate')?.patchValue(availableSlotData.forDate);
    this.availableSlotForm.get('regNumber')?.patchValue(availableSlotData.regNumber);
    this.availableSlotForm.get('rvsCategory')?.patchValue(availableSlotData.rvsCategory);
    this.availableSlotForm.get('line')?.patchValue(availableSlotData.line);
    this.availableSlotForm.get('basicTechPointService')?.patchValue(availableSlotData.basicTechPointService);
    this.getGroupedTechnicalPointWorkingHours(this.availableSlotForm.value, this.arePastLoaded);
  }

  async startTechInspection(futTechInsp: any) {
    this.annualService = null // set to null because when click the button from quick reservation to start this.annualService is set from init

    // Navigate to the reservation's id so its identity lives in the URL, not only in component memory,
    // then let loadTechnicalInspection$ (driven by route.queryParams) load and render it by id.
    // This makes the whole start flow refresh-safe: a page refresh reloads this exact inspection by id
    // (findTechnicalInspectionById has no status filter) instead of showing the "new inspection" screen
    // with a stale future list that no longer contains this reservation (the backend excludes STARTED
    // from getFutureTechnicalInspection). Previously the reservation was only held in component memory, so
    // after a refresh the Start button reappeared and re-submitting created a duplicate STARTED inspection
    // pointing at the same order.
    this.router.navigate(['/add-edit-technical-inspection'], { queryParams: { id: futTechInsp.id } });
  }

  validateMainFormRegNumber(regNumber: string) {
    this.mainFormValidation = true;
    this.slotFormValidation = false;

    regNumber = this.validateRegNumber(regNumber, true);
    this.mainTechInspForm.get('regNumber')?.patchValue(regNumber);
  }

  validateAvailableSlotFormRegNumber(regNumber: string) {
    this.slotFormValidation = true;
    this.mainFormValidation = false;

    regNumber = this.validateRegNumber(regNumber, false);
    this.availableSlotForm.get('regNumber')?.patchValue(regNumber);
  }

  validateRegNumber(regNumber: string, isMainForm: boolean) {
    regNumber = regNumber.toUpperCase();
    const endWithNumbers = /\d+$/;

    this.isInvalidRegNumberForForeigner = false;
    this.isInvalidRegNumberForDiplomatic = false;
    this.isInvalidRegNumberForElectric = false
    
    let firstDiplomaticCheck = false;
    let secondDiplomaticCheck = false;
    let thirdDiplomaticSymbolsEndCheck = true;

    if (regNumber.length < 5) {
      if (isMainForm) {
        regNumber.match(config.regexRegNumber) || regNumber === "" ? this.isValidMainFormRegNum = false :  this.isValidMainFormRegNum = true;
        return regNumber;
      } 
      regNumber.match(config.regexRegNumber) || regNumber === "" ? this.isValidSlotFormRegNum = false :  this.isValidSlotFormRegNum = true;

      return regNumber;
    } else {
      firstDiplomaticCheck = regNumber.startsWith('C') || regNumber.startsWith('CC') || regNumber.startsWith('С') || regNumber.startsWith('СС');
      secondDiplomaticCheck = regNumber.charAt(1).startsWith('C') || regNumber.charAt(1).startsWith('С');
      if (regNumber.charAt(regNumber.length - 1).match(endWithNumbers)) {
        thirdDiplomaticSymbolsEndCheck = false;
      }
    }

    // foreign
    if (regNumber.startsWith('XH') || regNumber.startsWith('ХН')) {
      regNumber.match(config.regexForeigner) || regNumber === "" ? this.isInvalidRegNumberForForeigner = false :  this.isInvalidRegNumberForForeigner = true;
      if (regNumber.length > 6) {
        this.isInvalidRegNumberForForeigner = true;
      }
    
    // diplomatic
    } else if (firstDiplomaticCheck && secondDiplomaticCheck && !thirdDiplomaticSymbolsEndCheck) {
      this.isValidMainFormRegNum = false;
      regNumber.match(config.regexDiplomatic) || regNumber === "" ? this.isInvalidRegNumberForDiplomatic = false :  this.isInvalidRegNumberForDiplomatic = true;
    // electric 
    } else if (regNumber.startsWith('EA') || regNumber.startsWith('ЕА')) {
      regNumber.match(config.regexElectric) || regNumber === "" ? this.isInvalidRegNumberForElectric = false :  this.isInvalidRegNumberForElectric = true;
      
      if (regNumber.length > 8) {
        this.isInvalidRegNumberForElectric = true;
      }

    // regular
    } else {
      if (isMainForm) {
        regNumber.match(config.regexRegNumber) || regNumber === "" ? this.isValidMainFormRegNum = false :  this.isValidMainFormRegNum = true;
      } else {
        regNumber.match(config.regexRegNumber) || regNumber === "" ? this.isValidSlotFormRegNum = false :  this.isValidSlotFormRegNum = true;
      }
    }
    
    return regNumber;
  }

  // validateMainFormRegCertNumber(regCertNumber: string): void {
  //   regCertNumber = regCertNumber.toUpperCase();
  //   regCertNumber.match(config.regexOnlyLatinSymbolsAndDigits) || regCertNumber === "" ? this.isValidMainFormRegCertNum = false :  this.isValidMainFormRegCertNum = true;
  //   this.mainTechInspForm.get('registrationCertNumber')?.patchValue(regCertNumber);
  // }

  normalizeRegNumber(formType: string, regNumber: string) {
    this.techInspService.getNormalizedRegNumber(regNumber).subscribe(normalizedNumber => {
      formType === 'mainForm' ? this.mainTechInspForm.get('regNumber')?.patchValue(normalizedNumber.regNum) : this.availableSlotForm.get('regNumber')?.patchValue(normalizedNumber.regNum);
    });

    if (formType === 'mainForm') {
      this.checkForContract(regNumber);
      if (regNumber != "") {
        this.getRegCertNumberIfExists(regNumber);
      }
    }
  }

  getRegCertNumberIfExists(regNumber: string) {
    this.techInspService.getRegCertNumberIfExists(regNumber).subscribe(resp => {
      this.mainTechInspForm.get('registrationCertNumber')?.patchValue(resp.regCertNumber);
    });
  }

  refreshFutureTechnicalInspections() {
    this.refreshIntervalId = setInterval(() => {
      this.techInspService.getFutureTechnicalInspections(this.userTechnicalPointId).subscribe(futureTechInspections => {
        const filteredFutureTechInspections = this.filterFutureTechInspectionsByEmployeeLines(futureTechInspections);
        this.futureTechInspections = filteredFutureTechInspections;
        this.showHideLateAndStartButton(this.futureTechInspections);
        this.getGroupedTechnicalPointWorkingHours(this.availableSlotForm.value, this.arePastLoaded);
        this.futureTechInspections.forEach(techInsp => {
          techInsp.inspectionDate = this.prepareDateForUi(techInsp.inspectionDate);
        });
      });
    }, 1 * 60_000);
  }

  prepareNewTechnicialInpection() {
    this.mainTechInspForm.reset();
    this.additionalTechInspForm.reset();
    this.showAllFields = false;
    this.isStartedTechInsp = false;
    this.isVisiblePaymentServicesTable = false;
    this.disableTechPointServices = null;
    this.disableAnnualServiceAndDiscount = null;
    this.submittedMainForm = false;
    this.submittedAdditionalForm = false;
    this.paymentServiceTableData = [];
    this.paymentServiceConvertedData = [];
    this.techPointWorkingHourIds = [];
    // The goods belong to the inspection that was just finished, not to the next vehicle.
    this.itemLines = [];
    this.catalogResults = [];
    this.itemQuantities = {};
    this.itemSearchTerm = '';
    this.additionalServiceQuantities = {};
    this.technicalInspection = new TechnicalInspection;;
    this.mainTechInspForm.get('rvsCategory')?.patchValue(this.defRvsCategory);
    // reset() above empties the email along with everything else, and the field is required, so
    // the placeholder address has to be put back the way the load path puts it back.
    this.additionalTechInspForm.get('email')?.patchValue(this.defaultTechnicalInspectionEmail);
    this.additionalTechInspForm.get('isAgreeWithNotification')?.patchValue(this.agreeWithNotification);
    this.additionalTechInspForm.get('isEmployee')?.patchValue(false);
    this.mainTechInspForm.get('technicalPointId')?.patchValue(this.userTechnicalPointId);
    this.customerSubscriptionName = null;
    this.techInspStatusName = null;
    this.techInspMobileId = null;
    this.annualService = null;
    this.customerSubscription = null;
    this.issueInvoice = false;
    
    this.techInspService.getRvsCategoryServicesByType(this.defRvsCategory, this.userTechnicalPointId).pipe(
      tap(([basicServices, additionalServices, annualServices]) => {
        this.basicServices = basicServices;
        this.additionalServices = additionalServices;
        this.annualServices = annualServices;
        this.mainTechInspForm.get('basicTechPointService')?.patchValue(this.basicServices.find(service => service.serviceName === this.BASIC_SERVICE_NAME) || null);
        this.annualService = annualServices[0];
        this.selectedAdditionalServices = [];
    })).subscribe();

  }

  private prepareDateForUi(date: any) {
    let splitted  = date.split("-");
    let dateYear  = splitted[0];
    let dateMonth = splitted[1];
    let dateDay   = splitted[2];
    
    if (dateMonth.length === 1) {
      dateMonth = '0'+ dateMonth;
    }
    if (dateDay.length === 1) {
      dateDay = '0'+ dateDay;
    }
    
    return dateDay + '-' + dateMonth + '-' + dateYear;
  }

  private prepareDateForDb(date: any) {
    if (date == null || date == undefined) {
      return null;
    }

    let splitted  = date.split("-");
    let dateYear  = splitted[2];
    let dateMonth = splitted[1];
    let dateDay   = splitted[0];
    
    if (dateMonth.length === 1) {
      dateMonth = '0' + dateMonth;
    }
    if (dateDay.length === 1) {
      dateDay = '0' + dateDay;
    }
    if (dateYear.length !== 4) {
      return date;
    }
    
    return dateYear + '-' + dateMonth + '-' + dateDay;
  }

  showHideLateAndStartButton(futureTechInsps: any) {
    this.showStartButton = false;
    this.showLateButton = false;

    if (futureTechInsps.length != 0) {
      let currentDate = new Date();
      let currentHour = currentDate.getHours();
      let currenMinute = currentDate.getMinutes();

      let firstAvailableSlotTimeFromHour: number = +futureTechInsps[0].slot!.split(":")[0];
      let firstAvailableSlotTimeFromMinute: number = +futureTechInsps[0].slot!.split(":")[1];
      let addedLateTime: number = +futureTechInsps[0].slot!.split(":")[1] + 5;
      let firstAvailableSlotTimeToHour: number = +futureTechInsps[0].slot!.split(":")[2];
      let firstAvailableSlotTimeToMinute: number = +futureTechInsps[0].slot!.split(":")[3];

      let timeFrom = firstAvailableSlotTimeFromHour + ':' + firstAvailableSlotTimeFromMinute;
      let timeTo = firstAvailableSlotTimeToHour + ':' + firstAvailableSlotTimeToMinute;
      let now = currentHour + ":" + currenMinute;
      let lateTime = firstAvailableSlotTimeFromHour + ':' + addedLateTime; 

      let [hours1, minutes1] = timeFrom.split(':');
      let totalSecondsTimeFrom: number = (+hours1) * 60 * 60 + (+minutes1);

      let [hours2, minutes2] = timeTo.split(':');
      let totalSecondsTimeTo: number = (+hours2) * 60 * 60 + (+minutes2);

      let [hours3, minutes3] = now.split(':');
      let totalSecondsNow: number = (+hours3) * 60 * 60 + (+minutes3);

      let [hours4, minutes4] = lateTime.split(':');
      let totalSecondsLated: number = (+hours4) * 60 * 60 + (+minutes4);

      let techInspId: number = +this.technicalInspectionId;
      if (techInspId === futureTechInsps[0].id) {
        this.isStartedTechInsp = true;
        this.showStartButton = false;
        this.showLateButton = false;
      } else {
        if(totalSecondsTimeFrom <= totalSecondsNow && totalSecondsNow <= totalSecondsTimeTo) {
          this.showStartButton = true;
          if (totalSecondsLated <= totalSecondsNow) {
            this.showLateButton = true;
          }
        }
      }
    }
  }

  customerDiscountFormatter(element: any): string {
    return element.discountName;
  }

  private applyLoyaltyCardDiscountForAllowedServices() {
    const value = this.mainTechInspForm.get('basicTechPointService')?.value;
    if (!value) {
      return false;
    }
    const mainServiceName = value.serviceName;
    if (!mainServiceName) {
      return false;
    }
    if (!this.allowedCustomerCardDiscountServices.includes(mainServiceName)) {
      return false;
    }

    return true;
  }

  async onChangeLoyaltyCard(event: any) {
    this.displayExceedCardMessage = false;
    this.displayCardDoesNotExistMessage = false;
    this.displayDiscountAlreadyExistsMessage = false;
    this.displayNoPermissionForMainServicePromoMessage = false;
    this.displayServiceNotSelectedForDiscountMessage = false;
    this.displayDiscountCoveredByPromoCampaignMessage = false;

    // Clear any existing error message timeouts
    if (this.exceedCardMessageTimeout) {
      clearTimeout(this.exceedCardMessageTimeout);
      this.exceedCardMessageTimeout = null;
    }
    if (this.cardDoesNotExistMessageTimeout) {
      clearTimeout(this.cardDoesNotExistMessageTimeout);
      this.cardDoesNotExistMessageTimeout = null;
    }
    if (this.discountAlreadyExistsMessageTimeout) {
      clearTimeout(this.discountAlreadyExistsMessageTimeout);
      this.discountAlreadyExistsMessageTimeout = null;
    }
    if (this.noPermissionForMainServicePromoMessageTimeout) {
      clearTimeout(this.noPermissionForMainServicePromoMessageTimeout);
      this.noPermissionForMainServicePromoMessageTimeout = null;
    }
    if (this.serviceNotSelectedForDiscountMessageTimeout) {
      clearTimeout(this.serviceNotSelectedForDiscountMessageTimeout);
      this.serviceNotSelectedForDiscountMessageTimeout = null;
    }
    if (this.discountCoveredByPromoCampaignMessageTimeout) {
      clearTimeout(this.discountCoveredByPromoCampaignMessageTimeout);
      this.discountCoveredByPromoCampaignMessageTimeout = null;
    }

    const canApply = this.applyLoyaltyCardDiscountForAllowedServices();
    if (!canApply) {
      this.displayExceedCardMessage = false;
      this.displayCardDoesNotExistMessage = false;
      this.clearSubject();
      return;
    }

    if (!event) {
      this.clearSubject();
      this.displayExceedCardMessage = false;
      this.displayCardDoesNotExistMessage = false;
      this.clearInvalidCard();
      return;
    }
    // The table stays on screen - only its contents are dropped, to be priced again below with
    // the discount applied. It used to be hidden here and brought back by the "покажи данни за
    // плащане" button; with that button gone, hiding it would simply make it disappear.
    this.paymentServiceConvertedData = [];
    this.paymentServiceTableData = [];

    const cardField = this.additionalTechInspForm.get('customerCard')?.value;
    if (!cardField) {
      this.additionalTechInspForm.get('customerCard')?.setValue('');
      this.clearSubject();
      this.clearInvalidCard();
      this.displayExceedCardMessage = false;
      this.displayCardDoesNotExistMessage = false;
      // Cleared above and returning early, so the table is priced again here - otherwise it
      // would be left empty with no later pass to fill it.
      this.refreshPaymentServicesTable();
      return;
    }

    await this.cardService.getLoyalComboByCard(cardField).toPromise().then(data => {
      if (data.messageOperation === 'Card usage exceeds maximum limit.') {
        this.displayCardDoesNotExistMessage = false;
        this.displayExceedCardMessage = true;
        this.hideErrorMessageAfterDelay('exceedCard', 7000);
      }
      if (data.messageOperation === 'Card does not exist.') {
        this.displayExceedCardMessage = false;
        this.displayCardDoesNotExistMessage = true;
        this.hideErrorMessageAfterDelay('cardDoesNotExist', 7000);
      }

      if (this.displayCardDoesNotExistMessage || this.displayExceedCardMessage) {
        this.customerCard = null;
        this.clearInvalidCard();
        this.clearSubject();
        return;
      }

      this.displayExceedCardMessage = false;
      this.displayCardDoesNotExistMessage = false;

      const loyalCombo = data as LoyalCombo;
      if (!loyalCombo.customerDiscount) {
        this.clearInvalidCard();
        this.clearSubject();
        console.error(`Card doesn't have a discount.`);
        this.customerCard = null;
        return;
      }
      this.lastSelectedDiscountByCard = loyalCombo.customerDiscount.discountName;
      this.customerDiscounts = [];
      this.customerDiscounts.push(loyalCombo.customerDiscount);

      this.additionalTechInspForm.get('customerDiscount')?.setValue(loyalCombo.customerDiscount);
      this.customerCard = loyalCombo.customerCard;
      
      if (loyalCombo.subjectVersion) {
        if (loyalCombo.subjectVersion?.mobileAppId) {
          this.subjectMobileAppId = loyalCombo.subjectVersion?.mobileAppId;
        }
        this.setSubjectData(loyalCombo.subjectVersion, true, true);
      } else {
        this.clearSubject();
      }
      // A full re-price rather than calculateServicesDiscount alone: the apply path above
      // emptied paymentServiceTableData, and that method only adjusts what is already in it.
      this.refreshPaymentServicesTable();
    })
    .catch(err => {
      err.customMessage = `Card doesn't exist`; 
      displayError(err);
      this.clearInvalidCard();
      this.clearSubject();
    });
  }

  private clearInvalidCard() {
    this.customerDiscounts = this.initialCustomerDiscounts;
    this.customerCard = null;
    this.additionalTechInspForm.get('customerDiscount')?.setValue('');
    this.lastSelectedDiscountByCard = this.INVALID_LOYAL_CARD;
  }

  async onApplyCardOrPromoCode() {
    const inputValue = this.additionalTechInspForm.get('cardOrPromoCode')?.value?.trim();
    if (!inputValue) {
      return;
    }

    this.isValidatingCardOrPromoCode = true;

    try {
      // Try to apply as promo code first
      const isPromoCode = await this.tryApplyPromoCode(inputValue);
      // Check if any error message was triggered - if so, stop the process
      if (this.displayDiscountAlreadyExistsMessage) {
        return;
      }
      if (this.displayNoPermissionForMainServicePromoMessage) {
        return;
      }
      if (this.displayServiceNotSelectedForDiscountMessage) {
        return;
      }
      if (this.displayDiscountCoveredByPromoCampaignMessage) {
        return;
      }
      if (!isPromoCode) {
        // If not a promo code, try as loyalty card
        const isLoyaltyCard = await this.tryApplyLoyaltyCard(inputValue);
        // Check if any error message was triggered - if so, stop the process
        if (this.displayDiscountAlreadyExistsMessage) {
          return;
        }
        if (!isLoyaltyCard) {
          // If not a normal loyalty card, check if it's a 16-digit mobile loyalty card
          if (this.isMobileLoyaltyCardFormat(inputValue)) {
            await this.tryApplyMobileLoyaltyCard(inputValue);
            // Check if any error message was triggered - if so, stop the process
            if (this.displayDiscountAlreadyExistsMessage) {
              return;
            }
          }
        }
      }
    } finally {
      this.isValidatingCardOrPromoCode = false;
      // Whatever happened above - a code applied, rejected, or not recognised at all - the
      // attempt emptied the price table on its way through. Pricing it again here, at the one
      // exit every branch passes through, is what keeps it from being left blank on a failure.
      this.refreshPaymentServicesTable();
    }
  }

  async onVoucherCodeSelect() {
    const selectedVoucherCode = this.additionalTechInspForm.get('voucherCode')?.value;
    if (selectedVoucherCode && selectedVoucherCode.code) {
      // Set the voucher code in the cardOrPromoCode input field
      this.additionalTechInspForm.get('cardOrPromoCode')?.setValue(selectedVoucherCode.code);
      // Automatically apply the voucher code as a promo code
      await this.onApplyCardOrPromoCode();
    }
  }

  private async tryApplyPromoCode(code: string): Promise<boolean> {
    try {
      const rvsCategory = this.mainTechInspForm.get('rvsCategory')?.value;
      if (!rvsCategory || !rvsCategory.code) {
        return false;
      }

      const promocodeDTO = {
        promoCode: code,
        rvsCategoryCode: rvsCategory.code,
        technicalPointMobileAppId: this.userTechnicalPointMobileIds?.[0]
      };

      return new Promise<boolean>((resolve) => {
        const promotionDiscountService = new PromotionDiscountService(this.http);
        promotionDiscountService.validatePromocode(promocodeDTO).subscribe(
          (data: CustomerPromocodeMobileDTO) => {
            if (!data || (!data.promocodeId && !data.voucherId)) {
              resolve(false);
              return;
            }

            // Get all service names this discount applies to
            const serviceNames = data.services?.map(s => s.name).filter(Boolean) || [];

            // Check if at least one service this discount applies to is selected (basic or additional)
            const selectedBasicService = this.mainTechInspForm.get('basicTechPointService')?.value;
            const serviceName = serviceNames.find(name =>
              (selectedBasicService?.serviceName === name) ||
              this.selectedAdditionalServices?.some((s: TechPointService) => s.serviceName === name) ||
              (this.annualService?.serviceName === name)
            ) || '';
            if (!serviceName) {
              this.displayExceedCardMessage = false;
              this.displayCardDoesNotExistMessage = false;
              this.displayDiscountAlreadyExistsMessage = false;
              this.displayNoPermissionForMainServicePromoMessage = false;
              this.displayServiceNotSelectedForDiscountMessage = true;
              this.hideErrorMessageAfterDelay('serviceNotSelectedForDiscount', 7000);
              resolve(false);
              return;
            }

            // Restrict applying promocode for main (basic) service if user lacks permission
            const isMainService = this.basicServices?.some(s => s.serviceName === serviceName);
            if (isMainService && !this.perms.hasAccess(this.perms.CAN_ENTER_PROMO_CODES_FOR_MAIN_SERVICES)) {
              this.displayExceedCardMessage = false;
              this.displayCardDoesNotExistMessage = false;
              this.displayDiscountAlreadyExistsMessage = false;
              this.displayNoPermissionForMainServicePromoMessage = true;
              this.hideErrorMessageAfterDelay('noPermissionForMainServicePromo', 7000);
              resolve(false);
              return;
            }

            // A service made free by a promo campaign trigger stays free: a voucher cannot beat 100%.
            if (!data.promocodeId && !!data.voucherId) {
              const selectedServices: TechPointService[] = [
                ...(selectedBasicService ? [selectedBasicService] : []),
                ...(this.selectedAdditionalServices || []),
                ...(this.annualService ? [this.annualService] : [])
              ];
              const targetService = selectedServices.find(s => s.serviceName === serviceName);
              if (targetService && this.promotionalCampaign.isEligibleForFreeCampaign(targetService, selectedServices, this.activeDiscountCampaigns)) {
                this.displayExceedCardMessage = false;
                this.displayCardDoesNotExistMessage = false;
                this.displayDiscountAlreadyExistsMessage = false;
                this.displayNoPermissionForMainServicePromoMessage = false;
                this.displayServiceNotSelectedForDiscountMessage = false;
                this.displayDiscountCoveredByPromoCampaignMessage = true;
                this.hideErrorMessageAfterDelay('discountCoveredByPromoCampaign', 7000);
                resolve(false);
                return;
              }
            }

            // Check if there's already a discount applied for this specific service
            const hasExistingDiscountForService = this.appliedDiscounts.some(ad => {
              if (ad.type === 'promocode' && ad.promoCodeService?.promotionServiceName) {
                return ad.promoCodeService.promotionServiceName === serviceName;
              }
              if (ad.type === 'card' || ad.type === 'customer-discount') {
                // Loyalty cards and customer discount apply to any main service name
                return this.mainServiceNamesForDiscount.includes(serviceName);
              }
              return false;
            });
            
            if (hasExistingDiscountForService) {
              this.displayExceedCardMessage = false;
              this.displayCardDoesNotExistMessage = false;
              this.displayDiscountAlreadyExistsMessage = true;
              this.hideErrorMessageAfterDelay('discountAlreadyExists', 7000);
              resolve(false);
              return;
            }
            
            // Clear error messages if validation passes
            this.displayDiscountAlreadyExistsMessage = false;
            this.displayNoPermissionForMainServicePromoMessage = false;
            this.displayServiceNotSelectedForDiscountMessage = false;
            this.displayDiscountCoveredByPromoCampaignMessage = false;

            // The table stays on screen - only its contents are dropped, to be priced again
            // with the promo code applied. Hiding it made sense when a button brought it back.
            this.paymentServiceConvertedData = [];
            this.paymentServiceTableData = [];

            // Map the mobile DTO response to PromotionCodeService format
            // discountType is the kind from PriceAttribute: 'P' for percentage, 'V' for value
            const discountKind = data.discountType || this.DISCOUNT_PERCENTAGE;
            const isPercentage = discountKind === this.DISCOUNT_PERCENTAGE;
            const discountValue = data.discountValue || 0;
            
            // Use promocodeId if available, otherwise use voucherId
            // At this point, we know at least one exists due to the check above
            const promotionId = data.promocodeId ?? data.voucherId ?? 0;
            // Determine if this is a voucher (has voucherId but no promocodeId)
            const isVoucher = !data.promocodeId && !!data.voucherId;

            // Create PromotionCodeService-like object from the response
            const promoCodeService: PromotionCodeService = {
              id: promotionId,
              promotionId: promotionId,
              promotionServiceName: serviceName,
              totalDiscountForServices: discountValue,
              isDiscountInPercentage: isPercentage
            };

            this.promotionCodeService = promoCodeService;
            this.hasPromocode = true;

            // Create CustomerDiscount from promo code data
            // When kind is 'P' (percentage): amount = discountValue, amountDds = 0, ddsValue = 0
            // Otherwise (value): amount = 0, amountDds = discountValue, ddsValue = 0
            const customerDiscount: CustomerDiscount = {
              id: promotionId,
              discountName: data.description || code,
              priceAttribute: {
                id: undefined,
                kind: discountKind,
                amount: isPercentage ? discountValue : null,
                amountDds: isPercentage ? null : discountValue,
                ddsValue: null
              },
              isValid: true,
              priceAttributeAmountDds: discountValue,
              priceAttributeKind: discountKind
            };

            // Add to applied discounts
            const appliedDiscount = {
              type: 'promocode' as const,
              name: code,
              discount: customerDiscount,
              promoCodeService: promoCodeService,
              isVoucher: isVoucher,
              paymentTypes: data.paymentTypes || [],
              isMainService: isMainService
            };

            // Check if already applied
            const exists = this.appliedDiscounts.some(ad => ad.type === 'promocode' && ad.name === code);
            if (!exists) {
              this.appliedDiscounts.push(appliedDiscount);
              // Add discount to customerDiscounts array
              this.customerDiscounts.push(appliedDiscount.discount);
              // Add to promoCodeDiscounts collection for multiple promo codes support
              this.promoCodeDiscounts.push(customerDiscount);
              this.showPromoCodeSuccessMessage = true;
              this.hideSuccessMessageAfterAnimation('promoCode', 7000);
              this.recalculateAllowedPaymentTypes();
            }

            // Set discount information on the corresponding service in paymentServiceTableData
            if (serviceName && this.paymentServiceTableData) {
              const targetService = this.paymentServiceTableData.find(service => service.serviceName === serviceName);
              if (targetService) {
                targetService.discountId = promotionId;
                targetService.discountTypeName = isVoucher ? 'VOUCHER_CODE' : 'PROMO_CODE';
                targetService.discountKindName = discountKind as 'V' | 'P';
                targetService.discountValue = discountValue;
              }
            }

            // Also set on services in basicServices, additionalServices, and annualServices if they match
            const allServices = [
              ...(this.basicServices || []),
              ...(this.additionalServices || []),
              ...(this.annualServices || [])
            ];
            const matchingService = allServices.find(service => service.serviceName === serviceName);
            if (matchingService) {
              matchingService.discountId = promotionId;
              matchingService.discountTypeName = isVoucher ? 'VOUCHER_CODE' : 'PROMO_CODE';
              matchingService.discountKindName = discountKind as 'V' | 'P';
              matchingService.discountValue = discountValue;
            }

            if (data.subjectVersion) {
              if (data.subjectVersion?.mobileAppId) {
                this.subjectMobileAppId = data.subjectVersion?.mobileAppId;
              }
              this.isSubjectFromPromocode = true;
              this.setSubjectData(data.subjectVersion, true, true);
            }

            // Clear input
            this.additionalTechInspForm.get('cardOrPromoCode')?.setValue('');
            // A full re-price rather than calculateServicesDiscount alone: the apply path above
            // emptied paymentServiceTableData, and that method only adjusts what is in it.
            this.refreshPaymentServicesTable();
            resolve(true);
          },
          (error: any) => {
            console.error('Error applying promo code:', error);
            // The voucher's services are all already covered by an active promo campaign with an
            // equal or greater discount. Show it here instead of falling through to the loyalty card check.
            if (error?.error?.code === this.DISCOUNT_COVERED_BY_PROMO_CAMPAIGN_ERROR_CODE) {
              this.displayExceedCardMessage = false;
              this.displayCardDoesNotExistMessage = false;
              this.displayDiscountAlreadyExistsMessage = false;
              this.displayNoPermissionForMainServicePromoMessage = false;
              this.displayServiceNotSelectedForDiscountMessage = false;
              this.displayDiscountCoveredByPromoCampaignMessage = true;
              this.hideErrorMessageAfterDelay('discountCoveredByPromoCampaign', 7000);
            }
            resolve(false);
          }
        );
      });
    } catch (error) {
      return false;
    }
  }

  private recalculateAllowedPaymentTypes(): void {
    const mainServicePromo = this.appliedDiscounts.find(
      ad => ad.type === 'promocode' && ad.isMainService && ad.paymentTypes && ad.paymentTypes.length > 0
    );
    this.allowedPaymentTypeDescriptions = mainServicePromo?.paymentTypes
      ? mainServicePromo.paymentTypes.filter(pt => pt.isValid).map(pt => pt.description)
      : null;
  }

  isPaymentTypeAllowed(paymentTypeDescription: string): boolean {
    return this.allowedPaymentTypeDescriptions === null || this.allowedPaymentTypeDescriptions.includes(paymentTypeDescription);
  }

  private isMobileLoyaltyCardFormat(value: string): boolean {
    // Mobile loyalty card is always 16 digits (0-9 only)
    return /^[0-9]{16}$/.test(value);
  }

  private async tryApplyLoyaltyCard(cardNumber: string): Promise<boolean> {
    this.displayExceedCardMessage = false;
    this.displayCardDoesNotExistMessage = false;
    this.displayDiscountAlreadyExistsMessage = false;
    this.displayNoPermissionForMainServicePromoMessage = false;
    this.displayServiceNotSelectedForDiscountMessage = false;
    this.displayDiscountCoveredByPromoCampaignMessage = false;

    // Clear any existing error message timeouts
    if (this.exceedCardMessageTimeout) {
      clearTimeout(this.exceedCardMessageTimeout);
      this.exceedCardMessageTimeout = null;
    }
    if (this.cardDoesNotExistMessageTimeout) {
      clearTimeout(this.cardDoesNotExistMessageTimeout);
      this.cardDoesNotExistMessageTimeout = null;
    }
    if (this.discountAlreadyExistsMessageTimeout) {
      clearTimeout(this.discountAlreadyExistsMessageTimeout);
      this.discountAlreadyExistsMessageTimeout = null;
    }
    if (this.noPermissionForMainServicePromoMessageTimeout) {
      clearTimeout(this.noPermissionForMainServicePromoMessageTimeout);
      this.noPermissionForMainServicePromoMessageTimeout = null;
    }
    if (this.serviceNotSelectedForDiscountMessageTimeout) {
      clearTimeout(this.serviceNotSelectedForDiscountMessageTimeout);
      this.serviceNotSelectedForDiscountMessageTimeout = null;
    }
    if (this.discountCoveredByPromoCampaignMessageTimeout) {
      clearTimeout(this.discountCoveredByPromoCampaignMessageTimeout);
      this.discountCoveredByPromoCampaignMessageTimeout = null;
    }

    const canApply = this.applyLoyaltyCardDiscountForAllowedServices();
    if (!canApply) {
      this.displayExceedCardMessage = false;
      this.displayCardDoesNotExistMessage = false;
      return false;
    }

    // The table stays on screen - only its contents are dropped, to be priced again below with
    // the discount applied. It used to be hidden here and brought back by the "покажи данни за
    // плащане" button; with that button gone, hiding it would simply make it disappear.
    this.paymentServiceConvertedData = [];
    this.paymentServiceTableData = [];

    try {
      const data = await this.cardService.getLoyalComboByCard(cardNumber).toPromise();
      
      if (data.messageOperation === 'Card usage exceeds maximum limit.') {
        this.displayCardDoesNotExistMessage = false;
        this.displayExceedCardMessage = true;
        this.hideErrorMessageAfterDelay('exceedCard', 7000);
        return false;
      }
      if (data.messageOperation === 'Card does not exist.') {
        this.displayExceedCardMessage = false;
        this.displayCardDoesNotExistMessage = true;
        this.hideErrorMessageAfterDelay('cardDoesNotExist', 7000);
        return false;
      }

      this.displayExceedCardMessage = false;
      this.displayCardDoesNotExistMessage = false;

      const loyalCombo = data as LoyalCombo;
      if (!loyalCombo.customerDiscount) {
        this.customerCard = null;
        console.error(`Card doesn't have a discount.`);
        return false;
      }

      // Check if there's already a discount applied for the selected basic service (loyalty cards apply to this service)
      const selectedBasicForCard = this.getSelectedBasicServiceName();
      const hasExistingDiscountForService = this.appliedDiscounts.some(ad => {
        if (ad.type === 'promocode' && ad.promoCodeService?.promotionServiceName) {
          return selectedBasicForCard ? ad.promoCodeService.promotionServiceName === selectedBasicForCard : this.mainServiceNamesForDiscount.includes(ad.promoCodeService.promotionServiceName);
        }
        if (ad.type === 'card' || ad.type === 'customer-discount') {
          // Another loyalty card / customer discount is already applied
          return true;
        }
        return false;
      });
      
      if (hasExistingDiscountForService) {
        this.displayExceedCardMessage = false;
        this.displayCardDoesNotExistMessage = false;
        this.displayDiscountAlreadyExistsMessage = true;
        this.hideErrorMessageAfterDelay('discountAlreadyExists', 7000);
        return false;
      }
      
      // Clear error messages if validation passes
      this.displayDiscountAlreadyExistsMessage = false;

      this.lastSelectedDiscountByCard = loyalCombo.customerDiscount.discountName;
      this.customerCard = loyalCombo.customerCard;
      this.additionalTechInspForm.get('customerCard')?.setValue(cardNumber);

      // Add to applied discounts
      const appliedDiscount = {
        type: 'card' as const,
        name: loyalCombo.customerDiscount.discountName,
        discount: loyalCombo.customerDiscount,
        cardNumber: cardNumber
      };

      // Check if already applied
      const exists = this.appliedDiscounts.some(ad => ad.type === 'card' && ad.cardNumber === cardNumber);
      if (!exists) {
        this.appliedDiscounts.push(appliedDiscount);
        this.customerDiscounts.push(loyalCombo.customerDiscount);
        this.additionalTechInspForm.get('customerDiscount')?.setValue(loyalCombo.customerDiscount);
        this.showCardSuccessMessage = true;
        this.hideSuccessMessageAfterAnimation('card', 7000);
      }

      // Set discount information on the corresponding service (selected basic service for loyalty cards)
      const serviceName = this.getSelectedBasicServiceName() ?? this.BASIC_SERVICE_NAME;
      const discountId = loyalCombo.customerDiscount.id;
      const discountKind = loyalCombo.customerDiscount.priceAttributeKind || loyalCombo.customerDiscount.priceAttribute?.kind || 'P';
      const discountValue = loyalCombo.customerDiscount.priceAttributeAmountDds || loyalCombo.customerDiscount.priceAttribute?.amountDds || loyalCombo.customerDiscount.priceAttribute?.amount || 0;

      // Set discount information on the corresponding service in paymentServiceTableData
      if (serviceName && this.paymentServiceTableData) {
        const targetService = this.paymentServiceTableData.find(service => service.serviceName === serviceName);
        if (targetService) {
          targetService.discountId = discountId;
          targetService.discountTypeName = 'LOYAL_CARD_PLASTIC';
          targetService.discountKindName = discountKind as 'V' | 'P';
          targetService.discountValue = discountValue;
        }
      }

      // Also set on services in basicServices, additionalServices, and annualServices if they match
      const allServices = [
        ...(this.basicServices || []),
        ...(this.additionalServices || []),
        ...(this.annualServices || [])
      ];
      const matchingService = allServices.find(service => service.serviceName === serviceName);
      if (matchingService) {
        matchingService.discountId = discountId;
        matchingService.discountTypeName = 'LOYAL_CARD_PLASTIC';
        matchingService.discountKindName = discountKind as 'V' | 'P';
        matchingService.discountValue = discountValue;
      }

      if (loyalCombo.subjectVersion) {
        if (loyalCombo.subjectVersion?.mobileAppId) {
          this.subjectMobileAppId = loyalCombo.subjectVersion?.mobileAppId;
        }
        this.setSubjectData(loyalCombo.subjectVersion, true, true);
      }

      // Clear input
      this.additionalTechInspForm.get('cardOrPromoCode')?.setValue('');
      // A full re-price rather than calculateServicesDiscount alone: the apply path above
      // emptied paymentServiceTableData, and that method only adjusts what is in it.
      this.refreshPaymentServicesTable();
      return true;
    } catch (err: any) {
      err.customMessage = `Card doesn't exist`;
      displayError(err);
      return false;
    }
  }

  private async tryApplyMobileLoyaltyCard(cardNumber: string): Promise<void> {
    this.displayExceedCardMessage = false;
    this.displayCardDoesNotExistMessage = false;
    this.displayDiscountAlreadyExistsMessage = false;
    this.displayNoPermissionForMainServicePromoMessage = false;
    this.displayServiceNotSelectedForDiscountMessage = false;
    this.displayDiscountCoveredByPromoCampaignMessage = false;

    // Clear any existing error message timeouts
    if (this.exceedCardMessageTimeout) {
      clearTimeout(this.exceedCardMessageTimeout);
      this.exceedCardMessageTimeout = null;
    }
    if (this.cardDoesNotExistMessageTimeout) {
      clearTimeout(this.cardDoesNotExistMessageTimeout);
      this.cardDoesNotExistMessageTimeout = null;
    }
    if (this.discountAlreadyExistsMessageTimeout) {
      clearTimeout(this.discountAlreadyExistsMessageTimeout);
      this.discountAlreadyExistsMessageTimeout = null;
    }
    if (this.noPermissionForMainServicePromoMessageTimeout) {
      clearTimeout(this.noPermissionForMainServicePromoMessageTimeout);
      this.noPermissionForMainServicePromoMessageTimeout = null;
    }
    if (this.serviceNotSelectedForDiscountMessageTimeout) {
      clearTimeout(this.serviceNotSelectedForDiscountMessageTimeout);
      this.serviceNotSelectedForDiscountMessageTimeout = null;
    }
    if (this.discountCoveredByPromoCampaignMessageTimeout) {
      clearTimeout(this.discountCoveredByPromoCampaignMessageTimeout);
      this.discountCoveredByPromoCampaignMessageTimeout = null;
    }

    try {
      const response = await this.cardService.getMobileLoyaltyCard(cardNumber).toPromise();
      
      if (!response || !response.success || !response.data) {
        this.displayCardDoesNotExistMessage = true;
        this.hideErrorMessageAfterDelay('cardDoesNotExist', 7000);
        return;
      }

      this.displayExceedCardMessage = false;
      this.displayCardDoesNotExistMessage = false;

      // Mobile loyalty card found - show success message
      this.showMobileCardSuccessMessage = true;
      this.hideSuccessMessageAfterAnimation('mobile-card', 7000);

      // Add mobile loyalty card to applied discounts (remove all whitespace from card number)
      const rawCardNumber = response.data.card_number || cardNumber;
      const mobileCardNumber = typeof rawCardNumber === 'string' ? rawCardNumber.replace(/\s/g, '') : String(rawCardNumber).replace(/\s/g, '');
      const appliedDiscount = {
        type: 'mobile-card' as const,
        name: `Мобилна карта - ${mobileCardNumber}`,
        discount: null,
        cardNumber: mobileCardNumber
      };

      // Check if already applied
      const exists = this.appliedDiscounts.some(ad => ad.type === 'mobile-card' && ad.cardNumber === mobileCardNumber);
      if (!exists) {
        this.appliedDiscounts.push(appliedDiscount);
        this.additionalTechInspForm.get('mobileCard')?.setValue(mobileCardNumber);
      }

      // Fetch active voucher codes by customer id
      if (response.data.erp_customer_id) {
        const customerId = parseInt(response.data.erp_customer_id);
        if (!isNaN(customerId)) {
          try {
            const voucherCodes = await this.cardService.getActiveVoucherCodesByCustomerId(customerId).toPromise();
            if (voucherCodes && voucherCodes.length > 0) {
              this.activeVoucherCodes = voucherCodes;
            }
          } catch (voucherErr: any) {
            console.error('Error fetching voucher codes:', voucherErr);
            this.activeVoucherCodes = [];
          }
        }
      }

      // Clear input
      this.additionalTechInspForm.get('cardOrPromoCode')?.setValue('');
    } catch (err: any) {
      this.displayCardDoesNotExistMessage = true;
      this.hideErrorMessageAfterDelay('cardDoesNotExist', 7000);
      err.customMessage = `Mobile loyalty card doesn't exist`;
      displayError(err);
    }
  }

  onRemoveAppliedDiscount(index: number) {
    const discount = this.appliedDiscounts[index];
    let adjustedIndex = index;
    
    // Get discount information to remove from order elements
    let discountIdToRemove: number | undefined;
    let discountTypeNameToRemove: string | undefined;
    let serviceNameToClear: string | undefined;

    if (discount.type === 'promocode') {
      discountIdToRemove = discount.discount?.id || discount.promoCodeService?.id;
      discountTypeNameToRemove = discount.isVoucher ? 'VOUCHER_CODE' : 'PROMO_CODE';
      serviceNameToClear = discount.promoCodeService?.promotionServiceName || undefined;
      
      // Remove promo code from promoCodeDiscounts collection
      if (discount.discount) {
        const promoCodeIndex = this.promoCodeDiscounts.findIndex(pcd => pcd.id === discount.discount?.id);
        if (promoCodeIndex !== -1) {
          this.promoCodeDiscounts.splice(promoCodeIndex, 1);
        }
      }
      // Remove promo code
      // Check if there are any promo codes left
      this.hasPromocode = this.promoCodeDiscounts.length > 0;
      if (!this.hasPromocode) {
        this.promotionCodeService = null;
        this.additionalTechInspForm.get('promotionId')?.setValue(null);
      }
      if (this.isSubjectFromPromocode && (this.mainServiceNamesForDiscount.includes(serviceNameToClear || '') || !this.hasPromocode)) {
        this.clearSubject();
      }
    } else if (discount.type === 'card') {
      discountIdToRemove = discount.discount?.id;
      discountTypeNameToRemove = 'LOYAL_CARD_PLASTIC';
      serviceNameToClear = this.getSelectedBasicServiceName() ?? this.BASIC_SERVICE_NAME;
      
      // Remove card discount
      if (discount.cardNumber === this.additionalTechInspForm.get('customerCard')?.value) {
        this.additionalTechInspForm.get('customerCard')?.setValue(null);
        this.customerCard = null;
        this.clearInvalidCard();
      }
      this.clearSubject();
    } else if (discount.type === 'customer-discount') {
      discountIdToRemove = discount.discount?.id;
      discountTypeNameToRemove = 'LOYAL_CARD_PLASTIC';
      serviceNameToClear = this.getSelectedBasicServiceName() ?? this.BASIC_SERVICE_NAME;
      this.additionalTechInspForm.get('customerDiscount')?.setValue(null);

      // Remove discount information from order elements (main service) and restore original prices, same as promocode/card
      if (this.technicalInspection?.techInspectionOrders) {
        this.technicalInspection.techInspectionOrders.forEach(techInspOrder => {
          if (techInspOrder.order?.orderElements) {
            techInspOrder.order.orderElements.forEach(orderElement => {
              const isMainServiceElement = orderElement.service?.name != null && this.mainServiceNamesForDiscount.includes(orderElement.service.name);
              const matchesDiscount = orderElement.discountId != null && orderElement.discountTypeName === discountTypeNameToRemove &&
                (orderElement.discountId === discountIdToRemove || (isMainServiceElement && discountIdToRemove == null));
              if (matchesDiscount) {
                const originalTotalAmount = (orderElement.totalAmount || 0) + (orderElement.discountAmount || 0);
                const ddsAmount = Math.round((originalTotalAmount * 20 / 100) * 100) / 100;
                const amount = Math.round((originalTotalAmount - ddsAmount) * 100) / 100;
                orderElement.totalAmount = originalTotalAmount;
                orderElement.amount = amount;
                orderElement.ddsAmount = ddsAmount;
                orderElement.discountAmount = 0;
                orderElement.discountId = undefined;
                orderElement.discountTypeName = undefined;
                orderElement.discountKindName = undefined;
                orderElement.discountValue = undefined;
              }
            });
          }
        });
      }
    } else if (discount.type === 'mobile-card') {
      // Remove mobile card - clear voucher codes and remove applied voucher codes
      this.activeVoucherCodes = [];
      this.additionalTechInspForm.get('voucherCode')?.setValue(null);
      this.additionalTechInspForm.get('mobileCard')?.setValue(null);
      
      // Collect voucher discount IDs to remove from order elements
      const voucherDiscountIdsToRemove: number[] = [];
      const voucherServiceNamesToClear = new Set<string>();
      
      // Find and remove all applied voucher codes (promocodes with isVoucher: true)
      // Iterate from end to beginning to avoid index shifting issues
      for (let i = this.appliedDiscounts.length - 1; i >= 0; i--) {
        const ad = this.appliedDiscounts[i];
        if (ad.type === 'promocode' && ad.isVoucher) {
          // Collect discount ID and service name for removal from order elements
          const voucherDiscountId = ad.discount?.id || ad.promoCodeService?.id;
          const voucherServiceName = ad.promoCodeService?.promotionServiceName;
          if (voucherDiscountId) {
            voucherDiscountIdsToRemove.push(voucherDiscountId);
          }
          if (voucherServiceName) {
            voucherServiceNamesToClear.add(voucherServiceName);
          }
          
          // Remove from promoCodeDiscounts
          if (ad.discount) {
            const promoCodeIndex = this.promoCodeDiscounts.findIndex(pcd => pcd.id === ad.discount?.id);
            if (promoCodeIndex !== -1) {
              this.promoCodeDiscounts.splice(promoCodeIndex, 1);
            }
            
            // Remove from customerDiscounts
            const customerDiscountIndex = this.customerDiscounts.findIndex(cd => cd.id === ad.discount?.id);
            if (customerDiscountIndex !== -1) {
              this.customerDiscounts.splice(customerDiscountIndex, 1);
            }
          }
          
          // Remove from appliedDiscounts
          this.appliedDiscounts.splice(i, 1);
          
          // Adjust the index if we removed an item before the mobile card
          if (i < adjustedIndex) {
            adjustedIndex--;
          }
        }
      }
      
      // Remove discount information from order elements for all voucher codes and restore original prices
      if (voucherDiscountIdsToRemove.length > 0 && this.technicalInspection?.techInspectionOrders) {
        this.technicalInspection.techInspectionOrders.forEach(techInspOrder => {
          if (techInspOrder.order?.orderElements) {
            techInspOrder.order.orderElements.forEach(orderElement => {
              if (orderElement.discountId && voucherDiscountIdsToRemove.includes(orderElement.discountId) && 
                  orderElement.discountTypeName === 'VOUCHER_CODE') {
                // Restore original prices by adding back the discount amount
                const originalTotalAmount = (orderElement.totalAmount || 0) + (orderElement.discountAmount || 0);
                
                // Recalculate ddsAmount (20% VAT)
                const ddsAmount = Math.round((originalTotalAmount * 20 / 100) * 100) / 100;
                
                // Recalculate amount (totalAmount - ddsAmount)
                const amount = Math.round((originalTotalAmount - ddsAmount) * 100) / 100;
                
                // Restore original prices
                orderElement.totalAmount = originalTotalAmount;
                orderElement.amount = amount;
                orderElement.ddsAmount = ddsAmount;
                orderElement.discountAmount = 0;
                
                // Clear discount information from order element
                orderElement.discountId = undefined;
                orderElement.discountTypeName = undefined;
                orderElement.discountKindName = undefined;
                orderElement.discountValue = undefined;
              }
            });
          }
        });
      }
      
      // Clear discount information from services for all voucher codes
      voucherServiceNamesToClear.forEach(serviceName => {
        // Clear from paymentServiceTableData
        if (this.paymentServiceTableData) {
          const targetService = this.paymentServiceTableData.find(service => service.serviceName === serviceName);
          if (targetService) {
            targetService.discountId = undefined;
            targetService.discountTypeName = undefined;
            targetService.discountKindName = undefined;
            targetService.discountValue = undefined;
          }
        }

        // Clear from basicServices, additionalServices, and annualServices
        const allServices = [
          ...(this.basicServices || []),
          ...(this.additionalServices || []),
          ...(this.annualServices || [])
        ];
        const matchingService = allServices.find(service => service.serviceName === serviceName);
        if (matchingService) {
          matchingService.discountId = undefined;
          matchingService.discountTypeName = undefined;
          matchingService.discountKindName = undefined;
          matchingService.discountValue = undefined;
        }
      });
      
      // Update hasPromocode flag
      this.hasPromocode = this.promoCodeDiscounts.length > 0;
      if (!this.hasPromocode) {
        this.promotionCodeService = null;
        this.additionalTechInspForm.get('promotionId')?.setValue(null);
      }
      this.clearSubject();
    }
    
    // Remove discount information from order elements and restore original prices
    if (discountIdToRemove && discountTypeNameToRemove && this.technicalInspection?.techInspectionOrders) {
      this.technicalInspection.techInspectionOrders.forEach(techInspOrder => {
        if (techInspOrder.order?.orderElements) {
          techInspOrder.order.orderElements.forEach(orderElement => {
            if (orderElement.discountId === discountIdToRemove && orderElement.discountTypeName === discountTypeNameToRemove) {
              // Restore original prices by adding back the discount amount
              const originalTotalAmount = (orderElement.totalAmount || 0) + (orderElement.discountAmount || 0);
              
              // Recalculate ddsAmount (20% VAT)
              const ddsAmount = Math.round((originalTotalAmount * 20 / 100) * 100) / 100;
              
              // Recalculate amount (totalAmount - ddsAmount)
              const amount = Math.round((originalTotalAmount - ddsAmount) * 100) / 100;
              
              // Restore original prices
              orderElement.totalAmount = originalTotalAmount;
              orderElement.amount = amount;
              orderElement.ddsAmount = ddsAmount;
              orderElement.discountAmount = 0;
              
              // Clear discount information from order element
              orderElement.discountId = undefined;
              orderElement.discountTypeName = undefined;
              orderElement.discountKindName = undefined;
              orderElement.discountValue = undefined;
            }
          });
        }
      });
    }
    
    // Clear discount information from services
    if (serviceNameToClear) {
      // Clear from paymentServiceTableData
      if (this.paymentServiceTableData) {
        const targetService = this.paymentServiceTableData.find(service => service.serviceName === serviceNameToClear);
        if (targetService) {
          targetService.discountId = undefined;
          targetService.discountTypeName = undefined;
          targetService.discountKindName = undefined;
          targetService.discountValue = undefined;
        }
      }

      // Clear from basicServices, additionalServices, and annualServices
      const allServices = [
        ...(this.basicServices || []),
        ...(this.additionalServices || []),
        ...(this.annualServices || [])
      ];
      const matchingService = allServices.find(service => service.serviceName === serviceNameToClear);
      if (matchingService) {
        matchingService.discountId = undefined;
        matchingService.discountTypeName = undefined;
        matchingService.discountKindName = undefined;
        matchingService.discountValue = undefined;
      }
    }
    
    // The table stays on screen - only its contents are dropped, to be priced again below with
    // the discount applied. It used to be hidden here and brought back by the "покажи данни за
    // плащане" button; with that button gone, hiding it would simply make it disappear.
    this.paymentServiceConvertedData = [];
    this.paymentServiceTableData = [];

    this.appliedDiscounts.splice(adjustedIndex, 1);
    this.recalculateAllowedPaymentTypes();
    // A full re-price rather than calculateServicesDiscount alone: the lines above emptied
    // paymentServiceTableData, and that method only adjusts what is already in it.
    this.refreshPaymentServicesTable();
  }

  private async reconstructAppliedDiscountsFromOrderElements(): Promise<void> {
    if (!this.technicalInspection?.techInspectionOrders || this.technicalInspection.techInspectionOrders.length === 0) {
      return;
    }

    // Clear existing applied discounts to avoid duplicates
    this.appliedDiscounts = [];
    this.promoCodeDiscounts = [];

    // Map to track unique discounts by discountId and discountTypeName
    const uniqueDiscounts = new Map<string, any>();

    // Process all order elements
    this.technicalInspection.techInspectionOrders.forEach(techInspOrder => {
      if (techInspOrder.order?.orderElements) {
        techInspOrder.order.orderElements.forEach(orderElement => {
          // Only process order elements with discount information
          if (orderElement.discountId && orderElement.discountTypeName && orderElement.discountValue) {
            const discountKey = `${orderElement.discountId}_${orderElement.discountTypeName}`;
            
            // If we haven't seen this discount yet, add it
            if (!uniqueDiscounts.has(discountKey)) {
              uniqueDiscounts.set(discountKey, {
                discountId: orderElement.discountId,
                discountTypeName: orderElement.discountTypeName,
                discountKindName: orderElement.discountKindName,
                discountValue: orderElement.discountValue,
                serviceName: orderElement.service?.name
              });
            }
          }
        });
      }
    });

    // Map to store discount names by discountId
    const discountNames = new Map<number, string>();
    // Map to store payment types by discountId (only for PROMO_CODE)
    const discountPaymentTypes = new Map<number, PaymentTypeDTO[]>();

    // Fetch discount names for VOUCHER_CODE and PROMO_CODE
    const fetchPromises: Promise<void>[] = [];

    uniqueDiscounts.forEach((discountInfo) => {
      if (discountInfo.discountTypeName === 'VOUCHER_CODE' || discountInfo.discountTypeName === 'PROMO_CODE') {
        const fetchPromise = (async () => {
          try {
            let discountName: string;
            if (discountInfo.discountTypeName === 'VOUCHER_CODE') {
              const voucherCode = await this.voucherService.getVoucherCodeById(discountInfo.discountId).toPromise();
              discountName = voucherCode?.code || 'Ваучер';
            } else {
              const promotion = await this.promotionService.findPromotionById(discountInfo.discountId).toPromise();
              discountName = promotion?.name || 'Промокод';
              const paymentTypes: PaymentTypeDTO[] = (promotion?.promotionPaymentTypes || [])
                .map(ppt => ppt.paymentType as PaymentTypeDTO)
                .filter(pt => pt != null);
              discountPaymentTypes.set(discountInfo.discountId, paymentTypes);
            }
            discountNames.set(discountInfo.discountId, discountName);
          } catch (error) {
            // If fetch fails, use default name
            const defaultName = discountInfo.discountTypeName === 'VOUCHER_CODE' ? 'Ваучер' : 'Промокод';
            discountNames.set(discountInfo.discountId, defaultName);
          }
        })();
        fetchPromises.push(fetchPromise);
      }
    });

    // Wait for all discount names to be fetched
    await Promise.all(fetchPromises);

    // Reconstruct appliedDiscounts from unique discounts
    uniqueDiscounts.forEach((discountInfo, key) => {
      if (discountInfo.discountTypeName === 'VOUCHER_CODE' || discountInfo.discountTypeName === 'PROMO_CODE') {
        // Create promo code/voucher discount
        const isVoucher = discountInfo.discountTypeName === 'VOUCHER_CODE';
        const isPercentage = discountInfo.discountKindName === 'P';
        
        // Get discount name from fetched data or use default
        const discountName = discountNames.get(discountInfo.discountId) || (isVoucher ? 'Ваучер' : 'Промокод');
        
        // Create CustomerDiscount object
        const customerDiscount: CustomerDiscount = {
          id: discountInfo.discountId,
          discountName: discountName,
          priceAttribute: {
            id: undefined,
            kind: discountInfo.discountKindName || 'P',
            amount: isPercentage ? discountInfo.discountValue : null,
            amountDds: isPercentage ? null : discountInfo.discountValue,
            ddsValue: null
          },
          isValid: true,
          priceAttributeAmountDds: discountInfo.discountValue,
          priceAttributeKind: discountInfo.discountKindName || 'P'
        };

        // Create PromotionCodeService object
        const promoCodeService: PromotionCodeService = {
          id: discountInfo.discountId,
          promotionId: discountInfo.discountId,
          promotionServiceName: discountInfo.serviceName || '',
          totalDiscountForServices: discountInfo.discountValue,
          isDiscountInPercentage: isPercentage
        };

        // Add to applied discounts
        const appliedDiscount = {
          type: 'promocode' as const,
          name: discountName,
          discount: customerDiscount,
          promoCodeService: promoCodeService,
          isVoucher: isVoucher,
          paymentTypes: discountPaymentTypes.get(discountInfo.discountId) || [],
          isMainService: this.mainServiceNamesForDiscount.includes(discountInfo.serviceName || '')
        };

        this.appliedDiscounts.push(appliedDiscount);
        this.promoCodeDiscounts.push(customerDiscount);
        this.customerDiscounts.push(customerDiscount);
        this.hasPromocode = true;
        this.promotionCodeService = promoCodeService;

        // Set discount information on the corresponding service
        if (discountInfo.serviceName) {
          // Set on services in paymentServiceTableData
          if (this.paymentServiceTableData) {
            const targetService = this.paymentServiceTableData.find(service => service.serviceName === discountInfo.serviceName);
            if (targetService) {
              targetService.discountId = discountInfo.discountId;
              targetService.discountTypeName = discountInfo.discountTypeName;
              targetService.discountKindName = discountInfo.discountKindName as 'V' | 'P';
              targetService.discountValue = discountInfo.discountValue;
            }
          }

          // Set on services in basicServices, additionalServices, and annualServices
          const allServices = [
            ...(this.basicServices || []),
            ...(this.additionalServices || []),
            ...(this.annualServices || [])
          ];
          const matchingService = allServices.find(service => service.serviceName === discountInfo.serviceName);
          if (matchingService) {
            matchingService.discountId = discountInfo.discountId;
            matchingService.discountTypeName = discountInfo.discountTypeName;
            matchingService.discountKindName = discountInfo.discountKindName as 'V' | 'P';
            matchingService.discountValue = discountInfo.discountValue;
          }
        }
      } else if (discountInfo.discountTypeName === 'LOYAL_CARD_PLASTIC') {
        const orderCustomerDiscount = this.technicalInspection?.techInspectionOrders?.[0]?.order?.customerDiscount;
        const isMainServiceDiscount = this.mainServiceNamesForDiscount.includes(discountInfo.serviceName || '');
        const isDropdownCustomerDiscount = isMainServiceDiscount && !this.technicalInspection?.customerCard && !!orderCustomerDiscount;

        if (isDropdownCustomerDiscount && orderCustomerDiscount) {
          // Dropdown customer discount (saved as LOYAL_CARD_PLASTIC on backend when no card was used)
          const appliedDiscount = {
            type: 'customer-discount' as const,
            name: orderCustomerDiscount.discountName,
            discount: orderCustomerDiscount
          };
          this.appliedDiscounts.push(appliedDiscount);
          if (!this.customerDiscounts.some(cd => cd.id === orderCustomerDiscount.id)) {
            this.customerDiscounts.push(orderCustomerDiscount);
          }
          this.additionalTechInspForm.get('customerDiscount')?.setValue(orderCustomerDiscount);
        } else {
          // Loyalty card discount
          const isPercentage = discountInfo.discountKindName === 'P';
          const customerDiscount: CustomerDiscount = {
            id: discountInfo.discountId,
            discountName: 'Карта за отстъпка',
            priceAttribute: {
              id: undefined,
              kind: discountInfo.discountKindName || 'P',
              amount: isPercentage ? discountInfo.discountValue : null,
              amountDds: isPercentage ? null : discountInfo.discountValue,
              ddsValue: null
            },
            isValid: true,
            priceAttributeAmountDds: discountInfo.discountValue,
            priceAttributeKind: discountInfo.discountKindName || 'P'
          };
          const cardNumber = this.technicalInspection?.customerCard?.cardNumber || '';
          const appliedDiscount = {
            type: 'card' as const,
            name: customerDiscount.discountName,
            discount: customerDiscount,
            cardNumber: cardNumber
          };
          this.appliedDiscounts.push(appliedDiscount);
          this.customerDiscounts.push(customerDiscount);
          this.additionalTechInspForm.get('customerDiscount')?.setValue(customerDiscount);
        }

        // Set discount information on the corresponding service (main service from order element)
        const nameForService = discountInfo.serviceName || this.BASIC_SERVICE_NAME;
        if (nameForService && this.paymentServiceTableData) {
          const targetService = this.paymentServiceTableData.find(service => service.serviceName === nameForService);
          if (targetService) {
            targetService.discountId = discountInfo.discountId;
            targetService.discountTypeName = discountInfo.discountTypeName;
            targetService.discountKindName = discountInfo.discountKindName as 'V' | 'P';
            targetService.discountValue = discountInfo.discountValue;
          }
        }
        const allServices = [
          ...(this.basicServices || []),
          ...(this.additionalServices || []),
          ...(this.annualServices || [])
        ];
        const matchingService = allServices.find(service => service.serviceName === nameForService);
        if (matchingService) {
          matchingService.discountId = discountInfo.discountId;
          matchingService.discountTypeName = discountInfo.discountTypeName;
          matchingService.discountKindName = discountInfo.discountKindName as 'V' | 'P';
          matchingService.discountValue = discountInfo.discountValue;
        }
      }
    });

    // If order has customerDiscount but it was not added from order elements (e.g. no LOYAL_CARD_PLASTIC for main service), add as customer-discount
    const orderCustomerDiscount = this.technicalInspection?.techInspectionOrders?.[0]?.order?.customerDiscount;
    if (orderCustomerDiscount && !this.technicalInspection?.customerCard) {
      const alreadyHasCustomerDiscount = this.appliedDiscounts.some(ad =>
        ad.type === 'customer-discount' || (ad.type === 'card' && ad.discount?.id === orderCustomerDiscount.id)
      );
      if (!alreadyHasCustomerDiscount) {
        this.appliedDiscounts.push({
          type: 'customer-discount' as const,
          name: orderCustomerDiscount.discountName,
          discount: orderCustomerDiscount
        });
        if (!this.customerDiscounts.some(cd => cd.id === orderCustomerDiscount.id)) {
          this.customerDiscounts.push(orderCustomerDiscount);
        }
        this.additionalTechInspForm.get('customerDiscount')?.setValue(orderCustomerDiscount);
      }
    }

    // If backend populated mobileCard in the form, add it to appliedDiscounts (mobile card is not stored in order elements)
    const mobileCardFromBe = this.additionalTechInspForm.get('mobileCard')?.value;
    if (mobileCardFromBe) {
      const mobileCardNumber = typeof mobileCardFromBe === 'string' ? mobileCardFromBe.trim() : String(mobileCardFromBe).trim();
      if (mobileCardNumber) {
        const exists = this.appliedDiscounts.some(ad => ad.type === 'mobile-card' && ad.cardNumber === mobileCardNumber);
        if (!exists) {
          this.appliedDiscounts.push({
            type: 'mobile-card' as const,
            name: `Мобилна карта - ${mobileCardNumber}`,
            discount: null,
            cardNumber: mobileCardNumber
          });
        }
      }
    }

    this.recalculateAllowedPaymentTypes();
  }

  getAppliedDiscountDisplayText(discount: any): string {
    if (discount.type === 'promocode') {
      const discountValue = discount.promoCodeService?.totalDiscountForServices || 0;
      const isPercentage = discount.promoCodeService?.isDiscountInPercentage;
      const discountText = discountValue < 0
        ? (isPercentage ? `+${Math.abs(discountValue)}%` : `+€${Math.abs(discountValue)}`)
        : (isPercentage ? `${discountValue}%` : `€${discountValue}`);
      const prefix = discount.isVoucher ? 'Приложен ваучер' : 'Приложен код';
      return `${prefix} - ${discount.name} - ${discountText} отстъпка от ${discount.promoCodeService?.promotionServiceName}`;
    } else if (discount.type === 'mobile-card') {
      return `Приложена мобилна карта - ${discount.cardNumber}`;
    } else if (discount.type === 'customer-discount') {
      const discountValue = discount.discount?.priceAttributeAmountDds ?? discount.discount?.priceAttribute?.amount ?? 0;
      const isPercentage = discount.discount?.priceAttributeKind === this.DISCOUNT_PERCENTAGE || discount.discount?.priceAttribute?.kind === this.DISCOUNT_PERCENTAGE;
      const discountText = isPercentage ? `${discountValue}%` : `€${discountValue}`;
      return `Приложена отстъпка - ${discount.name} - ${discountText} отстъпка от ${this.getSelectedBasicServiceName() ?? this.BASIC_SERVICE_NAME}`;
    } else {
      const discountValue = discount.discount?.priceAttributeAmountDds || 0;
      const isPercentage = discount.discount?.priceAttributeKind === this.DISCOUNT_PERCENTAGE;
      const discountText = isPercentage ? `${discountValue}%` : `€${discountValue}`;
      return `Приложена карта - ${discount.cardNumber} - ${discountText} отстъпка от ${this.getSelectedBasicServiceName() ?? this.BASIC_SERVICE_NAME}`;
    }
  }

  isCardOrPromoCodeReadonly(): boolean {
    return this.hasPromocode && !this.appliedDiscounts.some(ad => ad.type === 'promocode');
  }

  isApplyButtonDisabled(): boolean {
    const inputValue = this.additionalTechInspForm.get('cardOrPromoCode')?.value?.trim();
    return !inputValue || (this.hasPromocode && !this.appliedDiscounts.some(ad => ad.type === 'promocode'));
  }

  getPromoCodeName(): string {
    // First try to get from existing technical inspection
    if (this.technicalInspection?.techInspectionOrders?.[0]?.order?.promotion?.name) {
      return this.technicalInspection.techInspectionOrders[0].order.promotion.name;
    }
    // Otherwise get from applied discounts
    const promoDiscount = this.appliedDiscounts.find(ad => ad.type === 'promocode');
    return promoDiscount?.name || '';
  }

  private filterDiscountsByType(discounts: CustomerDiscount[]) {
    return discounts.filter((discount) => discount.customerDiscountType?.some((cdt) => cdt.discountType.code === this.DISCOUNT_TYPE_TECHNICAL_POINT));
  }

  private calculatePaidAmountServices() {
    this.currentAmountServices = 0;
    this.paymentServiceTableData.forEach(p => {
      if (p.amountDds !== undefined) {
        // Times the quantity: this list holds one entry per service at its unit price, so an
        // additional service chosen twice would otherwise be counted once.
        const quantity = this.additionalServiceQuantities[p.id!] ?? 1;
        this.currentAmountServices += (p.amountDds - (p.calculatedDiscount || 0)) * quantity;
      }
    });
    // Goods are part of what the customer pays, so they belong in the figure the prepaid
    // correction compares against. They are not in paymentServiceTableData - that holds
    // technical point services - so they are added from the bill directly.
    this.currentAmountServices += this.itemsTotal;
    this.currentAmountServices = parseFloat(this.currentAmountServices.toFixed(2));
    if (this.orderAmountPaid !== this.currentAmountServices) {
      this.orderPriceMismatch = true;
    } else {
      this.orderPriceMismatch = false;
    }
  }

  private calculatePaidAmountServicesFromTable() {
    if (this.orderAmountPaid !== this.currentAmountServices) {
      this.orderPriceMismatch = true;
    } else {
      this.orderPriceMismatch = false;
    }
  }

  hasTechInspectionOrder() {
    const result = 
      this.technicalInspection?.techInspectionOrders &&
      this.technicalInspection.techInspectionOrders.length > 0 &&
      this.technicalInspection.techInspectionOrders[0]?.order?.accountingDocument?.id && 
      this.technicalInspection.techInspectionOrders[0]?.order?.accountingDocument.id > 0;
      
    return result;
  }

  private onDataChangeModal() {
    const stored = localStorage.getItem('techInspModalPopUpCounter');
    this.techInspModalPopUpCounter = 0;
    if (stored) {
      this.techInspModalPopUpCounter = Number.parseInt(stored, 10);
    }
    if (this.techInspModalPopUpCounter > 0) {
      return;
    }
    this.dialogRef = this.dialog.open(this.dataChangeDialog, {
      width: '700px',
      height: '220px'
    });
    this.techInspModalPopUpCounter++;
    localStorage.setItem('techInspModalPopUpCounter', this.techInspModalPopUpCounter.toString());
  }

  onDataChangeConfirmationModal() {
    this.dialogRef.afterClosed().subscribe((result: any) => {
      if (result && this.lastTechInspId) {
        this.router.navigate(['/add-edit-technical-inspection'], { queryParams: { id: this.lastTechInspId }});
        this.lastTechInspId = 0;
      }
    });
  }

  private async findAlreadyStarted() {
    if (!this.technicalInspection?.mobileId) {
      return;
    } 

    const data = await this.techInspService.findLastIdByMobileAppId(this.technicalInspection.mobileId?.toString()).toPromise();
    if (!data) {
      this.lastTechInspId = 0;
      return;
    }

    this.lastTechInspId = data.id;
    const status = data.statusCode;
    if (this.technicalInspection.status) {
      const code = this.technicalInspection?.status.code;
      if ((code === 'STARTED' || code === 'TERMINATED' || code === 'RESERVED' || code === 'AWAITING_INSPECTION' || code === 'FINISHED') && code !== status) {
        this.onDataChangeModal();
        return;
      }
    }
  }

  isNegativeDiscount(value: number | null | undefined): boolean {
    return (value ?? 0) < 0;
  }

  // Get form
  get mainForm()       { return this.mainTechInspForm.controls; }

  get additionalForm() { return this.additionalTechInspForm.controls; }

  get availableForm()  { return this.availableSlotForm.controls; }

  /**
   * Whether the fiscal number reads as a complete receipt number, for the green confirmation on
   * the field.
   *
   * The control is optional, so "no errors" is not the same as "correct": an empty box passes the
   * pattern and must stay neutral rather than claim to be filled in correctly. Only the seven
   * digits actually printed on the receipt earn the tick.
   */
  get isFiscalNumberComplete(): boolean {
    const value = this.additionalTechInspForm.get('fiscalNumber')?.value;
    return typeof value === 'string' && /^[0-9]{7}$/.test(value.trim());
  }

}